import { portalEN } from '../portal/en';
import { privacyPolicyBodyEn } from '@/shared/i18n/locales/privacy-policy-bodies/en';
import { restaurantOrdersEn } from '@/shared/i18n/locales/restaurant-orders/en';

/** Starting point offered by the "suggest a structure" button on the policy editor. */
const paymentPolicyTemplate = `PAYMENT, CARD AND CANCELLATION POLICY

1. Venue identity
The booking and its payment are handled by [LEGAL NAME], based at [ADDRESS], reachable at [EMAIL/PHONE]. Stripe acts solely as the payment service provider.

2. Card verification
When only verification is required, no amount is charged or held. The card is verified through Stripe to confirm that it is valid.

3. Advance payment
When immediate payment is required, the total shown before confirmation is charged at booking time and recorded as [DEPOSIT/FULL PAYMENT].

4. Pre-authorisation
When a pre-authorisation is required, the amount shown is temporarily held. The venue may capture it in the cases described in this policy. If it is not captured, release times depend on the customer's bank.

5. Cancellation and changes
Free cancellation until [DEADLINE]. After that, the venue may retain or charge [AMOUNT/PERCENTAGE]. Changes are subject to availability.

6. No-show and late arrival
In case of a no-show, or a delay longer than [MINUTES], the venue may retain or charge [AMOUNT/PERCENTAGE].

7. Refunds
Refunds are issued to the same payment method within [DAYS] days; final crediting times depend on the card network and the bank.

8. Card storage and future use
The card may be stored in tokenised form by Stripe and reused only if the customer gives specific consent for the charges described. Neither PRO20 nor the venue stores the full card number.

9. Disputes and support
For requests, cancellations or disputes, contact [EMAIL/PHONE] quoting the booking code.

Version and validity
The accepted version, the amount, the payment mode and the acceptance date are recorded together with the booking.`;

export const en = {
  collaboratorRoles: {
    inherited:
      'This role is not configured yet and retains its current access. Save to define explicit permissions.',
    title: 'App permissions',
    description:
      'Choose which app operations each collaborator role can perform. Portal access is unchanged.',
    role: 'Collaborator role',
    enabled: 'enabled',
    wholeCategory: 'Entire category',
    search: 'Search operations',
    saved: 'Permissions saved',
    saveError:
      'Could not save. Reload the role to check for changes by another manager.',
    versionError: 'The permission catalog has changed. Reload before saving.',
    unsaved: 'Unsaved changes',
    appOnly: 'These permissions apply to the app only.',
    reset: 'Discard changes',
    save: 'Save permissions',
    discard: 'Discard unsaved changes and load the selected role?',
    selectBranch: 'Select a branch to configure permissions.',
    loading: 'Loading permissions…',
    unavailable:
      'Role permissions could not be loaded. Check your access and that the service supports this configuration. No permission changes have been applied.',
    retry: 'Retry',
  },

  portal: portalEN,
  restaurantOrders: restaurantOrdersEn,
  giftCards: {
    deselect: 'Deselect customer',
    cancelCard: 'Cancel gift card',
    cancelTitle: 'Cancel this gift card?',
    cancelDescription:
      'The gift card for {{recipient}} will no longer be usable. Its balance and operations will remain in the history.',
    cancelSuccess: 'Gift card cancelled.',
    cancelError: 'Unable to cancel the gift card.',
    openRecap: 'Open recap',
    recapError:
      'Unable to retrieve the personal Gift card link. The Wallet service may not be available yet.',

    select: 'Choose',
    card: 'Card',
    invalidDates: 'Select a valid date range.',

    title: 'Gift cards',
    description: 'Issued cards, QR codes and balances',
    create: 'Create gift card',
    search: 'Search customer or card code…',
    empty: 'No gift cards found',
    loadError: 'Unable to load gift cards.',
    noBranch: 'Select a branch to manage gift cards.',
    customer: 'Customer',
    amount: 'Amount (€)',
    chooseCustomer: 'Choose a customer from the list',
    selected: 'Selected customer',
    createSuccess: 'Gift card created.',
    createError: 'Unable to create the gift card.',
    amountError:
      'Enter an amount greater than zero, with up to two decimal places.',
    details: 'Gift card details',
    initial: 'Initial amount',
    balance: 'Balance',
    status: 'Status',
    created: 'Created',
    expires: 'Expires',
    operations: 'Transactions',
    operation: 'Transaction',
    note: 'Note',
    noOperations: 'No transactions available.',
    email: 'Send email',
    resend: 'Resend',
    resendChannel: 'How would you like to resend the gift card?',
    resendWhatsapp: 'Send via WhatsApp',
    resendWhatsappHint:
      'Send from the branch WhatsApp number using the configured gift card template.',
    resendNoEmail: 'Email unavailable.',
    resendNoPhone: 'International phone number unavailable or invalid.',
    resendPopupBlocked: 'Allow popups to open WhatsApp.',
    resendWhatsappMessage: 'Here is the link to your gift card:',
    resendWhatsappSuccess: 'WhatsApp delivery requested.',
    resendError: 'Resend not confirmed. Check delivery status before retrying.',
    resendEmail: 'Resend email',
    emailSuccess: 'Email delivery requested.',
    emailError: 'Unable to request email delivery.',
    active: 'Active',
    used: 'Used',
    expired: 'Expired',
    cancelled: 'Cancelled',
    allStates: 'All statuses',
    from: 'From',
    to: 'To',
    dateRange: 'Date range',
    clearDateRange: 'Clear date range',
    requestPending:
      'Keep the same customer and amount to retry the same request without issuing a second card.',
  },
  common: {
    retry: 'Try again',
    loading: 'Loading',
    save: 'Save',
    delete: 'Yes, Delete',
    deleting: 'Deleting...',
    createLoading: 'Creating...',
    cancel: 'Cancel',
    yes: 'Yes',
    no: 'No',
    back: 'Back',
    search: 'Search',
    noData: 'No data available',
    noDataDesc: 'There are no data matching the selected filters.',
    previous: 'Previous page',
    next: 'Next page',
    actions: 'Actions',
    serialNumber: 'Sr No.',
    itemsPerPage: 'Items per page',
    range: '{{start}} - {{end}} of {{total}}',
    footer: 'Pro20 © {{year}}',
    toast: {
      copyUrlSuccess: 'URL copied to clipboard.',
    },
  },
  nav: {
    collaboratorRoles: 'Collaborator roles',
    deliverySection: 'Delivery',
    drivers: 'Drivers',
    orders: 'Orders',
    delivery: 'Delivery',
    deliveryTakeaway: 'Delivery / Takeaway',
    orderingConfiguration: 'Configuration',
    takeaway: 'Takeaway',
    operations: 'Orders',
    suppliers: 'Suppliers and catalogs',
    supplierOrderHistory: 'Order history',
    supplierOrders: 'Supplier orders',
    ingredients: 'Ingredients',
    menuList: 'Your menus',
    home: 'HOME',
    menu: 'Menu',
    settingsGroup: 'SETTINGS',
    dashboard: 'Dashboard',
    calendar: 'Calendar',
    calendarDesc: 'Bookings and forms',
    marketing: 'Marketing',
    marketingDesc: 'Messaging and promotions',
    menuItem: 'Menu',
    staff: 'Staff',
    settings: 'Configurations',
    payments: 'Payments',
    settingsDesc: 'General settings',
    users: 'Users',
    reports: 'Reports',
    notifications: 'Notifications',
    bookingHistory: 'Reservations',
    forms: 'Booking Forms',
    formHistory: 'Booking',
    formMenu: 'Menu',
    others: 'Others',
    extraServiceAndProduct: 'Extra Services',
    events: 'Events',
    bulkMessage: 'Bulk Message',
    surveys: 'Questionnaires',
    automationConfiguration: 'Configuration',
    automationDashboard: 'Performance dashboard',
    automation: 'Automation',
    dataCollectionForm: 'Form data collection',
    promotion: 'Promotions',
    fidelity: 'Fidelity',
    discountCards: 'Discount cards',
    cards: 'Cards',
    experienceCards: 'Experience',
    prizes: 'Prize',
    loyalty: 'Loyalty',
    landingPage: 'Landing Pages',
    tag: 'Tag',
    publicRelation: 'Public relation',
    collaborator: 'Collaborators',
    collaboratorReport: 'Report collaborators',
    myCustomers: 'My clients',
    localConfiguration: 'Local',
    stripeConfiguration: 'Stripe configuration',
    stripeTransactions: 'Stripe transactions',
    configureWhatsApp: 'WhatsApp Unofficial',
    messages: 'Messages',
    officialWhatsAppChannel: 'Official WhatsApp Channel',
    templates: 'Templates',
    whatsAppOfficial: 'WhatsApp Official',
    whatsAppOfficialConfigure: 'Configure',
    whatsAppOfficialTemplates: 'Templates',
    whatsAppOfficialInbox: 'Inbox',
  },
  header: {
    fullscreen: 'Toggle fullscreen',
    language: 'Language',
    theme: 'Theme',
    lightMode: 'Light',
    darkMode: 'Dark',
    profile: 'Profile',
    signOut: 'Sign out',
    userCode: 'User Code',
    branchList: 'Branch List',
    searchBranches: 'Search branches...',
    noBranchesFound: 'No branches found',
    switchingBranch: 'Switching branch…',
  },
  dashboard: {
    aiCredits: 'AI credits',
    smsAvailable: 'SMS remaining',
    smsLoading: 'Loading SMS balance…',
    smsError: 'SMS balance error',
    smsErrorHint: 'SMS balance unavailable. Please retry.',
    smsRefresh: 'Refresh SMS balance',
    smsEmpty: 'No SMS remaining',
    title: 'Dashboard',
    subtitle: "Here's what's happening with your business.",
    headerMeta: '{{dateRange}} · {{days}} days performance window',
    customRange: 'Date Range',
    customRangePlaceholder: 'Select date range',
    refresh: 'Refresh',
    bookingStatistics: 'Booking statistics',
    lastWeek: 'Last week',
    thisWeek: 'This week',
    processedBookings: 'Processed bookings vs. Arrived customers',
    overview: 'Overview',
    bookings: 'Bookings',
    customersManaged: 'Customers managed',
    arrived: 'Arrived',
    refused: 'REFUSED',
    noShow: 'NO SHOW',
    cancelled: 'CANCELLED',
    summary: 'Summary of workspace',
    customerContacts: 'Customer Contacts',
    acceptedBookings: 'Accepted Bookings',
    managedGuests: 'Managed Guests',
    callCenterInteractions: 'Call Center Interactions',
    remainingSms: 'Remaining SMS',
    remainingEmails: 'Remaining Emails',
    totalUsers: 'Total Users',
    activeUsers: 'Active Users',
    revenue: 'Revenue',
    pendingTasks: 'Pending Tasks',
    recentActivity: 'Recent Activity',
    quickActions: 'Quick Actions',
    fromLastMonth: 'from last month',
    noData: '—',
    noBranchTitle: 'No branch selected',
    noBranchMessage: 'Select a branch to view dashboard analytics.',
    loadError: 'Could not load dashboard data. Please try again.',
    sectionLoadError: 'Could not load this section.',
    bookingsFlow: 'Bookings flow',
    forms: 'Forms',
    statusDistribution: 'Status distribution',
    totalBookingsShort: 'Bookings',
    waitingAction: 'Waiting for action',
    waitingActionSubtitle: 'Reservations in IN_ATTESA ready to process.',
    waitingBadge: 'Waiting',
    people: 'people',
    openBooking: 'Open booking',
    copyContact: 'Copy contact',
    copySuccess: 'Copied to clipboard.',
    emptyWaiting: 'No bookings waiting for action.',
    emptyChart: 'No data for this period.',
    emptyCustomers: 'No customers to show.',
    emptyForms: 'No forms to show.',
    guests: 'guests',
    children: 'children',
    accept: 'Accept',
    reject: 'Reject',
    acceptSuccess: 'Booking accepted.',
    rejectSuccess: 'Booking refused.',
    actionError: 'Could not update booking status.',
    busiestSlots: 'Busiest slots',
    busiestSlotsSubtitle: 'Where the service gets dense.',
    customerMix: 'Customer mix',
    customerRisk: 'Customer risk',
    customerRiskSubtitle: 'No-show and refusal concentration.',
    bestCustomers: 'Best customers',
    bestCustomersSubtitle: 'High-value recurring presence.',
    lastBooking: 'Last booking',
    messageDelivery: 'Message delivery',
    messageDeliverySubtitle: 'Health of automated communication.',
    dailyOperations: 'Daily operations',
    dailyOperationsSubtitle: 'Bookings and guest load by day.',
    bookingsFlowSubtitle: 'Daily bookings across the selected period.',
    formsSubtitle: 'Where reservations come from.',
    statusDistributionSubtitle: 'How bookings ended up.',
    customerMixSubtitle: 'Your customer base in this period.',
    pulse: {
      today: 'Today',
      upcoming: 'Upcoming',
      pending: 'Pending',
      waitList: 'Wait list',
      refusalRate: 'Refusal rate',
      google: 'Google Reservations',
      googleOn: 'Active',
      googleOff: 'Not active',
      googleMissing: '{{count}} missing requirements',
    },
    waitingTabs: {
      all: 'All',
      today: 'Today',
      future: 'Future',
      large: 'Large',
    },
    flow: {
      volume: 'Volume',
      people: 'People',
      outcome: 'Outcome',
    },
    kpis: {
      reservations: 'Reservations',
      reservationsHint: '{{count}} people',
      arrivalRate: 'Arrival rate',
      customers: 'Customers',
      arrivedOfTotal: '{{arrived}} of {{total}} arrived',
      customersHint: '{{active}} active · {{new}} new',
      messageErrors: '{{count}} delivery errors',
      noShowRate: 'No-show rate',
      arrivedVsNoShow: '{{arrived}} arrived · {{noShow}} no-show',
      peakDay: 'Peak day',
      peakSlot: 'Peak slot',
      dailyAverage: 'Daily average',
      dailyAverageHint: 'Reservations per day',
      messagesWaiting: 'Messages waiting',
      scheduledTotal: '{{count}} scheduled total',
      bookingsGuests: '{{bookings}} bookings, {{guests}} guests',
      slotStats: '{{bookings}} bookings · {{arrived}} arrived ({{rate}}%)',
      totalBookings: 'Total bookings',
      activeUsers: 'Active users',
      newUsers: 'New users',
      peakDate: 'Peak date',
      managedBookings: 'Managed bookings',
    },
    series: {
      bookings: 'Bookings',
      confirmed: 'Confirmed',
      pending: 'Pending',
      refused: 'Refused',
      noShow: 'No show',
      risk: 'Risk',
    },
    mix: {
      active: 'Active',
      new: 'New',
      marketingOptIn: 'Marketing opt-in',
      blacklisted: 'Blacklisted',
      ofBase: '{{percent}}% of base',
      total: 'total',
    },
    comm: {
      bookingNotifications: 'Booking notifications',
      scheduledMessages: 'Scheduled messages',
      successRate: 'Success rate',
      total: 'Total',
      processed: 'processed',
      waiting: 'waiting',
      errors: 'errors',
      centralino: 'Centralino status',
      centralinoActive: 'Centralino active',
      wao: 'WAO',
      whatsapp: 'WhatsApp',
      sms: 'SMS',
      email: 'Email',
      enabled: 'On',
      disabled: 'Off',
    },
    ops: {
      byDay: 'By day',
      byWeekday: 'By weekday',
      avgPeople: 'Avg people',
      avgBookings: 'Avg bookings',
      avgPartySize: 'Avg party size',
      busiestWeekday: 'Busiest weekday',
      totalPeople: 'Total people',
      daysCount: '{{count}} days',
    },
    days: {
      mon: 'Mon',
      tue: 'Tue',
      wed: 'Wed',
      thu: 'Thu',
      fri: 'Fri',
      sat: 'Sat',
      sun: 'Sun',
    },
  },
  auth: {
    welcomeBack: 'Welcome back!',
    signIn: 'Sign In',
    email: 'Email',
    password: 'Password',
    enterPassword: 'Enter your password',
    userCode: 'User code',
    enterUserCode: 'Enter your user code',
    branchCode: 'Branch code',
    enterBranchCode: 'Enter branch code',
    employeeId: 'Employee ID',
    enterEmployeeId: 'Enter employee ID',
    rememberMe: 'Remember me',
    rememberCredentials: 'Remember credentials',
    forgotPassword: 'Forgot password?',
    signInToContinue:
      'Sign in to continue managing your restaurant business with PRO20.',
    signInToAccount: 'Sign in to your account',
    chooseSignInMethod: 'Choose your preferred sign in method',
    or: 'or',
    loginWithEmployeeId: 'Sign in with Employee ID',
    loginWithUserCode: 'Sign in with User code',
    loginWithAdminCode: 'Log in with administrator code',
    invalidCredentials: 'Invalid credentials. Please try again.',
    enterpriseTagline: 'Enterprise admin, simplified.',
    enterpriseDescription:
      'Manage users, monitor analytics, and configure your workspace from a single powerful dashboard.',
    trustedBy: 'Trusted by restaurants worldwide',
    trustSecurityTitle: 'Enterprise Security',
    trustSecurityDesc: 'Bank-level data protection and compliance.',
    trustUptimeTitle: '99.9% Uptime',
    trustUptimeDesc: 'Reliable performance when you need it.',
    trustSupportTitle: '24/7 Support',
    trustSupportDesc: "We're here to help anytime.",
    secureSignIn: 'Secure sign in | Your connection is encrypted.',
    copyright: '© {{year}} PRO20. All rights reserved.',
    dashboardPreviewAlt: 'PRO20 dashboard preview',
    demoCredentials: 'Demo credentials: admin@proventi.com / password123',
    version: 'v. {{version}}(#1)',
  },
  subscription: {
    expired: {
      badge: 'Subscription expired',
      title: 'Your subscription has expired',
      description: 'If you have already renewed, please contact support.',
      infoTitle: 'Need to renew or restore access?',
      infoDescription:
        'Reach out to our support team and we will help you get back online.',
      footerHelp: 'Still need help?',
      footerHelpDesc: 'Our support team is available 24/7 to assist you.',
    },
  },
  automation: {
    disabled: {
      badge: 'Section not enabled',
      title: 'This section is not enabled',
      description:
        'This feature is currently disabled for your account. Please contact support to know more.',
      infoTitle: 'Need access to this section?',
      infoDescription:
        "Reach out to our support team and we'll help you get started.",
      footerHelp: 'Still need help?',
      footerHelpDesc: 'Our support team is available 24/7 to assist you.',
    },
  },
  orders: {
    disabled: {
      badge: 'Section not enabled',
      title: 'This section is not enabled',
      description:
        'This feature is currently disabled for your account. Please contact support to know more.',
      infoTitle: 'Need access to this section?',
      infoDescription:
        "Reach out to our support team and we'll help you get started.",
      footerHelp: 'Still need help?',
      footerHelpDesc: 'Our support team is available 24/7 to assist you.',
    },
  },
  delivery: {
    disabled: {
      badge: 'Section not enabled',
      title: 'This section is not enabled',
      description:
        'This feature is currently disabled for your account. Please contact support to know more.',
      infoTitle: 'Need access to this section?',
      infoDescription:
        "Reach out to our support team and we'll help you get started.",
      footerHelp: 'Still need help?',
      footerHelpDesc: 'Our support team is available 24/7 to assist you.',
    },
  },
  users: {
    title: 'Users',
    subtitle: 'Manage team members and permissions',
    searchPlaceholder: 'Search users...',
    role: 'Role',
    status: 'Status',
    joined: 'Joined',
    accountDetails: 'Account Details',
    userId: 'User ID',
    created: 'Created',
    lastUpdated: 'Last Updated',
    notFound: 'User not found',
  },
  settings: {
    title: 'Settings',
    subtitle: 'Manage your workspace preferences',
    comingSoon: 'This section is coming soon.',
    general: 'General',
    theme: 'Theme',
    language: 'Language',
    timezone: 'Timezone',
    notifications: 'Notifications',
    emailNotifications: 'Email Notifications',
    pushNotifications: 'Push Notifications',
    enabled: 'Enabled',
    disabled: 'Disabled',
  },
  userSettings: {
    title: 'User settings',
    subtitle: 'Manage your account password and preferences',
    password: {
      sectionTitle: 'Password management',
      sectionSubtitle: 'Change password',
      passwordLabel: 'Password',
      confirmLabel: 'Confirm password',
      submit: 'Update password',
      errors: {
        required: 'Password is required',
        minLength: 'Password must be at least 4 characters',
        confirmRequired: 'Please confirm your password',
        mismatch: 'Passwords do not match',
      },
      toast: {
        success: 'Password updated successfully',
        failed: 'Could not update password. Please try again.',
        missingUser: 'Could not find your user account. Please sign in again.',
      },
    },
  },
  localConfiguration: {
    subtitle: 'Configure restaurant logo, schedule, location and tracking.',
    saveConfiguration: 'Save Configuration',
    saveTooltip: 'Save restaurant configuration',
    noBranch: 'Select a branch to configure local settings.',
    loadError: 'Could not load local configuration. Please try again.',
    logoTitle: 'Restaurant logo',
    logoHint: 'Upload an image',
    logoDescription:
      'Upload a PNG logo with a transparent background. Double-click the image to open a preview.',
    uploadImage: 'Upload an image',
    removeLogo: 'Remove logo',
    logoUploading: 'Uploading…',
    logoRemoving: 'Removing…',
    timezoneTitle: 'Time zone of the room',
    timezoneHint:
      'Determine the current day shown in the form and prevent date slippage for customers abroad.',
    timezoneLabel: 'Geographical area',
    timezoneHelper:
      'The change between solar time and daylight saving time is automatically managed.',
    generalTitle: 'General details',
    generalHint: 'Configure venue capacity, time slots, and closing days.',
    scheduleTitle: 'Schedule',
    scheduleHint: 'Choose when guests can book.',
    dayOverviewTitle: 'When guests can book',
    dayOverviewHint: 'Open this to see the whole day.',
    dayOverviewShow: 'Show day preview',
    dayOverviewHide: 'Hide day preview',
    guestsCanBook: 'Guests can book during this time.',
    guestsCannotBook: 'Guests cannot book during this time.',
    emptyServiceTimes:
      'No service times yet. Add breakfast, lunch, or dinner so guests know when they can book.',
    timeStepHint:
      'Pick times in 15-minute steps. Bookings stop at the end time.',
    switchOn: 'On',
    switchOff: 'Off',
    editAction: 'Edit',
    deleteAction: 'Delete',
    howItWorks: 'How it works',
    previewOnly: 'Preview only — edit bands below',
    stepBadge: 'Step: 15 min — 04:00 / 03:59',
    localDay: 'Local day: {{time}}',
    tips: {
      turnOnTitle: 'Turn on',
      turnOn: 'Leave only the bands you use active.',
      moveTitle: 'Move',
      move: 'Move start and end with the sliders.',
      saveTitle: 'Save',
      save: 'The system avoids overlaps.',
    },
    shiftsTitle: 'Service times',
    shiftsHint:
      'Add the times you take bookings, such as lunch or dinner. You can turn one off without deleting it.',
    createMealBand: 'Add a service time',
    createMealBandAction: 'Add',
    createMealBandHint: 'Give it a name, then choose when it starts and ends.',
    editMealBand: 'Edit service time',
    editMealBandNamed: 'Edit {{name}}',
    updateMealBandAction: 'Save',
    mealBandName: 'Name',
    mealBandNamePlaceholder: 'Lunch',
    selectStartTime: 'Select start time',
    selectEndTime: 'Select end time',
    mealBandLimitReached: 'You can add up to 20 service times.',
    customMealBand: 'Service time',
    deleteMealBand: 'Delete {{name}}',
    deleteMealBandTitle: 'Delete this service time?',
    deleteMealBandDescription:
      'Are you sure you want to delete {{name}}? This cannot be undone.',
    active: 'Active',
    inactive: 'Inactive',
    shiftLegendOff: '{{name}}: not accepting bookings',
    shiftLegendRange: '{{name}}: {{from}} - {{to}}',
    booking: 'Booking',
    activeBand: 'Accept bookings',
    activeBandHint: 'Turn this off if guests cannot book during this time.',
    deactivate: 'Deactivate',
    deactivated: 'Deactivated',
    activate: 'Activate',
    valueSaved: 'Value saved',
    valueSavedHint: 'Customers will be able to book within this time frame.',
    selectBandTitle: 'Select the band with a single bar',
    selectBandHint: 'The selected part respects the other active bands.',
    fine: 'End',
    spaceAvailable: 'Space available for {{name}}: {{from}} - {{to}}',
    minStartValue: 'Min start',
    maxEndValue: 'Max end',
    bandTurnedOff:
      'This band is turned off. Turn it on with the switch above to edit times.',
    activityHours: 'Activity hours',
    start: 'Start',
    end: 'End',
    closuresTitle: 'Days the restaurant is closed',
    closuresHint: 'Guests cannot book on these dates.',
    addClosure: 'Add closure',
    closuresModalHint: 'Select a start and end date for the closing period.',
    closingPeriod: 'Closing period',
    chooseDateRange: 'Choose a date range',
    fromDate: 'From date',
    toDate: 'To date',
    actions: 'Actions',
    noClosures: 'No closed days yet.',
    deleteClosure: 'Delete closure',
    locationTitle: 'Location',
    trackingTitle: 'Tracking & Analytics',
    trackingHint:
      'Set up Google Tag Manager, Facebook Pixel, and tracking tools',
    googleTagManager: 'Google Tag Manager',
    facebookPixel: 'Facebook Pixel',
    tabs: {
      tabListLabel: 'Local configuration sections',
      general: 'General',
      bookingTimeSlots: 'Schedule',
      locationTracking: 'Location & Tracking',
    },
    shifts: {
      breakfast: 'Breakfast',
      breakfastSubtitle: 'Breakfast time slot',
      lunch: 'Lunch',
      lunchSubtitle: 'Lunch time slot',
      aperitivo: 'Happy hour',
      aperitivoSubtitle: 'Aperitivo time slot',
      dinner: 'Dinner',
      dinnerSubtitle: 'Dinner time slot',
    },
    fields: {
      localName: 'Local name',
      localCapacity: 'Local capacity',
      address: 'Address',
      city: 'City',
      zip: 'ZIP',
      province: 'Province',
      nation: 'Nation',
      state: 'State',
      latitude: 'Latitude',
      longitude: 'Longitude',
      gtmId: 'Container ID',
      pixelId: 'Pixel ID',
    },
    validation: {
      nameRequired: 'Local name is required',
      mealBandNameRequired: 'Enter a name',
      mealBandNameDuplicate: 'A service time with this name already exists',
      openingTimeRequired: 'Start time is required',
      closingTimeRequired: 'End time is required',
    },
    toast: {
      saved: 'Configuration saved',
      saveError: 'Could not save configuration',
      logoUploadSuccess: 'Logo uploaded successfully',
      logoUploadError: 'Could not upload logo',
      logoRemoveSuccess: 'Logo removed successfully',
      logoRemoveError: 'Could not remove logo',
      selectDateRange: 'Select a start date and an end date',
      closureAdded: 'Closing period added',
      closureAddError: 'Could not add closing period',
      closureOverlap: 'This period overlaps an existing closing period',
      closureRemoved: 'Closing period removed',
      closureDeleteError: 'Could not delete closing period',
      mealBandCreated: 'Service time added',
      mealBandCreateError: 'Could not add this service time',
      mealBandUpdated: 'Service time updated',
      mealBandUpdateError: 'Could not update this service time',
      mealBandStatusUpdated: 'Booking status updated',
      mealBandStatusError: 'Could not update booking status',
      mealBandDeleted: 'Service time deleted',
      mealBandDeleteError: 'Could not delete this service time',
      mealBandInvalidCode: 'This service time could not be saved. Try again.',
    },
  },
  stripe: {
    common: {
      refresh: 'Refresh from Stripe',
      noBranch: 'Select a venue to continue.',
      processing: 'Processing…',
    },
    configuration: {
      title: 'Stripe configuration',
      subtitle: 'Live data for the connected Stripe account.',
      subtitleWithBranch: 'Live data for the account connected to {{branch}}.',
      loadError: 'Could not read the account state from Stripe.',
      notConnectedTitle: 'Stripe account not configured',
      notConnectedDescription:
        'This venue has no connected Stripe account yet. Complete the onboarding from the backoffice.',
      accountSection: 'Account information',
      accountHeading: 'Stripe account',
      email: 'Email',
      country: 'Country',
      currency: 'Currency',
      accountType: 'Account type',
      disabledReason: 'Block reason',
      disabledReasonNone: 'None',
      detailsSubmitted: 'Details submitted',
      chargesEnabled: 'Payments',
      payoutsEnabled: 'Payouts',
      requirements: {
        title: 'Stripe requirements',
        currentlyDue: 'To be completed',
        pastDue: 'Past due',
        pendingVerification: 'Being verified',
        none: 'None',
      },
      policy: {
        title: 'Payment policy',
        description:
          'Text shown to the customer when a booking requires card verification, a pre-authorisation, or a payment.',
        versionMeta: 'Version {{version}} · updated {{date}}',
        useTemplate: 'Suggest a policy structure',
        templateWarning:
          'The template is a starting point: replace every field in square brackets and have the text reviewed by a legal advisor.',
        placeholder: 'Enter the payment policy for this venue…',
        charCount: '{{count}}/{{max}} characters',
        save: 'Save and create new version',
        template: paymentPolicyTemplate,
      },
      toast: {
        policySaved: 'Payment policy saved.',
        policyError: 'Could not save the payment policy.',
      },
      validation: {
        policyRequired: 'Enter the payment policy text.',
        policyMax: 'The policy cannot exceed 20000 characters.',
      },
    },
    transactions: {
      title: 'Stripe transactions',
      empty: 'No transactions',
      emptyDescription:
        'No payments or deposits have been recorded for this venue yet.',
      notConnectedTitle: 'Stripe account not connected.',
      notConnectedDescription:
        'All historical transactions recorded for this venue remain available here.',
      filters: {
        all: 'All',
        succeeded: 'Succeeded',
        uncaptured: 'Not captured',
        failed: 'Failed',
      },
      columns: {
        date: 'Date',
        booking: 'Booking',
        operation: 'Operation',
        requested: 'Requested',
        captured: 'Captured',
        status: 'Status',
        details: 'Details',
      },
      mode: {
        payNow: 'Payment',
        authorizeOnly: 'Deposit / pre-authorisation',
      },
      status: {
        linkCreated: 'Waiting for payment',
        processing: 'Recorded, awaiting Stripe confirmation',
        pending: 'Pending',
        authorized: 'Not captured',
        partiallyPaid: 'Partially captured',
        paid: 'Succeeded',
        failed: 'Failed',
        canceled: 'Cancelled',
        refundPending: 'Refund in progress',
        partiallyRefunded: 'Partially refunded',
        refunded: 'Refunded',
        refundFailed: 'Refund failed',
      },
      actions: {
        capture: 'Capture deposit',
        release: 'Decline deposit',
      },
      confirm: {
        captureTitle: 'Capture the deposit?',
        captureDescription:
          '{{amount}} will be captured for booking {{booking}}.',
        releaseTitle: 'Decline the deposit?',
        releaseDescription:
          'The {{amount}} authorisation will be cancelled and the amount released.',
      },
      toast: {
        captureSuccess: 'Stripe confirmed the capture.',
        captureError: 'Capture failed. Check the state on Stripe and retry.',
        releaseSuccess: 'Stripe cancelled the authorisation.',
        releaseError: 'Release failed. Check the state on Stripe and retry.',
      },
      technical: {
        title: 'Technical details',
        description: 'Identifiers used by PRO20 and Stripe for this operation.',
        open: 'Show technical details',
        close: 'Close',
        bookingCode: 'Booking code',
        depositCode: 'PRO20 transaction code',
        paymentIntentId: 'Stripe PaymentIntent',
        paymentLinkId: 'Stripe Payment Link',
        connectedAccountId: 'Stripe account',
      },
    },
  },
  messages: {
    orderingEvents: {
      CREATED: 'Order received',
      ACCEPTED: 'Order accepted',
      PREPARING: 'Order in preparation',
      READY: 'Order ready',
      DELIVERED: 'Order delivered',
      COLLECTED: 'Order collected',
      CANCELLED: 'Order cancelled',
      FAILED: 'Delivery unsuccessful',
      PAYMENT_REQUIRED: 'Payment link',
      PAYMENT_CONFIRMED: 'Payment received',
      PAYMENT_FAILED: 'Payment unsuccessful',
      REFUNDED: 'Refund confirmed',
      SCHEDULE_REMINDER: 'Order reminder',
    },
    orderingFlows: {
      CREATED:
        'After order creation. The customer is told that restaurant acceptance is still pending.',
      ACCEPTED: 'When you accept the order and choose to notify the customer.',
      PREPARING:
        'When preparation starts and you choose to notify the customer.',
      READY:
        'When the order is ready and you choose to notify the customer. For delivery, this does not mean the driver has departed.',
      DELIVERED:
        'When you individually process the order as delivered and choose to notify the customer. Bulk and courier completion remain silent.',
      COLLECTED:
        'When you individually process the order as collected and choose to notify the customer. Bulk processing remains silent.',
      CANCELLED:
        'When you cancel the order and choose to notify the customer. This does not confirm a refund.',
      FAILED:
        'When you mark delivery as unsuccessful and choose to notify the customer.',
      PAYMENT_REQUIRED:
        'Only after a valid payment link has actually been created.',
      PAYMENT_CONFIRMED:
        'After actual payment confirmation, automatic or manual. Bulk operations remain silent.',
      PAYMENT_FAILED:
        'If a payment attempt fails and the order remains unpaid.',
      REFUNDED: 'Only after the refund has successfully completed.',
      SCHEDULE_REMINDER:
        'Before the scheduled time, only for accepted or preparing orders. Unpaid card orders are excluded.',
    },
    ordering: {
      configurationHint:
        'Enable this message for this branch’s orders. Saving does not send a message.',
      initialize: 'Create Delivery and Asporto messages',
      initialized: 'Messages created. Nothing sent.',
      loadError: 'Unable to load Delivery and Asporto messages. Please retry.',
      missing:
        'Create missing messages. Existing custom content is preserved. New messages start disabled.',
      minutes: 'Minutes before the scheduled order',
      scheduleError: 'Enter a whole number from 1 to 10080 minutes.',
      exclusive:
        'Choose unofficial WhatsApp or an official template, not both.',
      emailPreset: 'Default email content',
      paymentHint:
        'Keep disabled if you already send the payment link manually on WhatsApp.',
    },

    searchPlaceholder: 'Search by name, code, category or channel…',
    allCategories: 'All categories',
    browseCategories: 'Message categories',
    searchEmpty: 'No matches. Try another term or select all categories.',

    giftCards: {
      scheduleTitle: 'Reminder before expiry',
      daysBeforeExpiry: 'Days before expiry',
      scheduleHint:
        'Send the reminder this many days before expiry, at the selected hour in the business timezone. Exclude used, cancelled and zero-balance cards.',
      scheduleError:
        'Enter a positive whole number of days and an hour between 00:00 and 23:00.',

      creation: 'Gift card creation',
      expired: 'Gift card expired',
      walletHint:
        'The backend supplies the personal wallet_link when sending. A Wallet URL button must use the card-specific suffix, never a fixed customer link.',

      initialize: 'Create missing messages',
      initializeSuccess: 'Gift card configurations created.',
      initializeError: 'The backend could not create gift card configurations.',
      missing:
        'These configurations are missing. Create them to associate official WhatsApp and email templates.',
      notCreated: 'Not created on the backend',
      loadError:
        'Unable to load Marketing configurations. Retry before editing.',
    },
    title: 'Message Center',
    subtitle:
      'Manage all automatic WhatsApp, SMS, and Email messages of the venue.',
    configuredLabel: 'Configured messages',
    refresh: 'Refresh',
    emptyCategory: 'No messages in this category yet.',
    emptyTitle: 'No messages found',
    emptyDescription:
      'There are no automatic messages configured for this branch yet.',
    noBranchTitle: 'No branch selected',
    noBranchDescription:
      'Select a branch to view and manage automatic messages.',
    loadErrorTitle: 'Could not load messages',
    loadErrorDescription: 'Failed to load the message list. Please try again.',
    categories: {
      promo: 'Promo',
      fidelity: 'Fidelity',
      restaurantOrdering: 'Delivery & Asporto',
      supplierOrders: 'Supplier orders',
      giftCards: 'Gift card messages',
      standard: 'Standard messages',
      cta: 'CTA',
      marketing: 'Marketing messages',
      preBooking: 'Pre-booking communications',
      postBooking: 'Post-booking communications',
      switchboard: 'Switchboard messages',
    },
    categoryDescriptions: {
      promo: 'Messages for promotions and coupons.',
      fidelity: 'Messages for loyalty programmes, points and cards.',
      marketing: 'Promotional messages, loyalty and automatic card actions.',
    },
    items: {
      ORDER_SENT: 'Order sent',
      ORDER_UPDATED: 'Order updated',
      ORDER_DELIVERY: 'Order delivery',
      ORDER_GENERIC: 'Supplier order',

      ATT: 'Message awaiting confirmation',
      ARR: 'Arrival confirmation',
      CONF: 'Booking confirmation',
      REF: 'Refused booking',
      DEL: 'Deleted booking',
      FQ1: 'Queue management 1',
      FQ2: 'Queue management 2',
      EDTU: 'Booking update (user)',
      EDTC: 'Booking update (customer)',
      EDTR: 'Booking update (restaurant)',
      ANS1: 'No-show alert 1',
      ANS2: 'No-show alert 2',
      ANS3: 'No-show alert 3',
      RPR: 'Promo reminder',
      DOB: 'Birthday promo',
      AAD: 'Review request',
      RRP: 'We miss you promo',
      CBP: 'Return invitation',
      CENTRALINO1: 'Switchboard function 1',
      CENTRALINO2: 'Switchboard function 2',
      CENTRALINO3: 'Switchboard function 3',
    },
    variables: {
      nomelocale: '@nomelocale@',
      ospiti: '@ospiti@',
      bambini: '@bambini@',
      nome: '@nome@',
      cognome: '@cognome@',
      oraprenotazione: '@oraprenotazione@',
      dataprenotazione: '@dataprenotazione@',
      servizioextra: '@servizioextra@',
      linkmodificaprenotazione: '@linkmodificaprenotazione@',
      linkrichiestacartadicredito: '@linkrichiestacartadicredito@',
      avviso_uscita: '@avviso_uscita@',
    },
    detail: {
      close: 'Close',
      pageSubtitle: 'Configure automatic message channels for this template.',
      marketingTitle: 'Marketing message',
      enableConfiguration: 'Enable this configuration',
      enableConfigurationHint:
        'The changes apply to all programs that use {{type}}.',
      enableWhatsApp: 'Enable WhatsApp',
      enableTemplates: 'Enable templates',
      enableEmail: 'Enable Email',
      emailObject: 'Subject',
      configuration: 'Configuration',
      configurationHint: 'Choose how you want to send messages',
      configurationHintPreBooking: 'Configure sending methods and timing',
      configurationHintPostBooking:
        'Configure sending and scheduling of post-booking messages',
      sendingMethods: 'Sending methods',
      sendingMode: 'Sending mode',
      sendingTiming: 'Sending timing',
      whenToSend: 'When to send',
      sendingNow: 'Sending now',
      timingImmediate: 'Send the message immediately after booking 🕒',
      timingOneHourBefore: 'Send the message 1 hour before 🕒',
      timingHoursAdvance: 'Send the message {{count}} hours in advance 🕒',
      timingOneDayAdvance: 'Send the message 1 day in advance 🕒',
      timingDaysAdvance: 'Send the message {{count}} days in advance 🕒',
      whenToSendOneDayLater: 'Send message 1 day later',
      whenToSendDaysLater: 'Send the message {{count}} days later',
      sendingWhatsAppOnly: 'Send WhatsApp only',
      sendingWhatsAppAndEmail: 'Send WhatsApp and Email',
      whatsappMessage: 'WhatsApp Message',
      whatsappMessageDesc:
        'Configure the WhatsApp message sent to the customer',
      whatsappForeign: 'WhatsApp foreign customers',
      whatsappForeignDesc:
        'English version. Automatic message in English for customers with international dialing codes.',
      whatsappBusinessTemplate: 'WhatsApp Business Template',
      whatsappBusinessTemplateDesc:
        'Select from Meta-approved templates for sending via WhatsApp Business API.',
      smsMessage: 'SMS message',
      smsMessageDesc: 'Configure the SMS message sent to the customer',
      email: 'Email',
      emailDesc: 'Configure the email message sent to the customer',
      emailSubject: 'Email subject',
      emailSubjectPlaceholder: 'Ex: Communication from @hotel-name',
      bodyPlaceholder: 'Write your message here...',
      inspireMe: 'Inspire me',
      testSending: 'Test sending',
      testSendPrompt: 'Enter the number to which you want to send the message',
      testSendAction: 'Send',
      selectTemplate: 'Select Template',
      selectTemplateTooltip: 'Select Template from those approved by meta',
      selectTemplateHint: 'Choose a Meta-approved template for this message.',
      selectedTemplate: 'Selected template',
      noTemplateSelected: 'No template selected',
      noTemplateHint:
        'No template selected. Select a Meta-approved template to set up sending via WhatsApp Business API.',
      templateCategory: 'Category',
      templateType: 'Type',
      templateLanguages: 'Languages',
      templateLanguage: 'Language',
      templateVariableMap: 'Variable map',
      templateBody: 'Body',
      templateBodyEmpty: 'No body preview available for this language.',
      ctaButton: 'Call to action',
      ctaDynamicPath: 'Dynamic path',
      ctaDynamicPathPlaceholder:
        'Dynamic path, e.g. /menu/xxxx0111 or /booking/0000101111',
      ctaDynamicPathHint:
        'This is appended to the template URL when the message is sent, in place of {{1}}, so you can build links based on the message context.',
      ctaDynamicPathRequired: 'The dynamic path cannot be empty.',
      ctaUrlValid: '{{url}} is valid',
      ctaUrlInvalid: 'Invalid URL. Check the format.',
      ctaLinksTooltip: 'What are my options?',
      ctaLinksTitle: 'Links available for services created on Pro20',
      ctaLinksDesc: 'Select a link to use it as the dynamic path.',
      ctaLinksEmpty:
        'You have not created any service on Pro20 yet, so there is nothing to suggest.',
      ctaLinkUnnamed: 'Unnamed link',
    },
    toast: {
      refreshed: 'Message list refreshed.',
      saveSuccess: 'Message configuration saved.',
      saveError: 'Could not save message configuration.',
      testSent: 'Test message sent successfully.',
      sendError: 'Could not send message.',
      templateSelected: 'Template selected.',
      resetSuccess: 'Configuration restored to defaults.',
      resetError: 'Could not reset configuration. Please try again.',
      applyDefaultsSuccess:
        'Default channel texts applied and channel enabled.',
      applyDefaultsError:
        'Could not apply defaults. Please try again or cancel.',
      detailUnavailable:
        'Message detail is not available for this category yet.',
    },
    validation: {
      whatsappBodyRequired:
        'Enter a WhatsApp message when WhatsApp is enabled.',
      templateRequired:
        'Select a WhatsApp Business template when templates are enabled.',
      emailSubjectRequired: 'Enter the email subject when Email is enabled.',
      emailBodyRequired: 'Enter the email body when Email is enabled.',
    },
    reset: {
      tooltip: 'Restore default configuration',
      title: 'Reset configuration?',
      description:
        'Existing configurations will be replaced with the default configuration.',
      confirm: 'Yes, go ahead!',
      cancel: 'Cancel',
      resetting: 'Resetting…',
    },
    applyDefaults: {
      whatsappTooltip: 'Apply default texts and enable WhatsApp for this group',
      emailTooltip:
        'Apply default subject and body and enable email for this group',
      whatsappAria: 'Apply default WhatsApp configuration',
      emailAria: 'Apply default email configuration',
      whatsappTitle: 'Apply WhatsApp defaults?',
      emailTitle: 'Apply email defaults?',
      description:
        'Texts for the selected channel will be replaced and the channel will be enabled for the whole group. Message activation, other channels, and send rules stay unchanged.',
      marketingWhatsappNote:
        'WhatsApp will use the free-form message; official template data will be kept.',
      confirm: 'Apply and enable',
      cancel: 'Cancel',
      applying: 'Applying…',
    },
  },
  configureWhatsApp: {
    title: 'Configure WhatsApp',
    subtitle: 'Connect a WhatsApp Business number for this branch',
    disabled: {
      badge: 'Configuration disabled',
      title: 'WhatsApp configuration not available',
      description: 'Proceed via the mobile app to enable this feature.',
    },
    onboarding: {
      eyebrow: 'WhatsApp Business',
      title: 'Connect the local WhatsApp number',
      description:
        'Create a new instance, scan QR code, and enable the sending of automatic messages from the PRO20 platform.',
      create: 'Create configuration',
      guided: 'Guided and reversible operation',
      featureSecureTitle: 'Secure & reliable',
      featureSecureDesc: 'Enterprise-grade security',
      featureQuickTitle: 'Quick setup',
      featureQuickDesc: 'Takes less than 2 minutes',
      featureReversibleTitle: 'Reversible',
      featureReversibleDesc: 'Change or disconnect anytime',
      step1Title: 'Create instance',
      step1Desc:
        'Generate the technical configuration connected to the selected branch.',
      step1Time: '~ 30 sec',
      step2Title: 'Scan QR',
      step2Desc:
        'Open WhatsApp on your phone and connect your device via QR code.',
      step2Time: '~ 60 sec',
      step3Title: 'Try sending',
      step3Desc:
        'Send a test message and check that everything is operational.',
      step3Time: '~ 30 sec',
    },
    creating: {
      title: 'Configuration in progress…',
      description: 'Please wait while we create your WhatsApp instance.',
      starting:
        'WhatsApp is starting. The first connection can take a couple of minutes.',
    },
    success: {
      title: 'Completed',
      description: 'Instance created successfully!',
      banner:
        'You can now link your device with a QR code or mobile pairing code.',
      continue: 'Continue',
    },
    configured: {
      heading: 'WhatsApp instance',
      subtitle: 'Manage linking, status, and test messages for this branch.',
      statusInstanceCreated: 'Instance created',
      statusQr: 'Waiting for QR scan',
      statusReady: 'Connected',
      statusCreating: 'Creating…',
      statusFailed: 'Connection failed',
      restart: 'Restart connection',
      stillStarting:
        'WhatsApp is still starting. This page will keep checking. Use Scan QR when the code is ready, or restart the connection.',
      failureHint:
        'The WhatsApp engine did not start. Restart the connection to try again.',
      scanQr: 'Scan QR',
      delete: 'Delete',
      deleting: 'Deleting...',
      statusDeleting: 'Deleting...',
      sessionName: 'Session name',
      instanceId: 'Instance ID',
      connectedNumber: 'Connected number',
      operatingStatus: 'Operating status',
      nextStepTitle: 'Next step',
      nextStepQr:
        'Scan the QR code with WhatsApp on your phone to link the device.',
      notLinked: 'Not linked yet',
    },
    link: {
      title: 'Link device',
      description: 'Use a QR code or a phone pairing code to connect WhatsApp.',
      tabQr: 'QR code',
      tabPhone: 'Mobile code',
      qrInstructions:
        'Open WhatsApp on your phone → Linked devices → Link a device, then scan this code.',
      waiting: 'Waiting for scan…',
      phoneInstructions:
        'Enter the mobile number that will be linked, then request a pairing code.',
      phoneLabel: 'Mobile number',
      phonePlaceholder: '333 123 4567',
      getCode: 'Get code',
      pairingCode: 'Your pairing code',
      pairingHint:
        'Enter this code in WhatsApp → Linked devices → Link with phone number.',
    },
    delete: {
      title: 'Delete WhatsApp configuration?',
      description:
        'This will remove the instance for this branch. You can create a new one later.',
      confirm: 'Yes, delete',
    },
    test: {
      title: 'Send test message',
      phone: 'Recipient number',
      phonePlaceholder: '333 123 4567',
      message: 'Message',
      defaultMessage: 'Hello! This is a test message from Proventi.',
      send: 'Send test',
      success: 'Test message sent successfully.',
      disabledHint:
        'Scan the QR code and link WhatsApp before sending a test message.',
      notConnected:
        'WhatsApp session is not connected. Scan the QR code to link your device first.',
    },
    toast: {
      linked: 'WhatsApp linked successfully.',
      stillStarting:
        'WhatsApp is still starting. This page will keep checking.',
      createFailed: 'Failed to create WhatsApp session.',
      qrFailed: 'Failed to prepare WhatsApp QR.',
    },
  },
  configureWhatsAppOfficial: {
    title: 'Configure WhatsApp Official',
    subtitle:
      'Connect your official WhatsApp Business (Meta) number for this branch.',
    loading: 'Loading onboarding status…',
    loadError: 'Failed to load onboarding status.',
    retry: 'Retry',
    toast: {
      onboardSuccess: 'WhatsApp onboarding completed successfully.',
      onboardError: 'Failed to complete WhatsApp onboarding.',
      registerSuccess: 'Phone number registered successfully.',
      registerError: 'Failed to register phone number.',
      businessDetailsSuccess: 'Business details loaded successfully.',
      businessDetailsError: 'Failed to load business details.',
      missingPhoneNumberId: 'Phone number ID is missing.',
      metaError: 'WhatsApp signup error: {{message}}',
      missingBranch: 'Branch code is missing.',
      sdkNotReady: 'Facebook SDK is not ready yet.',
    },
    onboarding: {
      eyebrow: 'WhatsApp Business API',
      title: 'Connect with WhatsApp Business API',
      description:
        'In this section you can create and connect a number to the official WhatsApp Business channel. Configuration lets you associate your business number with the platform to start sending and receiving messages in a professional, automated and policy-compliant way.',
      connect: 'Create configuration',
      connecting: 'Connecting…',
      howToConnect: 'How to connect',
      featuresTitle: 'With Meta you can',
      featureExisting: 'Connect an existing WhatsApp Business number',
      featureNew: 'Create a new account with Meta',
      step1Title: 'Register your number',
      step1Desc:
        'Add and verify your business phone number with Meta to activate the channel.',
      step1Time: '~ 2 min',
      step2Title: 'Create template & get approved',
      step2Desc:
        'Build a message template and submit it to Meta for review and approval.',
      step2Time: '~ 1 day',
      step3Title: 'Send text template',
      step3Desc:
        'Send an approved template message to start a new conversation.',
      step3Time: '~ 30 sec',
      step4Title: 'Send to a phone number',
      step4Desc:
        'Deliver messages to any customer phone number and start chatting.',
      step4Time: '~ 30 sec',
    },
    configured: {
      heading: 'WhatsApp Onboarding Information',
      subtitle: 'Your official WhatsApp Business account is connected.',
      connectedBadge: 'Connected',
      phoneNumberId: 'Phone Number ID',
      wabaId: 'WhatsApp Business Account ID',
      businessId: 'Business ID',
      onboardingStatus: 'Onboarding Status',
      phoneRegistrationStatus: 'Phone Registration Status',
      statusCompleted: 'Completed',
      statusSuccess: 'Success',
      statusPending: 'Pending',
      statusRegistered: 'Registered',
      statusNotRegistered: 'Not registered',
      registerPhone: 'Register Your Number',
      registeringPhone: 'Registering…',
      businessDetailsHeading: 'Business details',
      businessDetailsEmpty:
        'Business details are not available yet. Fetch them from Meta using your phone number ID.',
      fetchBusinessDetails: 'Get business details',
      fetchingBusinessDetails: 'Fetching…',
      verifiedName: 'Verified name',
      displayPhoneNumber: 'Display phone number',
      qualityRating: 'Quality rating',
      codeVerificationStatus: 'Code verification',
      platformType: 'Platform type',
      throughputLevel: 'Throughput',
      phoneStatus: 'Phone status',
      accountMode: 'Account mode',
      nameStatus: 'Name status',
      messagingLimitTier: 'Messaging limit',
      officialBusinessAccount: 'Official business account',
      businessProfileHeading: 'Business profile',
      email: 'Business email',
      vertical: 'Business category',
      description: 'Business description',
      about: 'About',
      address: 'Address',
      websites: 'Websites',
      metaPhoneNumberId: 'Meta phone number ID',
      webhookApplication: 'Webhook URL',
      reset: 'Reset configuration',
      resetting: 'Resetting…',
    },
    reset: {
      title: 'Reset configuration?',
      description:
        'This only resets the local view so you can run configuration again. It does not delete the backend onboarding.',
      confirm: 'Yes, reset',
    },
    guide: {
      title: 'WhatsApp Business API Onboarding Guide',
      intro:
        'Welcome! Follow the steps below to connect your WhatsApp Business Account to our platform.',
      prerequisitesTitle: 'Prerequisites',
      prerequisitesIntro:
        'Before you begin, please ensure you have the following:',
      prereq1: 'A Facebook account with Admin access.',
      prereq2: 'A Meta Business Manager account (or be ready to create one).',
      prereq3:
        'A valid mobile number that can receive SMS or Voice verification.',
      prereq4:
        'A phone number not currently registered with WhatsApp Messenger, the WhatsApp Business App, or another WhatsApp Business API provider.',
      prereq5:
        'Your business information ready: business name, website (optional), email, and category.',
      prereq6: 'A valid credit/debit card to add as a payment method in Meta.',
      prereq7: 'A stable internet connection.',
      prereqNote:
        'Important: If your phone number is currently used in the WhatsApp Business App, remove it from the app before connecting it to the WhatsApp Business Platform.',
      stepsTitle: 'Onboarding steps',
      step1Title: 'Step 1 – Login to our platform',
      step1Body:
        'Sign in to your account, navigate to WhatsApp Integration, and click Connect WhatsApp.',
      step2Title: 'Step 2 – Login with Facebook',
      step2Body:
        'A secure Meta (Facebook) login window opens. Sign in with your Facebook account and click Continue.',
      step3Title: 'Step 3 – Select or create a Business Manager',
      step3Body:
        'Select your existing Meta Business Manager, or click Create Business Account and enter your business name, email, website and country, then click Next.',
      step4Title: 'Step 4 – Create or select a WhatsApp Business Account',
      step4Body:
        'Use an existing WhatsApp Business Account, or create a new one by providing the account name, display name and business category, then click Continue.',
      step5Title: 'Step 5 – Add your phone number',
      step5Body:
        'Select your country, enter your phone number, choose SMS or Voice Call verification, then enter the 6-digit verification code.',
      step6Title: 'Step 6 – Grant permissions',
      step6Body:
        'Meta will request the necessary permissions. Click Continue and Finish; our platform completes the setup automatically.',
      step7Title: 'Step 7 – Connection complete',
      step7Body:
        'Once connected, you will see your connected phone number, WhatsApp Business Account, connection status, and be ready to send and receive messages.',
      nextStepsTitle: 'Recommended next steps',
      nextStep1: 'Create your WhatsApp message templates.',
      nextStep2: 'Add a valid payment method in Meta Business Manager.',
      nextStep3: 'Complete Meta Business Verification (recommended).',
      nextStep4: 'Send a test template message.',
      nextStep5: 'Configure your chatbot or automation (if applicable).',
      notesTitle: 'Important notes',
      note1:
        'One phone number can only be connected to one WhatsApp account at a time.',
      note2:
        'Do not delete your WhatsApp Business Account from Meta after onboarding.',
      note3:
        'Keep your payment method active to avoid message delivery interruptions.',
      note4:
        'Business Verification is recommended for higher messaging limits.',
      note5: 'Display Name approval is handled by Meta and may take some time.',
      issuesTitle: 'Common issues',
      issue1Title: 'Verification code not received',
      issue1Body:
        'Verify the phone number is correct, try the Voice Call option, and wait a few minutes before requesting another code.',
      issue2Title: 'Phone number already registered',
      issue2Body:
        'The number is connected to WhatsApp Messenger, the WhatsApp Business App, or another WhatsApp Business API provider. Remove it from the existing service before onboarding.',
      issue3Title: 'Permission error',
      issue3Body:
        'Ensure you are an Admin of both the Facebook account and the Meta Business Manager.',
      issue4Title: 'Payment required',
      issue4Body:
        'Meta requires a valid payment method before certain conversations can be initiated. Add your payment method in Meta Business Manager.',
      issue5Title: 'Business verification pending',
      issue5Body:
        'Your account can still be connected and used, but completing Business Verification is strongly recommended to unlock higher messaging limits and improve account trust.',
      needHelpTitle: 'Need help?',
      needHelpIntro:
        'If you encounter any issue during onboarding, please contact our support team with:',
      help1: 'Screenshot of the error.',
      help2: 'Phone number (including country code).',
      help3: 'Meta Business Manager ID (if available).',
      help4: 'WhatsApp Business Account ID (if available).',
      needHelpOutro:
        'Providing this information will help us resolve your issue more quickly.',
    },
  },
  whatsAppOfficial: {
    title: 'WhatsApp Official',
    subtitle: 'Manage inbox conversations for the official WhatsApp channel.',
    systemBadge: 'System',
    disabled: {
      badge: 'Section not enabled',
      title: 'This section is not enabled',
      description:
        'This feature is currently disabled for your account. Please contact support to know more.',
      infoTitle: 'Need access to this section?',
      infoDescription:
        "Reach out to our support team and we'll help you get started.",
      footerHelp: 'Still need help?',
      footerHelpDesc: 'Our support team is available 24/7 to assist you.',
    },
    inbox: {
      searchPlaceholder: 'Search conversations...',
      noConversations: 'No conversations found',
      noConversationsDesc:
        'New chats will appear here as soon as customers contact this branch.',
      emptySelectionTitle: 'Select a chat',
      emptySelectionDescription:
        'Choose a conversation from the list to view the thread and reply.',
    },
    thread: {
      sessionLabel: 'State 24 hours',
      sessionTooltip:
        'When the 24-hour service window is active, you can send free text replies. Otherwise, you must send an approved template first.',
      active: 'Active',
      inactive: 'Inactive',
      today: 'Today',
      yesterday: 'Yesterday',
      emptyTitle: 'No messages yet',
      emptyDescription:
        'This conversation is ready to receive the first inbound or outbound message.',
      pending: 'Sending…',
      retry: 'Retry',
      retryConfirmTitle: 'Retry message?',
      retryConfirmMessage: 'Are you sure you want to send this message again?',
      billingFree: 'Free',
      billingPaid: 'Paid',
    },
    delivery: {
      warningTooltip: 'Not delivered — tap for details',
      detailsTitle: 'Delivery issue',
      fallbackTitle: 'Message was not delivered',
      technicalDetails: 'Technical details',
      noTechnicalDetails: 'No technical details available.',
    },
    composer: {
      placeholder: 'Write a message...',
      send: 'Send',
      submitTemplate: 'Send Template',
      templateOnlyTitle: 'Template required',
      templateOnlyDescription:
        'The 24-hour customer service window is inactive. Send an approved template to reopen the conversation.',
      attach: 'Attach file',
      attachImage: 'Image',
      attachAudio: 'Audio',
      attachDocument: 'Document',
      removeAttachment: 'Remove attachment',
    },
    templateSend: {
      title: 'Send template',
      fillVariablesTitle: 'Fill variables',
      customerLabel: 'Customer',
      phoneLabel: 'Recipient phone',
      prefix: 'Prefix',
      number: 'Number',
      sendingTo: 'Sending to',
      phoneInvalid: 'Insert prefix and phone number before sending.',
      enterValuePlaceholder: 'Enter value',
      fieldRequired: 'This field is required',
      ctaUrlSuffix: 'CTA URL suffix',
      ctaUrlSuffixPlaceholder: 'URL suffix',
      ctaUrlSuffixHint:
        'Appended to the button URL when the template has a dynamic link.',
      noVariables: 'This template has no variables to fill.',
      sendMessage: 'Send message',
      cancel: 'Cancel',
      previewTitle: 'Preview',
      infoCategory: 'Category',
      infoLanguage: 'Language',
      infoType: 'Type',
      infoSendingTo: 'Sending to',
    },
    toast: {
      sendSuccess: 'Message sent.',
      sendError: 'Could not send message.',
      templateSendSuccess: 'Template sent.',
      templateSendError: 'Could not send template.',
      loadConversationsError: 'Could not load conversations.',
      loadMessagesError: 'Could not load messages.',
    },
  },
  templateslist: {
    import_no_branch: 'Branch code is missing. Please try again.',
    processing: 'Processing…',
    import_global_template: 'Import global template',
    bulk_import_started: 'Bulk import started',
    import_failed: 'Could not import templates.',
    bulk_import_job_not_found: 'Bulk import job not found.',
    bulk_import_poll_timeout: 'Bulk import timed out. Please try again.',
    bulk_import_bad_accept_response:
      'Invalid response from bulk import request.',
    bulk_import_failed_with_reason: 'Bulk import failed: {{message}}',
    bulk_import_completed_summary:
      'Bulk import completed. Success: {{success}}, Failed: {{failed}}',
    import_success: 'Global templates imported.',
  },
  whatsAppTemplates: {
    browseGroups: 'Template groups',
    allGroups: 'All templates',
    allStatuses: 'All statuses',
    filterStatus: 'Filter by status',
    searchEmpty: 'No templates match the search or selected filters.',
    groups: {
      UTILITY: 'Service',
      MARKETING: 'Marketing',
      AUTHENTICATION: 'Authentication',
      OTHER: 'Other',
    },
    title: 'Templates',
    subtitle:
      'Organize templates for quick access and scale communication with predefined messages.',
    learnMore: 'Learn more',
    importGlobal: 'Import Global Template',
    addTemplate: 'Add Template',
    tableTitle: 'Templates',
    sync: 'Sync Templates',
    searchPlaceholder: 'Search templates...',
    emptyTitle: 'No templates found',
    emptyDescription: 'Create or sync templates to start messaging customers.',
    detailsTitle: 'Template details',
    deleteTitle: 'Delete template?',
    deleteDescription:
      'Are you sure you want to delete "{{name}}"? This action cannot be undone.',
    columns: {
      statusDate: 'Status / Date',
      nameType: 'Name / Type',
      category: 'Category',
      language: 'Language',
      actions: 'Actions',
    },
    stats: {
      approved: 'Approved templates',
      pending: 'Pending templates',
      draft: 'Draft templates',
      rejected: 'Rejected templates',
    },
    status: {
      APPROVED: 'APPROVED',
      PENDING: 'PENDING',
      DRAFT: 'DRAFT',
      REJECTED: 'REJECTED',
    },
    languages: {
      en: 'English',
      it: 'Italiano',
      es: 'Español',
      fr: 'Français',
      nl: 'Nederlands',
      zh: '中文',
    },
    actions: {
      menu: 'Template actions',
      edit: 'Edit',
      delete: 'Delete',
    },
    toast: {
      learnMoreSoon: 'Documentation will be available soon.',
      importSoon: 'Global import will be available soon.',
      createSoon: 'Template creation will be available soon.',
      syncSuccess: 'Templates synced successfully.',
      syncError: 'Could not sync templates.',
      importSuccess: 'Global templates imported.',
      importError: 'Could not import global templates.',
      loadError: 'Could not load templates.',
      deleteSuccess: 'Template deleted.',
      deleteError: 'Could not delete template.',
    },
    create: {
      title: 'New Template',
      subtitle: 'Easily create new templates according to your needs.',
      backToList: 'Back to templates',
      formTitle: 'Add New Template',
      editTitle: 'Edit Template',
      editSubtitle: 'Update a rejected template and resubmit it for review.',
      editFormTitle: 'Edit Template',
      category: 'Category',
      name: 'Template name',
      namePlaceholder: 'e.g. booking_confirmation_v1',
      language: 'Template language',
      typeLabel: 'Type of template',
      types: {
        text: 'Text',
        image: 'Image',
        video: 'Video',
        audio: 'Audio',
        document: 'Document',
      },
      categories: {
        UTILITY: 'Utility',
        MARKETING: 'Marketing',
        AUTHENTICATION: 'Authentication',
      },
      uploadFile: 'Upload file',
      browse: 'Browse…',
      uploading: 'Uploading…',
      clearMedia: 'Clear',
      noFileChosen: 'No file chosen',
      orPasteUrl: 'Or paste handle / URL',
      mediaUrlPlaceholder: 'https://…',
      previewHandlePlaceholder: 'Header media uploaded (preview unavailable)',
      mediaHintImage: 'JPEG or PNG only, maximum 5 MB.',
      mediaHintVideo: 'MP4 only, maximum 16 MB, max 60 seconds.',
      mediaHintAudio: 'MP3, OGG or M4A, maximum 16 MB.',
      mediaHintDocument: 'PDF or Office documents, maximum 100 MB.',
      heading: 'Heading (optional)',
      headingHint: 'Shown above the message body.',
      body: 'Body',
      footer: 'Footer',
      variablesTitle: 'Variables',
      variables: {
        requested_amount: 'Requested deposit',
        balance_due: 'Remaining balance',
        paid_total: 'Total received',
        deposit_percentage: 'Deposit percentage',
        payment_link: 'Deposit payment link',

        service_name: 'Service',
        order_number: 'Order number',
        ordering_channel: 'Asporto / Delivery',
        scheduled_label: 'Order date and time',
        total_amount: 'Order total',
        order_link: 'Order recap link',
        delivery_address: 'Delivery address',
        pickup_address: 'Pickup address',
        paid_amount: 'Paid amount',
        refund_amount: 'Refund amount',
        cancellation_reason: 'Cancellation reason',
        failure_reason: 'Delivery failure reason',
        amount_due: 'Amount due',

        customerName: 'Customer Name',
        branchName: 'Branch Name',
        bookingId: 'Booking ID',
        discountCode: 'Discount Code',
        promoCode: 'Promo Code',
        bookingDate: 'Booking Date',
        bookingTime: 'Booking Time',
        bookingUpdateLink: 'Booking Update Link',
        branchCode: 'Branch Code',
        guestCount: 'Guest Count',
        childrenCount: 'Children Count',
        creditCardRequestUrl: 'Credit Card Request URL',
        depositRequestLink: 'Deposit Request Link',
        depositAmount: 'Deposit Amount',
        expectedLeaveTime: 'Expected Leave Time',
        qrCodeImageUrl: 'QR Code Image URL',
      },
      buttonsTitle: 'Buttons',
      buttonType: 'Button type',
      buttonText: 'Button text',
      buttonUrl: 'Button URL',
      quickReply: 'Quick reply',
      ctaUrl: 'CTA URL',
      addQuickReply: 'Quick reply',
      addCta: 'CTA',
      removeButton: 'Remove',
      buttonsHint: 'You can add up to 3 buttons per template.',
      submit: 'Send',
      previewTitle: 'Preview WhatsApp',
      previewEmpty: 'Your template preview will appear here.',
      validation: {
        categoryRequired: 'Select a category.',
        nameMin: 'Name must be at least 3 characters.',
        nameMax: 'Name must be at most 60 characters.',
        nameFormat: 'Use lowercase letters, numbers, and underscores only.',
        languageRequired: 'Select a language.',
        bodyRequired: 'Body is required.',
        bodyMax: 'Body must be at most 1024 characters.',
        mediaRequired: 'Media is required for image and video templates.',
        invalidImageType: 'Image must be JPEG or PNG.',
        invalidVideoType: 'Video must be MP4.',
        imageTooLarge: 'Image must be 5 MB or smaller.',
        videoTooLarge: 'Video must be 16 MB or smaller.',
        videoDurationMax: 'Video must be 60 seconds or shorter.',
        videoDurationUnreadable: 'Could not read video duration.',
        buttonLabelRequired: 'Button text is required.',
        buttonUrlRequired: 'CTA buttons require a URL.',
        qrPlaceholder: 'Quick reply text cannot contain variables.',
        qrMax: 'Quick reply text must be 25 characters or fewer.',
        urlInvalid: 'CTA URL must be a valid http(s) address.',
      },
      toast: {
        success: 'Template created successfully.',
        error: 'Could not create template.',
        updateSuccess: 'Template updated successfully.',
        updateError: 'Could not update template.',
        uploadSuccess: 'Header media uploaded.',
        uploadFailed: 'Could not upload header media.',
      },
    },
  },
  notifications: {
    title: 'Notifications',
    allCaughtUp: 'All caught up',
    unread: 'unread notification',
    unreadPlural: 'unread notifications',
    markAllRead: 'Mark all as read',
    noNotifications: 'No notifications',
    noNotificationsDesc:
      "You're all caught up. New notifications will appear here.",
    viewAll: 'View all notifications',
  },
  bookings: {
    history: {
      title: 'Booking History',
      subtitle: 'Bookings for {{branch}}',
      subtitleDefault: 'View and manage booking history',
      totalBookings: '{{count}} bookings',
      bookingsForDateBefore: '{{count}} bookings for ',
      bookingsForDateAfter: '',
      previousDay: 'Previous day',
      nextDay: 'Next day',
      calendar: {
        title: 'Select booking date',
        openCalendar: 'Open booking calendar',
        previousMonth: 'Previous month',
        nextMonth: 'Next month',
        bookings: '{{count}} bookings',
        arrived: '{{count}} arrived',
        refused: '{{count}} refused',
        legendBookings: 'Bookings',
        legendArrived: 'Arrived',
        legendRefused: 'Refused',
      },
      calendarConfig: {
        eyebrow: 'Reservation calendar',
        title: 'Configure this day',
        subtitle: 'Edit only the selected date in the calendar.',
        branchLabel: 'Branch',
        singleDay: 'This day only',
        updateDate: 'Update {{date}}',
        dateRange: 'Date range',
        dateRangeHint: 'Create or overwrite multiple days',
        eventLabel: 'Recurrence or event',
        eventPlaceholder: 'Add a recurrence or event name',
        daysSelected: '{{count}} day selected',
        daysSelectedPlural: '{{count}} days selected',
        rangePlaceholder: 'Select date range',
        dateRangeRequired: 'Please select a start and end date.',
        rulesTitle: 'Booking rules',
        manual: 'Manual',
        acceptTo: 'Accept to',
        acceptToValue: 'Number',
        acceptToRequired: 'This field is required',
        acceptAll: 'Accept all',
        refuseAll: 'Refuse all',
        manualHint: 'Manual leaves the decision to the normal app/portal flow.',
        close: 'Close',
        saveDay: 'Save day',
        saveRange: 'Save range',
        loadError: 'Failed to load day configuration.',
        saveSuccess: 'Day configuration saved.',
        saveError: 'Failed to save day configuration.',
      },
      exportPdf: "Download PDF of today's reservations",
      exportPdfGeneratingTitle: 'PDF generation',
      exportPdfGeneratingDescription: 'Preparing the bookings file...',
      exportPdfReadyTitle: 'PDF ready',
      exportPdfReadyDescription: 'The file was generated successfully.',
      exportPdfOpen: 'Open PDF',
      exportPdfClose: 'Close',
      exportPdfError: 'Failed to generate booking PDF. Please try again.',
      addBooking: 'Add booking',
      shifts: {
        all: 'All',
        filterLabel: 'Service',
        breakfast: 'Breakfast',
        daily: 'Lunch',
        happyhour: 'Happy hour',
        night: 'Dinner',
      },
      searchPlaceholder: 'Search customer, email, phone or booking code',
      filterStatus: 'Filter states',
      callCustomer: 'Call {{name}}',
      moreActions: 'More actions',
      snapshot: {
        bookings: 'Bookings',
        confirmed: 'Confirmed',
        arrived: 'Arrived',
        waiting: 'Waiting',
        issues: 'No-show / refused',
        configureShifts: 'Configure day booking rules',
        filterShift: 'Filter {{shift}}',
        shiftSelected: '{{shift}} selected — click to clear',
        shiftUnavailable: '{{shift}} is not configured for this branch',
      },
      itemsPerPage: 'Items per page',
      range: '{{start}} - {{end}} of {{total}}',
      date: 'Date',
      time: 'Time',
      timeAndGuests: 'Time & Guests',
      customer: 'Customer',
      guests: 'Guests',
      guestCount: '{{count}} guests',
      childrenCount: '+ {{count}} children',
      event: 'Event',
      noEvent: 'no event',
      contacts: 'Contacts',
      details: 'Details',
      openImage: 'Open image',
      openPdf: 'Open PDF of the reservation',
      googleReservation: 'Booking from Google Reservation',
      walkIn: 'Walk in',
      associatedServicesProducts: 'Associated services/products',
      generatedBooking: 'Generated booking {{score}}/100',
      extraServices: 'Extra services {{count}}',
      extraServicesOnly: 'Extra services',
      tables: 'Assigned tables',
      noTables: 'No tables assigned',
      notes: 'Private notes',
      noNotes: 'No private notes',
      manageBooking: 'Manage Booking',
      eventDetails: {
        title: 'Event Details',
        eventName: 'Event name',
        eventCode: 'Event code',
        startDate: 'Start',
        maxCapacity: 'Capacity',
        eventDescription: 'Description',
        eventImage: 'Event image',
        formCodes: 'Forms',
        noImage: 'No image',
      },
      menuDetails: {
        title: 'Menu details',
        menuName: 'Menu name',
        peopleCount: 'People count',
      },
      viewBookingImage: 'View booking image',
      bookingImage: 'Booking image',
      users: 'users',
      status: 'Status',
      processedBy: 'Processed by',
      source: 'Source',
      noBranch: 'Select a branch to view booking history',
      empty: 'No bookings found',
      emptyDesc: 'There are no reservations for the day {{date}}.',
      updateStatus: 'Update the booking status',
      confirmOperationTitle: 'Confirm operation?',
      confirmStatusChange: 'Update the booking status to "{{status}}"?',
      notifyCustomerTitle: 'Notify customer?',
      notifyCustomerMessage:
        'Do you want to notify the customer of this change?',
      statusUpdateSuccess: 'Booking status updated successfully.',
      statusUpdateError: 'Failed to update booking status. Please try again.',
      updatingStatus: 'Updating status...',
      associatedServices: {
        title: 'Associated services/products',
        reference: 'Booking {{code}}',
        quantity: '×{{count}}',
        empty: 'No associated services for this booking.',
        paid: 'Paid',
        unpaid: 'Unpaid',
      },
      messageHistory: {
        open: 'Message history',
        communicationError: 'Message delivery failed',
        title: 'Message history',
        reference: 'Reference {{code}}',
        refresh: 'Refresh',
        empty: 'No messages sent for this booking yet.',
        loadError: 'Failed to load message history.',
        endpoint: 'Endpoint',
        eventCode: 'Event code',
        payload: 'Payload',
        response: 'Response',
        statusProcessed: 'Sent',
        statusPending: 'Pending',
        messageText: 'Message',
        messageTextUnavailable: 'Message text is not available.',
        httpStatus: 'HTTP {{status}}',
        attempts: '{{count}} attempts',
        resubmit: 'Resend message',
        resubmitSuccess: 'Message resubmitted successfully.',
        resubmitError: 'Failed to resubmit message. Please try again.',
        resubmitConfirmTitle: 'Resend message?',
        resubmitConfirmMessage: 'Are you sure you want to resend this message?',
      },
      statusActions: {
        confirm: 'Confirmation',
        refuse: 'Refuse',
        eliminate: 'Eliminate',
        arrived: 'Arrived',
        notArrived: 'Not Arrived',
        notifyUser: 'User notification to go to the venue',
        bookingAlreadyProcessed: 'Booking Already Processed',
        moveToConfirm: 'Move to confirmed',
      },
      statuses: {
        IN_ATTESA: 'Waiting',
        MODIFICATO_DA_UTENTE: 'Modified by user',
        ELIMINATO_DA_UTENTE: 'Eliminated by user',
        MODIFICA_CONFERMATA: 'Modification confirmed',
        MODIFICA_RIFIUTATA: 'Modification refused',
        PRE_CONFERMA_PAGAMENTO: 'Pre-payment confirmation',
        CONFERMATO: 'Confirmed',
        AVVISATO_LISTA_ATTESA: 'Waiting list notified',
        ARRIVATO: 'Arrived',
        NON_ARRIVATO: 'No show',
        RIFIUTATO: 'Refused',
        LISTA_ATTESA: 'Waiting list',
        ELIMINATO: 'Cancelled',
      },
    },
    payment: {
      column: 'Payments',
      close: 'Close',
      connectRequired:
        'Stripe Connect is not ready for this venue. Complete onboarding to manage payments.',
      loadError: 'Could not load payment operations for this booking.',
      linkCopied: 'Payment link copied.',
      linkCopyError: 'Could not copy the payment link.',
      linkReadyTitle: 'Deposit link ready',
      linkReadyDescription: 'The link is ready to send through your channels.',
      badge: {
        title: 'Payments',
        cardPresent: 'Card on file',
        cardMissing: 'No card',
        paidPendingOps: 'Paid · pending operations',
        manage: 'Manage',
        noCard: 'No card linked',
        verifiedSuccess: 'Verified successfully',
        depositAuthorizedCapture: 'Deposit authorised · Capture',
        depositCaptured: 'Deposit captured',
        depositRefunded: 'Deposit refunded',
        depositPartiallyRefunded: 'Deposit partially refunded',
        depositPrefix: 'Deposit',
      },
      tooltip: {
        cardPresent: 'Open saved card details for this venue',
        cardMissing: 'Send the customer a request for card details',
        openOperations: 'Open deposit and payment operations for this booking',
        openVerified: 'Open payment details and verified card',
        noCard: 'No card linked. Open to manage payments',
      },
      status: {
        notRequired: 'Not required',
        pending: 'Awaiting payment',
        awaitingConfirmation: 'Payment sent, awaiting Stripe confirmation',
        cardVerified: 'Card verified',
        authorized: 'Not captured',
        paid: 'Paid',
        partiallyPaid: 'Partially paid',
        failed: 'Failed',
        canceled: 'Cancelled',
        refundPending: 'Refund in progress',
        partiallyRefunded: 'Partially refunded',
        refunded: 'Refunded',
        refundFailed: 'Refund failed',
      },
      actions: {
        copyLink: 'Copy link',
        refund: 'Refund',
      },
      modal: {
        title: 'Deposits',
        description:
          'Review pre-authorisations, generate a link, and decide whether to capture or release the deposit.',
        bookingLabel: 'Booking {{code}}',
        pendingWarning:
          'One or more payments are complete, but operations or links are still pending.',
        requested: 'Requested',
        captured: 'Captured',
        authorized: 'Authorised',
        netCaptured: 'Net captured',
        refunded: 'Refunded',
        descriptionLabel: 'Description',
        operationsTab: 'Operations ({{count}})',
        newRequestTab: 'New request',
        historyTitle: 'Operation history',
        historyDescription:
          'Deposits and authorisations linked to this booking.',
        emptyTitle: 'No operations yet',
        emptyDescription:
          'Open the “New request” tab to generate the first link.',
        createRequest: 'Create request',
      },
      create: {
        title: 'Create a deposit link',
        description:
          'The customer authorises the amount; you can capture or release it later.',
        amountLabel: 'Deposit amount (€)',
        descriptionLabel: 'Optional description',
        descriptionPlaceholder: 'Example: deposit for the booking',
        authorizeOnlyHint:
          'The amount is only authorised. You can capture or release it later.',
        submit: 'Generate deposit link',
        invalidAmount: 'Enter an amount greater than zero.',
        error: 'Could not create the Stripe link.',
        success: 'Deposit link created.',
        sent: 'Deposit link created and sent.',
        linkCreatedMessageFailed:
          'Link created, but the deposit request message was not sent.',
      },
      refund: {
        title: 'Refund payment',
        maxAmount: 'Maximum refundable amount: {{amount}}',
        amountLabel: 'Amount (€)',
        reasonLabel: 'Reason',
        invalidAmount: 'Enter a valid amount within the refundable maximum.',
        started: 'Refund started.',
        completed: 'Refund completed.',
        error: 'Refund failed. Check the status on Stripe and retry.',
        reasons: {
          requested_by_customer: 'Requested by customer',
          duplicate: 'Duplicate payment',
          fraudulent: 'Fraudulent',
        },
      },
    },
    create: {
      title: 'Create Booking',
      cancel: 'Cancel',
      submit: 'Create Booking',
      customerDetails: 'Customer Details',
      bookingDetails: 'Booking Details',
      firstName: 'First Name *',
      firstNamePlaceholder: 'Enter first name',
      lastName: 'Last Name *',
      lastNamePlaceholder: 'Enter last name',
      email: 'Email',
      emailPlaceholder: 'Enter email',
      phone: 'Phone Number *',
      phonePlaceholder: 'Enter phone number',
      nickName: 'Nick Name',
      nickNamePlaceholder: 'Enter nick name',
      bookingImage: 'Booking Image',
      pdfDocument: 'PDF Document',
      bookingTime: 'Booking Time *',
      bookingTimePlaceholder: 'Select booking time',
      guests: 'Guests *',
      guestsPlaceholder: 'Enter guests',
      children: 'Children *',
      childrenPlaceholder: 'Enter children',
      customerRequests: 'Customer Requests',
      customerRequestsPlaceholder: 'Enter customer requests',
      restaurantNotes: 'Restaurant Notes',
      restaurantNotesPlaceholder: 'Enter restaurant notes',
    },
    calendar: {
      selected: 'Selected: {{date}}',
      dotsHint: 'Dots indicate available bookings',
    },
  },
  forms: {
    title: 'Booking Form',
    subtitle: ' Manage all active, suspended or archived forms',
    addDataCollectionForm: 'Add Form',
    searchPlaceholder: 'Search for forms by name',
    noDataTitle: 'No Form',
    noDataDesc:
      'You have not yet created any forms. Start by creating your first form to collect bookings and interactions.',
    status: 'Status',
    active: 'Active',
    suspended: 'Suspended',
    url: 'URL',
    qrCode: 'QR Code',
    preview: 'Preview',
    googleBooking: 'Google Booking',
    googleReservation: 'Google Reservation',
    copyUrl: 'Copy URL',
    downloadQr: 'Download QR',
    editForm: 'Edit Form',
    dublicateUrl: 'Dublicate Form',
    statusActive: 'Active',
    statusInactive: 'Inactive',
    statusDraft: 'Draft',
    statsCount: '{{count}} Forms',
    updatedAgo: 'Updated 2 mins ago',
    deleteForm: 'Delete Form',
    share: {
      label: 'Share form',
      whatsapp: 'WhatsApp',
      instagram: 'Instagram',
      twitter: 'Twitter / X',
      facebook: 'Facebook',
      defaultText: 'Book with us',
      instagramCopied: 'Link copied. Paste it in Instagram to share.',
      instagramOpened:
        'Instagram opened. Form link copied — paste it to share.',
      copyFailed: 'Could not copy the link. Please try again.',
    },
    createTitle: 'Create Form',
    formName: 'Form Name',
    formNamePlaceholder: 'Enter form name',
    createForm: 'Create form',
    toast: {
      createSuccess: 'Form created successfully.',
      createError: 'Failed to create the form. Please try again.',
      deleteSuccess: 'Form deleted successfully.',
      deleteError: 'Failed to delete the form. Please try again.',
      duplicateSuccess: 'Form duplicated successfully.',
      duplicateError: 'Failed to duplicate the form. Please try again.',
      statusActivateSuccess: 'Form activated successfully.',
      statusSuspendSuccess: 'Form suspended successfully.',
      statusActivateError: 'Failed to activate the form. Please try again.',
      statusSuspendError: 'Failed to suspend the form. Please try again.',
      googleReservationEnabled: '{{name}} Google Reservation enabled',
      googleReservationDisabled: '{{name}} Google Reservation disabled',
      googleReservationError:
        'Could not update Google Reservation. Please try again.',
      logoUploadSuccess: 'Logo uploaded successfully.',
      logoUploadError: 'Failed to upload logo.',
      logoRemoveSuccess: 'Logo removed successfully.',
      logoRemoveError: 'Failed to remove logo.',
      bannerUploadSuccess: 'Banner uploaded successfully.',
      bannerUploadError: 'Failed to upload banner.',
      bannerRemoveSuccess: 'Banner removed successfully.',
      bannerRemoveError: 'Failed to remove banner.',
    },
    deleteModel: {
      deleteTitle: 'Delete Form?',
      deleteDescription:
        'Are you sure you want to delete this form? This action cannot be undone.',
    },
    validation: {
      formNameMin: 'Form name must be at least 2 characters',
      formNameMax: 'Form name cannot exceed 50 characters',
    },
    scanQrAccess: 'Scan QR code to access:',
    qrClose: 'Close',
    qrDownload: 'Download',
    edit: {
      pageTitle: 'Personal data consent',
      editTitle: 'Edit {{name}}',
      subtitle: 'Configure the reservation form',
      saveChanges: 'Save changes',
      close: 'Close',
      toast: {
        saveSuccess: 'Form saved successfully.',
        saveError: 'Failed to save form.',
      },
      tabListLabel: 'Form edit sections',
      uploading: 'Uploading...',
      removing: 'Removing...',
      tabs: {
        general: 'General',
        capacity: 'Capacity',
        schedule: 'Schedule',
        formFields: 'Form fields',
        preferences: 'Preferences',
        services: 'Services',
      },
      general: {
        media: 'Media',
        logo: 'Logo',
        banner: 'Banner image',
        bannerHint: 'Banner image — drag and drop or browse',
        basicDetails: 'Basic details',
        formName: 'Form name',
        customerName: 'Emoticon Form',
        address: 'Local address to show on form',
        customThankYouPage: 'Custom Thank You Page',
        buttonTextOnBookingForm: 'Button Text On Booking Form',
        policy: 'Policy',
        webLink: 'Web link',
      },
      capacity: {
        hint: 'Reservation limits and table rules for this form.',
        sections: {
          coverLimits: 'Cover limits',
          timeSettings: 'Time slot settings',
          policies: 'Policies',
        },
        maxGuests: 'Maximum covers before the call',
        maxGuestsHint:
          'What is the maximum number of covers your establishment accepts before requiring a call?',
        maxTableGuests: 'Max guests per table',
        maxTableGuestsHint:
          'Set the maximum number of guests you can accept at a single table. If you leave the value at 0, the maximum will equal the total seating capacity of the venue.',
        minGuests: 'Min covers per table',
        minGuestsHint: 'Smallest party size for a single table.',
        phoneOverbooking: 'Overbooking phone',
        phoneOverbookingHint:
          'Phone shown when guests exceed max per table. Format: PREFIX + NUMBER (e.g. +39 3444393343).',
        cut: 'Cut',
        cutHint: 'Split availability into booking slots of this length.',
        cutSlotOption: 'Divide time slots into {{minutes}}-minute slots',
        stayOnTable: 'Stay on the table',
        stayOnTableHint: 'Expected dining duration per reservation.',
        minutes: '{{count}} min',
        dogsAccepted: 'Dogs accepted',
        dogsAcceptedHint:
          'Allow customers to indicate they are bringing a dog.',
        menuSelectionMandatory: 'Menu selection mandatory',
        menuSelectionMandatoryHint:
          'When enabled, guests must choose a menu (or “No menu”) for each person so the total equals guests + children before continuing.',
        showRestaurantNoMenu: 'Show restaurant no-menu card',
        showRestaurantNoMenuHint:
          'When enabled, the public booking form shows a card explaining that the restaurant has no fixed menu.',
      },
      schedule: {
        shiftOverview: 'Shift overview',
        weeklyHours: 'Weekly hours',
        addTimeSlot: 'Add time slot',
        day: 'Day',
        open: 'Open',
        closed: 'Closed',
        hours: 'Hours',
        editSlot: 'Edit slot',
        deleteSlot: 'Delete slot',
        deleteSlotConfirmTitle: 'Delete this time slot?',
        deleteSlotConfirmDesc:
          'This will remove the selected time slot from this day.',
        deleteSlotConfirmMultiDay:
          'This service uses the same code on {{count}} days. Deleting it will remove it from all of those days.',
        extraordinarySection: 'Extraordinary openings / closures',
        addException: 'Add exception',
        dateRange: 'Date range',
        type: 'Type',
        closedOpenDay: 'Closed/Open Day',
        timeSlots: 'Time slots',
        cuttingMin: 'Cutting (min)',
        maxGuests: 'Max guests',
        noShifts: 'No shifts configured yet.',
        emptyWeeklyHours:
          'No weekly hours configured. Use + to create a schedule for all days.',
        emptyExtraordinary: 'No extraordinary openings or closures configured.',
        createForAllDays: 'Create general configuration for every day',
        resetConfiguration: 'Reset configuration',
        saveConfiguration: 'Save configuration',
        resetConfirmTitle: 'Delete all existing configurations?',
        resetConfirmDesc:
          'This will remove all schedule settings for this form.',
        resetProceed: 'Proceed',
        tags: 'Tags',
        tagsPlaceholder: 'Select tags',
        tagsPickerDesc: 'Select tags for this time slot.',
        tagsPickerEmpty: 'No tags available for this branch.',
        allowOverbook: 'Allow overbooking',
        alertMessage: 'Alert message',
        alertMessagePlaceholder: 'Enter an alert message for this time range',
        enableOverbookBlocking: 'Enable overbooking blocking',
        disableOverbookBlocking: 'Disable overbooking blocking',
        deposit: {
          tooltipInactive: 'Configure deposit / card check',
          tooltipValidation: 'Stripe: card validation only',
          tooltipDeposit: 'Stripe deposit: {{amount}} per guest',
          tooltipMixed: 'Mixed Stripe configuration across bands',
          allStandardBands: 'All standard bands',
          allExtraordinaryBands: 'All extraordinary bands',
          singleBand: 'Selected time band',
          depositTitle: 'Deposit',
          checkCardTitle: 'Check card',
          bulkHint:
            'The configuration will be applied in one operation to all bands in this section.',
          requestTitle: 'Request a credit card',
          requestDesc:
            'You can request just card verification or also a preauthorized deposit.',
          validationTitle: 'Card validation',
          validationDesc:
            'Verify and link the card without blocking an amount.',
          depositOptionTitle: 'Deposit',
          depositOptionDesc:
            'Validate the card and pre-authorize the configured amount.',
          amountLabel: 'Amount EUR',
          amountHint:
            'The amount is per guest: a pre-authorization of this amount per person will be required.',
          saveConfiguration: 'Save configuration',
          close: 'Close',
          toastSaveSuccess: 'Deposit configuration saved.',
          toastSaveError: 'Failed to save deposit configuration.',
        },
        legacyCreditCard: {
          enable: 'Enable direct credit card details request',
          disable: 'Disable direct credit card details request',
        },
        maxGuestsPerSlot: 'Max coverage per time slot',
        customizeCutter: 'Customize time slot cut on standard configuration',
        noSlotsForDay: 'No time slots for this day. Use + to add one.',
        slotClosedOnDate: 'slot not available on {{date}}',
        alertModalTitle: 'Alert for customer in case of time slot selection',
        maxGuestsModalTitle:
          'Set the maximum number of seats for this time slot {{range}}',
        maxGuestsModalDesc:
          'Choose the maximum number of guests allowed for this time slot.',
        cutterModalTitle: 'Update time slot cut',
        days: {
          monday: 'Monday',
          tuesday: 'Tuesday',
          wednesday: 'Wednesday',
          thursday: 'Thursday',
          friday: 'Friday',
          saturday: 'Saturday',
          sunday: 'Sunday',
        },
        addTimeAllDays: {
          title: 'Add time to all days',
          subtitle:
            "The available times respect the venue's time slots and the slots already configured.",
          chosenTime: 'Chosen time',
          duration: 'Duration: {{duration}}',
          startAt: '1. Start at',
          endsAt: '2. It ends at',
          allOkDesc: 'You can save this schedule for all days.',
          saveOnAllDays: 'Save on all days',
        },
        slotTime: {
          title: 'Slot time',
          subtitle: 'First choose when it starts. Then choose when it ends.',
          allOk: 'Everything OK',
          allOkDesc: 'You can save this time.',
          saveTime: 'Save time',
          timeRangeInvalid: 'End time must be after start time.',
          timeRangeConflict:
            'This time range conflicts with another. Please edit or delete one of the ranges.',
          occupiedTitle: 'Times already occupied',
          occupiedHint: 'The second list avoids these times automatically.',
          cannotSave: 'Change time',
          cannotSaveDesc: 'There is no valid end for this start.',
          cannotSaveButton: "Can't save",
          durationMinutes: '{{count}} minutes',
          durationHours: '{{count}} hours',
          durationHoursMinutes: '{{hours}} hours and {{minutes}} minutes',
          selectService: 'Select a service',
          selectServiceDesc:
            'Choose a meal band to add its opening hours to the schedule.',
          noLocalSlots:
            'Time slots were deleted in Local configuration. Create them again there, then add them to this schedule.',
          selectedCount: '{{count}} services selected',
          alreadyAdded: 'Already added',
        },
        extraordinary: {
          badge: 'Extraordinary configuration',
          createRange: 'Create range',
          editRange: 'Edit range',
          description: 'Description',
          descriptionPlaceholder: 'E.g. Ferragosto, private event...',
          rangeDate: 'Range date',
          rangeDatePlaceholder: 'Select date range',
          clearDates: 'Clear',
          applyDates: 'Apply',
          open: 'Open',
          create: 'Create',
          update: 'Update',
          validation: 'Description and date range are required.',
          dateRequired: 'Please select a date range.',
          dateOrder: 'End date cannot be before start date.',
          timeRangeInvalid: 'End time must be after start time.',
          addConfiguration: 'Add extraordinary configuration',
          saveConfiguration: 'Save configuration',
          addTimeSlot: 'Add time slot',
          deleteRange: 'Delete range',
          deleteConfirmTitle: 'Delete extraordinary range?',
          deleteConfirmDesc:
            'This will permanently remove this date range configuration.',
          toast: {
            createSuccess: 'Extraordinary range created.',
            updateSuccess: 'Extraordinary range updated.',
            saveError: 'Failed to save extraordinary range.',
            deleteSuccess: 'Extraordinary range deleted.',
            deleteError: 'Failed to delete extraordinary range.',
          },
        },
        toast: {
          saveSuccess: 'Schedule configuration saved.',
          saveError: 'Failed to save schedule.',
          resetSuccess: 'Schedule configuration reset.',
          resetError: 'Failed to reset schedule.',
          addSlotSuccess: 'Time slot added.',
          addSlotError: 'Failed to add time slot.',
          deleteSlotSuccess: 'Time slot deleted.',
          deleteSlotError: 'Failed to delete time slot.',
          updateTimeSuccess: 'Time slot updated.',
          updateTimeError: 'Failed to update time slot.',
          dayToggleSuccess: 'Day status updated.',
          dayToggleError: 'Failed to update day status.',
          configurationMissing: 'Day configuration is not available yet.',
          slotNotReady: 'Time slot is not ready. Please refresh and try again.',
          overbookToggleSuccess: 'Overbooking setting updated.',
          overbookToggleError: 'Failed to update overbooking setting.',
          legacyCreditCardToggleSuccess: 'Credit card request updated.',
          legacyCreditCardToggleError: 'Failed to update credit card request.',
          tagsSaveSuccess: 'Tags saved.',
          tagsSaveError: 'Failed to save tags.',
          tagsLoadError: 'Failed to load tags.',
          tagsDeleteSuccess: 'Tags removed.',
          alertSaveSuccess: 'Alert message saved.',
          alertSaveError: 'Failed to save alert message.',
          alertDeleteSuccess: 'Alert message removed.',
          maxGuestsInvalid: 'Enter a valid number of guests.',
          maxGuestsSaveSuccess: 'Maximum guests updated.',
          maxGuestsSaveError: 'Failed to update maximum guests.',
          cutterSaveSuccess: 'Time slot cut updated.',
          cutterSaveError: 'Failed to update time slot cut.',
        },
      },
      formFields: {
        hint: 'Choose which fields appear on the public reservation form.',
        configure: 'Configure form fields',
        configureDesc: 'Toggle fields shown to customers during booking.',
        name: 'Name',
        surname: 'Surname',
        phone: 'Phone',
        email: 'Email',
        dateOfBirth: 'Date of birth',
        zipCode: 'ZIP code (CAP)',
        customerNotes: 'Customer notes',
        field: 'Field',
        mandatory: 'Mandatory',
        nonMandatory: 'Non-mandatory',
      },
      preferences: {
        sectionTitle: 'Additional options',
        sectionDescription:
          'Extra configurations, allow your customers to communicate preferences, allergies or intolerances more easily.',
        singleChoicePredefined:
          'Fill out the list as suggested to create single preference choice options.',
        singleChoiceAdditional:
          'Creates an additional single-choice preference option',
        multiChoicePredefined:
          'Fill in the list as suggested to create multiple preference choice options.',
        multiChoiceAdditional:
          'Create an additional preference option with multiple choice',
        singleChoiceOptionsPlaceholder:
          'Examples: Outdoor Room | Indoor Room | Bar',
        multiChoiceOptionsPlaceholder: 'Ex: Lactose | Celiac | Peanuts',
        singleChoiceFieldPlaceholder: 'Ex: Single Choice Field Name',
        multiChoiceFieldPlaceholder: 'Ex: Field name on multiple choice',
        pipeSeparatedNote: 'Note: Separate values with the | character.',
        importRooms: 'Import my rooms',
        importRoomsSuccess: 'Rooms imported successfully.',
        importRoomsError: 'Failed to import rooms.',
        importRoomsEmpty: 'No rooms found for this branch.',
        importRoomsNotFound:
          'No rooms found for this branch. Check the branch code or create rooms first.',
        suggestAllergens: 'Suggest me a complete allergen list',
        validation: {
          fillRequired:
            'When Mandatory or Non-mandatory is checked, fill in both the label and options fields.',
          labelRequired: 'Label is required when this option is enabled.',
          optionsRequired:
            'Add at least one option separated by | when this option is enabled.',
        },
      },
      services: {
        sectionTitle: 'Additional products and services section',
        sectionDescription:
          'Create a list of products and services to associate with the booking form.',
        mandatoryLabel:
          'I want the choice of products or services to be mandatory',
        title: 'Additional products and services',
        description: 'Upsell extras during the booking flow.',
        add: 'Add product or service',
        name: 'Name',
        descriptionCol: 'Description',
        price: 'Price',
        pax: 'Pax',
        empty: 'No products or services linked to this form yet.',
        pickerTitle: 'List of services/products',
        pickerDesc: 'Select one or more items to associate with this form.',
        pickerEmpty: 'No services or products available for this branch.',
        deleteTitle: 'Remove service/product?',
        deleteDesc:
          'This will remove the selected item from this booking form.',
        toast: {
          saveSuccess: 'Services and products updated.',
          saveError: 'Failed to update services and products.',
          deleteSuccess: 'Service/product removed.',
          deleteError: 'Failed to remove service/product.',
          loadError: 'Failed to load services and products.',
        },
      },
      formConfiguration: 'Form configuration',
      formConfigurationDesc: 'General form configurations',
      formImage: 'Form image',
      formImageHint:
        'We recommend a PNG logo with a transparent background. Double click on the image for immediate redirect.',
      formBackgroundImage: 'Form background image',
      imageUpload: 'Image upload',
      removeImage: 'Remove image',
      redirectAfterCompilation: 'Redirect after compilation',
      linkForm: 'Link form',
      buttonText: 'Button text',
      dataFields: 'Data fields',
      requestEmail: 'Request email',
      requestAddress: 'Request address',
      requestNationality: 'Request nationality',
      requestZipCode: 'Request ZIP code',
      requestDateOfBirth: 'Request date of birth',
      tagsApplied: 'Tags applied to the build',
      tagsPlaceholder: 'Select tags',
      tagsHint: 'Comma-separated tag names',
      validation: {
        buttonTextRequired: 'Button text is required',
      },
    },
  },
  customers: {
    title: 'My Customers',
    subtitle: 'Customers for {{branch}}',
    subtitleDefault: 'View and manage your customer list',
    statsCount: '{{count}} customers',
    searchPlaceholder: 'Search name, email or phone',
    customer: 'Customer',
    surname: 'Surname',
    name: 'Name',
    email: 'Email',
    phone: 'Phone',
    contacts: 'Contacts',
    dateOfBirth: 'Date of Birth',
    reservations: 'Reservations',
    noShow: 'No Show',
    tags: 'Tags',
    visits: 'Visits',
    presenceCount: '{{count}} presence',
    noShowCount: '{{count}} No show',
    registrationDate: 'Registered',
    itemsPerPage: 'Items per page',
    range: '{{start}} - {{end}} of {{total}}',
    noBranch: 'Select a branch to view customers',
    empty: 'No customers found',
    emptyDesc: 'There are no customers for this branch yet.',
    downloadPdf: 'Download pdf',
    downloadPdfSuccess: 'Booking history PDF downloaded.',
    downloadPdfError: 'Failed to download booking history PDF.',
    downloadPdfEmpty: 'No bookings found for this customer.',
    downloadPdfMissingPhone: 'This customer has no phone number.',
    importExport: {
      menuLabel: 'Import/export',
      import: 'Import Customer List',
      export: 'Export Customer List',
      downloadSample: 'Download Sample List',
      downloadSampleSuccess: 'Sample file downloaded.',
      downloadSampleError: 'Failed to download sample file.',
      exportSuccess: 'Customer list exported.',
      exportError: 'Failed to export customer list.',
      dialogTitle: 'Import Customer List',
      dialogDescription: 'Select a CSV or XLSX file to import customers.',
      chooseFile: 'Choose file',
      selectedFile: 'Selected file',
      upload: 'Upload',
      uploading: 'Uploading customer list...',
      cancel: 'Cancel',
      close: 'Close',
      invalidType: 'Please select a CSV or XLSX file.',
      importError: 'Failed to import customer list. Please try again.',
      importAnother: 'Import another file',
      totalRecords: 'Total records',
      successfulImports: 'Successful imports',
      failedImports: 'Failed imports',
    },
    create: {
      new: 'New customer',
      title: 'Create Customer',
      action: 'Customer',
      tooltip: 'Add Customer',
      submit: 'Create Customer',
      cancel: 'Cancel',
      firstName: 'First name',
      firstNamePlaceholder: 'Enter first name',
      lastName: 'Last name',
      lastNamePlaceholder: 'Enter last name',
      mobile: 'Mobile number',
      mobilePlaceholder: 'Enter mobile number',
      emailPlaceholder: 'Enter email address',
      nickname: 'Customer nickname',
      nicknamePlaceholder: 'Enter nickname',
      tagsTitle: 'Customer tags',
      tagsSubtitle: 'Select the tags associated with the customer.',
      tagsPlaceholder: 'Select tags',
      success: 'Customer created successfully.',
      error: 'Failed to create customer. Please try again.',
      loading: 'Creating customer...',
    },
    bookingHistoryPdf: {
      title: 'Booking history',
      generatedMeta: 'Generated on {{date}} · {{count}} bookings',
      customerCode: 'Customer code: {{code}}',
      phone: 'Phone',
      email: 'Email',
      birthDate: 'Date of birth',
      address: 'Address',
      registration: 'Registration',
      tags: 'Tags',
      notAvailable: 'Not available',
      guestsWithChildren: '{{guests}} + {{children}} children',
      columns: {
        date: 'Date',
        time: 'Time',
        code: 'Code',
        status: 'Status',
        guests: 'Guests',
        source: 'Source',
      },
      sources: {
        WEB: 'Web',
        APP_CUSTOMER: 'Portal',
        APP: 'App',
      },
    },
    filters: {
      title: 'Filters',
      tags: 'Tags',
      birthYears: 'Birth years',
      birthMonth: 'Birth month',
      clearAll: 'Clear all filters',
      noTags: 'No tags available',
      onlyWithEmail: 'email configured',
      marketingConsent: 'marketing consent',
      blacklist: 'blacklist customers',
      phonePrefix: 'Country code',
      allPhonePrefixes: 'All country codes',
      searchOptions: 'Search...',
      noSearchResults: 'No matching options',
      minimumBookings: 'Minimum booking',
      selectMinimumBookings: 'Select minimum booking',
      form: 'Form',
      selectForm: 'All forms',
      noForms: 'No forms available',
      bookingPeriodTitle: 'Reservations in the period',
      bookingRangeLabel: 'Booking range',
      bookingRangePlaceholder: 'Select booking range',
      removePeriod: 'Remove period',
      minimumBookingsOptions: {
        '1': 'At least 1 booking',
        '2': 'At least 2 bookings',
        '3': 'At least 3 bookings',
        '4': 'At least 4 bookings',
        '5': 'At least 5 bookings',
        '10': 'At least 10 bookings',
        '15': 'At least 15 bookings',
        '20': 'At least 20 bookings',
        '30': 'At least 30 bookings',
        '50': 'At least 50 bookings',
      },
      months: {
        '1': 'January',
        '2': 'February',
        '3': 'March',
        '4': 'April',
        '5': 'May',
        '6': 'June',
        '7': 'July',
        '8': 'August',
        '9': 'September',
        '10': 'October',
        '11': 'November',
        '12': 'December',
      },
    },
  },
  customerDetails: {
    title: 'Customer Details',
    subtitle: 'Customers Details for {{branch}}',
    subtitleDefault: 'View and manage your customer details',
    deleteModelTitle: 'Delete customer?',
    deleteModelDescription:
      'Deleting the customer will also remove the bookings associated with them.',
    deleteModelConfirmText: 'Yes, delete',
    deleteModelCancelText: 'Cancel',
    card: {
      title: 'Customer Card',
      close: 'Close',
      delete: 'Eliminate',
      update: 'Update Customer',
      name: 'Name',
      surname: 'Surname',
      nickname: 'Nickname',
      mobile: 'Mobile',
      address: 'Address',
      city: 'City',
      province: 'Province',
      postalCode: 'Postal code',
      country: 'Country',
      personalDataTitle: 'Personal data',
      personalDataSubtitle: 'First name, last name and main contacts.',
      phoneTitle: 'Phone',
      phoneSubtitle: 'Country code and mobile number.',
      addressTitle: 'Address',
      addressSubtitle: 'Geographic data and customer contact details.',
      birthDateTitle: 'Date of birth',
      birthDateSubtitle: 'Storage format: yyyy-MM-dd.',
      tagsTitle: 'Customer Tag',
      tagsSubtitle: 'Select the tags associated with the customer.',
      tagsPlaceholder: 'Select tags',
      updateSuccess: 'Customer updated successfully.',
      updateError: 'Failed to update customer. Please try again.',
      deleteSuccess: 'Customer deleted successfully.',
      deleteError: 'Failed to delete customer. Please try again.',
    },
  },
  dcfQuestions: {
    title: 'Questions and answer tags',
    description:
      'Collect customer details and answers in one form. Tags can be assigned to answered questions and selected options.',
    backendRequired:
      'Questions require an update to the connected API. You can configure them here, but saving questions is disabled until the service supports them.',
    saveMismatch:
      'The API did not return the saved questions. Your edits are still here; ask the API team to enable question persistence.',
    unavailable: 'This form cannot accept answers yet. Please try again later.',
  },
  dataCollectionForms: {
    title: 'Data Collection Form',
    subtitle: 'Forms present',
    addDataCollectionForm: 'Add Form',
    searchPlaceholder: 'Search for data collection forms by name',
    noDataTitle: 'No form present',
    noDataDesc:
      'Create your first data collection form to start collecting leads and interactions.',
    form: 'Form',
    tags: 'Tags',
    tagsMore: '+{{count}} more',
    tagsLess: 'Less',
    status: 'Status',
    interactions: 'Interactions',
    interactionsCount: '{{count}} interactions',
    url: 'URL',
    qrCode: 'QR Code',
    preview: 'Preview',
    copyUrl: 'Copy URL',
    downloadQr: 'Download QR',
    editDataCollectionForm: 'Edit Data Collection Form',
    statusActive: 'Active',
    statusInactive: 'Inactive',
    statusSuspended: 'Suspended',
    statusDraft: 'Draft',
    statsCount: '{{count}} Data Collection Forms',
    updatedAgo: 'Updated 2 mins ago',
    deleteDataCollectionForm: 'Delete Data Collection Form',
    duplicate: 'Duplicate',
    createTitle: 'Create Data Collection Form',
    formName: 'Form Name',
    formNamePlaceholder: 'Enter form name',
    createForm: 'Create form',
    deleteModel: {
      deleteTitle: 'Delete Data Collection Form?',
      deleteDescription:
        'Are you sure you want to delete this data collection form? This action cannot be undone.',
    },
    validation: {
      formNameMin: 'Form name must be at least 2 characters',
      formNameMax: 'Form name cannot exceed 255 characters',
    },
    scanQrAccess: 'Scan QR code to access:',
    qrClose: 'Close',
    qrDownload: 'Download',
    toast: {
      createSuccess: 'Form created successfully.',
      createError: 'Failed to create the form. Please try again.',
      updateSuccess: 'Form updated successfully.',
      updateError: 'Failed to update the form. Please try again.',
      logoUploadSuccess: 'Logo uploaded successfully.',
      logoUploadError: 'Failed to upload logo.',
      logoRemoveSuccess: 'Logo removed successfully.',
      logoRemoveError: 'Failed to remove logo.',
      bgUploadSuccess: 'Background uploaded successfully.',
      bgUploadError: 'Failed to upload background.',
      bgRemoveSuccess: 'Background removed successfully.',
      bgRemoveError: 'Failed to remove background.',
      statusUpdateSuccess: 'Status updated successfully.',
      statusUpdateError: 'Failed to update status. Please try again.',
      deleteSuccess: 'Form deleted successfully.',
      deleteError: 'Failed to delete the form. Please try again.',
      duplicateSuccess: 'Form duplicated successfully.',
      duplicateError: 'Failed to duplicate the form. Please try again.',
    },
    edit: {
      pageTitle: 'Personal data consent',
      subtitle: 'Configure the data collection form',
      close: 'Close',
      formConfiguration: 'Form configuration',
      formConfigurationDesc: 'General data collection form configurations',
      formImage: 'Form image',
      formImageHint:
        'We recommend a PNG logo with a transparent background. Double click on the image for immediate redirect.',
      formBackgroundImage: 'Form background image',
      imageUpload: 'Image upload',
      removeImage: 'Remove image',
      redirectAfterCompilation: 'Redirect after compilation',
      linkForm: 'Link form',
      buttonText: 'Button text',
      description: 'Description',
      descriptionPlaceholder: 'Text shown on the left side of the public form',
      dataFields: 'Data fields',
      requestEmail: 'Request email',
      requestAddress: 'Request address leave',
      requestNationality: 'Request nationality',
      requestZipCode: 'Request ZIP code',
      requestDateOfBirth: 'Request date of birth',
      tagsApplied: 'Tags applied to the build',
      tagsOnDownload: 'Tags applied on download',
      tagsOnUse: 'Tags assigned after saving data',
      tagsPlaceholder: 'Select tags',
      tagsHint: 'Comma-separated tag names',
      uploading: 'Uploading...',
      removing: 'Removing...',
      validation: {
        redirectUrlRequired: 'Redirect URL is required.',
        redirectUrlInvalid: 'Enter a valid URL (e.g. https://example.com).',
      },
    },
  },
  publicDataCollectionForm: {
    documentTitle: 'Data collection form',
    loading: 'Loading form',
    missingFormCode: 'Form code is missing from the URL.',
    loadError: 'Unable to load this data collection form.',
    unavailableTitle: 'Form not available',
    unavailableMessage:
      'This data collection form is currently suspended or unavailable.',
    personalInfo: 'Personal information',
    prefixLabel: 'Prefix',
    phoneLabel: 'Mobile',
    phonePlaceholder: 'Your number',
    firstName: 'Name',
    firstNamePlaceholder: 'Your name',
    lastName: 'Surname',
    lastNamePlaceholder: 'Your surname',
    email: 'Email',
    emailPlaceholder: 'name@example.com',
    optional: 'Optional',
    cap: 'Postal code',
    capPlaceholder: 'e.g. 09124',
    birthDate: 'Date of birth',
    address: 'Address',
    addressPlaceholder: 'Street and number',
    nationality: 'Nationality',
    nationalityPlaceholder: 'Your nationality',
    requiredNote: '* Required fields',
    consentsTitle: 'Your preferences',
    privacyConsent: 'Privacy and data treatment',
    marketingConsent: 'Marketing and communication consensus',
    profilingConsent: 'Profiling consent',
    detailsLink: 'Privacy notice',
    selectAll: 'Select all',
    submit: 'Access the page',
    welcomeBackTitle: 'Welcome back, {{name}}!',
    welcomeBackTitleGeneric: 'Welcome back!',
    welcomeBackMessage:
      'We already have your data. You can add another contact if this is for someone else.',
    addAnotherContact: 'Add another contact',
    toast: {
      success: 'Data submitted successfully.',
      error: 'Failed to submit data. Please try again.',
    },
    thankYou: {
      title: 'Thank you!',
      message: 'Your data has been submitted successfully.',
      continue: 'Continue',
    },
    validation: {
      prefixRequired: 'Select a country code.',
      phoneMin: 'Enter a valid mobile number.',
      firstNameRequired: 'First name is required.',
      lastNameRequired: 'Last name is required.',
      emailRequired: 'Email is required.',
      emailInvalid: 'Enter a valid email address.',
      birthDateInvalid: 'Enter a valid date that is not in the future.',
      privacyRequired: 'Privacy consent is required.',
    },
  },
  bulkMessage: {
    title: 'Bulk Message',
    subtitle: 'Manage WhatsApp, Email and SMS campaigns for {{branch}}',
    subtitleDefault: 'View and manage your bulk message campaigns',
    statsCount: '{{count}} campaigns',
    searchPlaceholder: 'Search for bulk mailing...',
    campaignName: 'Campaign Name',
    campaignNamePlaceholder: 'Enter campaign name',
    addCampaign: 'New campaign',
    creating: 'Creating...',
    createTitle: 'Create new message sending campaign',
    createDesc: 'Add a new bulk message campaign for your branch.',
    close: 'Close',
    duplicate: 'Duplicate',
    editCampaign: 'Edit campaign',
    viewDetails: 'View details',
    pause: 'Pause campaign',
    resume: 'Resume campaign',
    delete: 'Delete campaign',
    noBranch: 'Select a branch to view bulk messages',
    empty: 'No campaigns found',
    emptyDesc: 'There are no bulk message campaigns for this branch yet.',
    channels: {
      whatsappWithImage: 'WHATSAPP WITH IMAGE',
    },
    columns: {
      name: 'Name',
      type: 'Type',
      state: 'State',
      progress: 'Progress',
      selectedCustomers: 'Selected customers',
      status: 'Status',
      channel: 'Channel',
      messageType: 'Message type',
      customer: 'Customer',
      reference: 'Reference',
      attempts: 'Attempts',
      createdAt: 'Created at',
    },
    statusFilter: {
      all: 'All statuses',
      pending: 'Pending',
      sent: 'Sent',
      delivered: 'Delivered',
      failed: 'Failed',
    },
    noDataTitle: 'No messages found',
    noDataDesc: 'There are no message delivery events for this branch yet.',
    deleteModal: {
      title: 'Delete campaign?',
      description:
        'Are you sure you want to delete this campaign? This action cannot be undone.',
    },
    details: {
      pageTitle: 'Campaign {{name}}',
      subtitle: 'Bulk campaign delivery progress',
      empty: 'No delivery events found',
      emptyDesc: 'There are no message delivery events for this campaign yet.',
      loadError: 'Could not load delivery events. Please try again.',
      emailLabel: 'Email:',
      phoneLabel: 'Phone:',
      chatId: 'Chat {{id}}',
      httpStatus: 'HTTP {{status}}',
      resubmit: 'Resend message',
      resubmitSuccess: 'Event re-queued successfully.',
      resubmitError: 'Could not re-queue the message. Please try again.',
      resubmitConfirmTitle: 'Previous attempt succeeded',
      resubmitConfirmDescription:
        'Are you sure you want to send this message again?',
      resubmitConfirmAction: 'Yes, send again',
      columns: {
        recipient: 'Recipient',
        message: 'Message',
        channel: 'Channel',
        status: 'Outcome',
        details: 'Details',
      },
    },
    validation: {
      nameMin: 'Campaign name must be at least 2 characters',
      nameMax: 'Campaign name cannot exceed 120 characters',
    },
    toast: {
      createSuccess: 'Campaign created successfully.',
      createError: 'Failed to create campaign. Please try again.',
      updateSuccess: 'Campaign saved successfully.',
      updateError: 'Failed to save campaign. Please try again.',
      deleteSuccess: 'Campaign deleted successfully.',
      deleteError: 'Failed to delete campaign. Please try again.',
      duplicateSuccess: 'Campaign duplicated successfully.',
      duplicateError: 'Failed to duplicate campaign. Please try again.',
      imageRemoveSuccess: 'Attached image removed successfully.',
      imageRemoveError: 'Failed to remove attached image.',
      imageUploadSuccess: 'Image uploaded successfully.',
      imageUploadError: 'Failed to upload image. Please try again.',
      imageTypeError: 'Only JPG and PNG images are allowed.',
      imageSizeError: 'Image must be 5 MB or smaller.',
      startSuccess:
        'Bulk message sending has started. Monitor the list for progress.',
      startError: 'Failed to start the campaign. Please try again.',
      pauseSuccess: 'Campaign paused successfully.',
      pauseError:
        'Failed to pause the campaign. Please try again in a couple of minutes.',
      resumeSuccess: 'Campaign resumed successfully.',
      resumeError:
        'Failed to resume the campaign. Please try again in a couple of minutes.',
    },
    edit: {
      pageTitle: 'Edit {{name}}',
      subtitle: 'Configure campaign details and message content',
      startCampaign: 'Start Campaign',
      start: {
        confirmTitle: 'Shall we proceed?',
        confirmDescription:
          'You are about to send a bulk message of type {{channel}} to {{count}} customers.',
        confirmAction: 'Yes, proceed',
        noRecipients:
          'There are no recipients. Add recipients to start the campaign.',
        emptyMessage: 'Enter a message to start the campaign.',
        emptySubject: 'Enter a subject to start the email campaign.',
        missingImage: 'Add an image to start the WhatsApp with image campaign.',
        missingTemplate:
          'Select a template to start the WhatsApp Business campaign.',
        corruptTemplateVariables:
          'Template variables are empty or unavailable. The template appears corrupt. Choose another template.',
        corruptTemplateLanguages:
          'This template has no associated languages and appears corrupt. Choose another template.',
      },
      saveConfiguration: 'Save configuration',
      configTitle: 'Bulk message configuration',
      configDesc:
        'Set up WhatsApp and email campaigns to send to selected customers.',
      configuration: 'Configuration',
      configurationDesc:
        'Set the campaign name and the mass message sending channel.',
      audience: 'Audience',
      selectedCustomers: 'Selected customers',
      customersReady: '{{count}} customers ready for bulk shipping',
      selectCustomers: 'Select customers',
      selectCustomersSoon: 'Customer selection will be available soon.',
      channel: 'Channel',
      whatsappTitle: 'WhatsApp Message',
      whatsappDesc: 'Configure the content of the WhatsApp message.',
      whatsappPlaceholder: 'Write your message here...',
      whatsappBusinessTitle: 'WhatsApp Business Template',
      whatsappBusinessDesc:
        'Select from Meta-approved templates for sending via WhatsApp Business API.',
      whatsappBusinessEmptyDesc:
        'Select a Meta-approved template to set up sending via WhatsApp Business API.',
      selectedTemplate: 'Selected template',
      noTemplateSelected: 'No template selected',
      selectTemplate: 'Select Template',
      selectTemplateTooltip: 'Select Template from those approved by meta',
      selectTemplateSoon: 'Template selection will be available soon.',
      templateModal: {
        title: 'Select a template',
        description:
          'Choose one of the templates approved on WhatsApp Business API.',
        messageTypeBadge: 'For {{messageType}}',
        countBadge: '{{count}} templates',
        countBadgeFiltered: '{{count}} of {{total}}',
        searchPlaceholder: 'Search template by name...',
        empty: 'No templates available',
        selectAction: 'Select template',
        recommended:
          'Recommended template based on the selected message type ({{messageType}})',
        bodyPreview: 'Body Preview',
        noPreview: 'No body preview available.',
        noCta: 'No CTA',
        ctaEnabled: 'CTA Enabled',
        corruptHint:
          'This template appears corrupt and cannot be used. It was probably modified manually in Facebook Business Manager and no longer matches the structure required by WhatsApp Business API.',
        loadError: 'Could not load templates',
        loadErrorDesc:
          'Failed to fetch WhatsApp Business templates. Please try again.',
        selectSuccess: 'Template selected successfully.',
        selectError: 'Failed to select template. Please try again.',
      },
      whatsappWithImageTitle: 'WhatsApp with image',
      whatsappWithImageDesc:
        'Configure message, image attachment, and dynamic placeholders.',
      whatsappMessageText: 'WhatsApp Message Text',
      emailTitle: 'Bulk email',
      emailDesc: 'Configure email, attachments, and HTML content.',
      emailSubject: 'Email subject',
      emailSubjectPlaceholder: 'Ex: Special promo for you 🎉',
      emailBody: 'Email content',
      emailBodyHint: 'If you insert a link, leave a space',
      applyTemplate: 'Apply template',
      sendTest: 'Send test message',
      attachedImage: 'Attached image',
      uploadImage: 'Upload image',
      removeImage: 'Remove image',
      uploading: 'Uploading...',
      removing: 'Removing...',
      test: {
        title: 'Test sending',
        emailTitle: 'Test Send',
        oopsTitle: 'Oops...',
        ok: 'OK',
        send: 'Send',
        testSending: 'Test sending',
        backwards: 'Backwards',
        emptyMessage: 'Enter a message to try sending.',
        emptySubject: 'Enter an email subject to try sending.',
        missingImage:
          'No image is associated with this campaign. Add one to test send with image.',
        mobilePlaceholder: 'Mobile phone',
        emptyPhone: 'Enter a mobile phone number to try sending.',
        emptyEmail: 'Enter an email address to try sending.',
        emailLabel: 'Email:',
        emailPlaceholder:
          'Enter the email address where you want to receive the test',
        apiSoon: 'Test send API will be available soon.',
        sendSuccess: 'Test message sent successfully.',
        sendError: 'Could not send the test message. Please try again.',
      },
    },
    selectCustomers: {
      pageTitle: 'Assign customers to {{name}}',
      subtitle: 'Filter and select customers for this campaign',
      subtitleWithBranch: 'Filter and select customers for {{branch}}',
      searchPlaceholder: 'Search customer...',
      selected: 'Selected',
      clearSelection: 'Clear selection',
      removeAll: 'Remove all',
      saveList: 'Save customer list',
      bookingPeriodTitle: 'Reservations in the period',
      bookingRangeLabel: 'Booking range',
      bookingRangePlaceholder: 'Select booking range',
      removePeriod: 'Remove period',
      marketingConsentShort: 'Marketing consent',
      listTitle: 'Customer list',
      listDesc: 'Manage customer selection',
      selectPage: 'Select page',
      deselectPage: 'Deselect page',
      selectAll: 'Select all',
      deselectAll: 'Deselect all',
      progressAdding: 'Adding {{current}} of {{total}}',
      selectRow: 'Select customer',
      results: 'Results',
      page: 'Page',
      noEmail: 'No email',
      columns: {
        customer: 'Customer',
        phone: 'Phone',
        birth: 'Birth',
        bookings: 'Bookings',
        tags: 'Tags',
      },
      toast: {
        saveSuccess: 'Customer list saved successfully.',
        saveError: 'Failed to save customer list. Please try again.',
        updateError: 'Failed to update customer selection. Please try again.',
        selectAllSuccess: 'Selected {{count}} customers.',
        selectAllPartial:
          'Added {{added}} customers, then the request failed. Please try Select all again.',
        clearSuccess: 'All customers removed from selection.',
        clearError: 'Failed to clear customer selection. Please try again.',
      },
    },
  },
  fidelity: {
    showPreview: 'Show preview',
    title: 'Fidelity',
    subtitleDefault: 'View and manage your fidelities',
    statsCount: '{{count}} fidelities',
    searchPlaceholder: 'Search for a fidelity by name...',
    addProgram: 'New fidelity',
    creating: 'Creating...',
    saving: 'Saving...',
    createTitle: 'Create fidelity',
    createDesc: 'Add a new loyalty fidelity for your branch.',
    createActiveConflict:
      'An active {{type}} fidelity "{{name}}" already exists for this branch.',
    createActiveConflictAction:
      'Deactivate the existing fidelity first, or keep this one inactive.',
    createActiveBlocked:
      'Turn off Active fidelity to continue, or deactivate "{{name}}" from the list.',
    edit: {
      pageTitle: 'Modify fidelity',
    },
    programName: 'Fidelity name',
    programNamePlaceholder: 'Enter fidelity name',
    description: 'Description',
    descriptionPlaceholder: 'Enter fidelity description',
    policyMessageCard: 'Policy / Message Card',
    tagsOnDownload: 'Tags applied on download',
    tagsOnUse: 'Tags applied on use',
    tagsPlaceholder: 'Select tags',
    type: 'Type',
    typePoints: 'Points',
    typeDiscount: 'Discount',
    typeDiscountPrepaid: 'Discount / prepaid',
    customerCards: 'Customer cards',
    noThresholds: 'No point thresholds configured.',
    codeLinePrepaid:
      'Prepaid credit program with a discount applied to the card.',
    color: 'Color',
    value: 'Value',
    active: 'Active',
    inactive: 'Inactive',
    activeProgram: 'Active fidelity',
    discountApplied: 'Discount applied (%)',
    codeLineRange: 'From {{from}} to {{to}} euros',
    codeLineDiscount: '{{value}}% discount',
    thresholdCount: '{{count}} thresholds',
    customersCount: '{{count}} customers',
    thresholdsTitle: 'Point thresholds',
    thresholdsHint: 'The backend reorders the thresholds and closes any gaps.',
    addThreshold: 'Threshold',
    updateThreshold: 'Update threshold',
    thresholdFrom: 'From euros*',
    thresholdTo: 'In euros*',
    thresholdPointsLabel: 'Points*',
    thresholdLabel: 'Label',
    thresholdColor: 'Threshold color',
    editProgram: 'Edit fidelity',
    deleteProgram: 'Delete fidelity',
    duplicate: 'Duplicate',
    editThreshold: 'Edit threshold',
    deleteThreshold: 'Delete threshold',
    noBranch: 'Select a branch to view fidelities',
    empty: 'No fidelity configured',
    emptyDesc:
      'Create a points or discount fidelity to start generating customer cards.',
    cardsTitle: 'Customer cards',
    cardsSubtitle: 'Issued cards, QR codes and balances',
    cardsEmpty: 'No fidelity cards found for this fidelity.',
    searchCustomerPlaceholder: 'About the customer, the card...',
    createCard: 'Create card',
    refreshCards: 'Refresh cards',
    update: 'Update',
    scanQrCode: 'Scan QR code',
    useCard: 'Use card',
    toggleCardStatus: 'Change used/available status',
    operationHistory: 'Operation history',
    qrCodeTitle: 'Scan QR code',
    downloadQr: 'Download',
    historyTitle: 'Fidelity card history',
    historyEmpty: 'No operations available for this fidelity card.',
    postedBy: 'Posted by',
    branchCode: 'Branch',
    fidelityCode: 'Fidelity',
    customerId: 'Customer ID',
    pointsSuffix: 'points',
    amountSuffix: 'euros',
    cardAvailable: 'available',
    cardUsed: 'used',
    columns: {
      qr: 'QR',
      customer: 'Customer',
      contacts: 'Contacts',
      card: 'Card',
      balance: 'Balance',
      state: 'State',
      activation: 'Activation',
      threshold: 'Threshold',
      label: 'Label',
      points: 'Points',
      color: 'Color',
    },
    createCardPage: {
      title: 'Choose customer',
      subtitle: 'Select a customer to issue a new fidelity card.',
      subtitleWithProgram: 'Customers for {{branch}} · {{program}}',
      newCard: 'New card',
      chooseCustomer: 'Choose customer',
      searchPlaceholder: 'Search by name, phone number, or email...',
      results: 'Results',
      page: 'Page',
      availableCustomers: 'Available customers',
      availableCustomersDesc:
        'Select a customer to open the card creation confirmation.',
      noEmail: 'No email',
      columns: {
        customer: 'Customer',
        telephone: 'Telephone',
        wood: 'Visits',
        tags: 'Tags',
      },
    },
    createCardConfirm: {
      title: 'Do you want to create card for this customer?',
      confirm: 'Yes, create cards',
      creating: 'Creating...',
    },
    statCards: {
      programs: 'Fidelity',
      active: 'Active',
      types: 'Types',
    },
    deleteModal: {
      title: 'Delete fidelity?',
      description:
        'Are you sure you want to delete this fidelity? This action cannot be undone.',
    },
    deleteThresholdModal: {
      title: 'Delete threshold?',
      description:
        'Are you sure you want to delete this threshold? This action cannot be undone.',
    },
    thresholdModal: {
      createTitle: 'Add point threshold',
      editTitle: 'Change point threshold',
      description: 'Configure the range, points, label and threshold color.',
      lowerLimit: 'Lower limit*',
      upperLimit: 'Upper limit*',
      pointsApplied: 'Points applied*',
    },
    toast: {
      createSuccess: 'Fidelity created successfully.',
      createError: 'Failed to create fidelity. Please try again.',
      updateSuccess: 'Fidelity updated successfully.',
      updateError: 'Failed to update fidelity. Please try again.',
      deleteSuccess: 'Fidelity deleted successfully.',
      deleteError: 'Failed to delete fidelity. Please try again.',
      duplicateSuccess: 'Fidelity duplicated successfully.',
      duplicateError: 'Failed to duplicate fidelity. Please try again.',
      thresholdCreateSuccess: 'Threshold created successfully.',
      thresholdCreateError: 'Failed to create threshold.',
      thresholdUpdateSuccess: 'Threshold updated successfully.',
      thresholdUpdateError: 'Failed to update threshold.',
      thresholdDeleteSuccess: 'Threshold deleted successfully.',
      thresholdDeleteError: 'Failed to delete threshold.',
      cardCreateSuccess: 'Fidelity card created successfully.',
      cardCreateError: 'Failed to create fidelity card.',
      cardUseSuccess: 'Card marked as used.',
      cardUseError: 'Failed to update card usage.',
      cardToggleSuccess: 'Card status updated successfully.',
      cardToggleError: 'Failed to update card status.',
    },
    operations: {
      addPoints: 'Added points',
      updatePoints: 'Updated points',
      removePoints: 'Removed points',
      addBillAmount: 'Added bill amount',
      addPrepaidAmount: 'Added prepaid amount',
      updatePrepaidAmount: 'Update prepaid amount',
      disableAccount: 'Disabled account',
      createAccount: 'Created account',
      used: 'Used card',
    },
    registration: {
      title: 'Customer self-registration',
      description:
        'Share this link so customers can create their own fidelity card.',
      linkLabel: 'Fidelity registration link',
      openForm: 'Open form',
      copy: 'Copy link',
      qr: 'QR code',
    },
    views: {
      label: 'Fidelity program',
      configuration: 'Configuration',
      configurationHint: 'Rules, benefits and messages',
      cards: 'Customer cards',
      cardsHint: 'Issued cards, QR and activity',
      allPrograms: 'All fidelity programs',
    },
    wizard: {
      program: 'Program and benefits',
      messages: 'Messages',
      review: 'Review',
      back: 'Back',
      continue: 'Continue',
      save: 'Save configuration',
      stepOf: '{{current}} / {{total}}',
      stepListLabel: 'Fidelity setup steps',
      pointsIntro:
        'Define the program, the rules for earning points, and the welcome message.',
      discountIntro:
        'Define the percentage, the card appearance, and the welcome message.',
      discountNote:
        'This program uses a discount percentage and does not accumulate points. Prepaid balance is managed on the customer card.',
      benefitPoints: 'Points accumulation',
      benefitDiscount: '{{value}}% discount',
      messageLabel: 'Welcome message',
      messagePlaceholder: 'Choose a fidelity message',
      messageStatus: '{{status}} · WhatsApp {{whatsapp}} · Email {{email}}',
      messageActive: 'Active',
      messageInactive: 'Inactive',
      channelOn: 'on',
      channelOff: 'off',
      messageWarning:
        'Enable WhatsApp or email for this message before saving.',
      messageLoadError: 'Unable to load messages.',
      configureMessages: 'Configure messages',
      rulesLabel: 'Program rules',
      reviewTitle: 'Program summary',
      name: 'Name',
      status: 'Status',
      benefit: 'Benefit',
      message: 'Message',
      autoUpdates: 'Automatic updates',
      autoUpdatesValue:
        'A dedicated message for each operation, configured in Messages.',
      createProgram: 'The program',
      createBenefits: 'Benefits',
      createPoints: 'Spend bands',
      createRewards: 'Redeemable rewards',
      createHistorical: 'Lifetime levels',
      createProgramDesc: 'Choose the name and type of the program.',
      createPointsDesc:
        'Define one earning rule. The same points will feed both the spendable and lifetime balances.',
      createRewardsDesc:
        'Configure thresholds and redeemable rewards. Redemption deducts the required points from the available balance.',
      createHistoricalDesc:
        'Configure each Fidelity level’s points, name, colour and discount, including a starting level at 0 points. The Fidelity QR identifies the discount; lifetime points are never deducted.',
      createBenefitsDesc:
        'Set the spend bands for points, or the discount percentage.',
      createMessagesDesc:
        'Set the WhatsApp and email welcome message with the card QR.',
      createReviewDesc:
        'Check the benefits, status, and message before creating the program.',
      createAction: 'Create program',
      afterSaveNote: 'You can create customer cards after saving the program.',
      thresholdStepHint:
        'For each spend band, set how many points the customer receives.',
      thresholdReviewLine: 'From {{from}} to {{to}} €: {{points}} points',
    },
    validation: {
      nameMin: 'Fidelity name must be at least 2 characters',
      nameMax: 'Fidelity name cannot exceed 100 characters',
      colorInvalid: 'Enter a valid hex color (e.g. #FFFFFF)',
      thresholdRequired: 'Add at least one threshold.',
      thresholdRange:
        'Upper limit must be greater than or equal to lower limit.',
      thresholdLower: 'Enter a valid lower limit.',
      thresholdUpper: 'Enter a valid upper limit.',
      thresholdPoints: 'Enter valid points.',
      thresholdLabelRequired: 'Threshold label is required.',
      thresholdLabelMax: 'Threshold label cannot exceed 100 characters.',
    },
  },
  marketingGuide: {
    infoButton: 'Marketing guide',
    title: 'Choose the right marketing tool',
    intro:
      'Promotions and Fidelity can both offer a benefit, but they serve different goals.',
    promotionTitle: 'Promotions · a campaign with a deadline',
    promotionBody:
      'Use a promotion to attract new customers, reactivate occasional customers, or support a quiet period. Customers join through a public link or QR code and receive a coupon; they do not need to earn points.\nExample: “Get 10% off until Sunday, with a €30 minimum spend, once per customer.”',
    fidelityTitle: 'Fidelity · a reason to come back',
    fidelityBody:
      'Use Fidelity to reward repeat visits. Spending earns points according to the configured rules; customers can redeem a reward when they reach a threshold. Their balance and operation history stay on their card.\nExample: “Earn points with each receipt and choose a reward when you reach a milestone.”',
    rulesTitle: 'Practical marketing rules',
    rulesBody:
      '1. Set one clear goal: acquire customers, reactivate them, increase repeat visits, or raise the average order.\n2. Protect your margin: choose a discount and minimum spend you can sustain, and set a quantity or budget limit.\n3. State the conditions clearly: dates, eligible products or channels, minimum spend, and whether benefits can be combined.\n4. Keep the offer easy to understand and redeem; too many conditions reduce participation.\n5. Measure redemptions and repeat spending, not just coupons issued or points collected.',
    togetherTitle: 'They can work together',
    togetherBody:
      'Use a time-limited promotion to bring someone in, then invite them to join Fidelity for an ongoing reason to return. Keep the coupon and Fidelity reward clearly identified so staff and customers know which benefit is being used.',
  },
  promotions: {
    title: 'Promotions',
    titleWithBranch: 'Promo {{branch}}',
    subtitle: 'Promotion List for {{branch}}',
    subtitleDefault: 'View and manage your promotion list',
    pageDescription:
      'Manage promotions, coupons and promo configurations of the venue',
    statsCount: '{{count}} Promotions',
    searchPlaceholder: 'Search for promos by name...',
    promoName: 'Promo Name',
    promoNamePlaceholder: 'Enter promotion name',
    description: 'Description',
    descriptionPlaceholder: 'Enter promotion description',
    used: 'Used',
    usedCouponCount: '{{count}} coupon',
    downloaded: 'Downloaded',
    messages: 'Messages',
    messagesTooltip: 'Messages sent through this promo',
    messageHistory: {
      title: 'Message history',
      reference: 'Reference {{code}}',
      empty: 'No messages available.',
      refresh: 'Refresh',
      loadError: 'Failed to load messages.',
      attempts: '{{count}} attempts',
    },
    customerCoupons: {
      title: 'Customer coupons',
      subtitle: 'Promo: {{name}} · {{count}} coupons issued',
      discount: '{{percent}}% discount',
      redeemed: 'Redeemed',
      notRedeemed: 'Not redeemed',
      generatedOn: 'Generated on {{date}}',
      redeemedOn: 'Redeemed on {{date}}',
      operator: 'Operator: {{name}}',
      empty: 'No coupons issued yet.',
      loadError: 'Failed to load customer coupons.',
      loading: 'Loading coupons...',
    },
    status: 'Status',
    active: 'Active',
    expired: 'Expired',
    url: 'URL',
    qrCode: 'QR Code',
    preview: 'Preview',
    copyUrl: 'Copy URL',
    duplicate: 'Duplicate',
    stop: 'Stop',
    restart: 'Restart',
    editPromotion: 'Edit promotion',
    delete: 'Delete promotion',
    addPromotion: 'Add promotion',
    creating: 'Creating...',
    createTitle: 'Create Promotion',
    createDesc: 'Add a new message campaign for your branch.',
    createWizard: {
      detailsDesc:
        'Set the name, available coupons, and discount. You can customize the customer form after creation.',
      messageDesc:
        'Choose one of the three messages configured in Settings → Messages.',
      reviewHint: 'Check the settings before creating the promotion.',
      stepCount: '{{current}} / {{total}}',
      back: 'Back',
      continue: 'Continue',
      createAndCustomize: 'Create and customize',
      couponHint: 'Use 0 if the promotion has no percentage discount.',
      automaticUpdatesLabel: 'Automatic updates',
      automaticUpdates:
        'A dedicated message for each action, configured in Messages.',
    },
    close: 'Close',
    save: 'Save',
    saveChanges: 'Save changes',
    buttonLabel: 'Button label',
    buttonLabelPlaceholder: 'Enter button label',
    redirectPage: 'Redirect page',
    redirectPagePlaceholder: 'Custom thank-you page URL',
    percentageDiscount: 'Discount (%)',
    maxCouponCount: 'Max coupons',
    maxUsageCount: 'Max usage',
    itemsPerPage: 'Items per page',
    range: '{{start}} - {{end}} of {{total}}',
    noBranch: 'Select a branch to view promotions',
    empty: 'No promos available',
    emptyDesc:
      'You have not yet created promotions for this place. Start by creating your first promo.',
    deleteModal: {
      title: 'Delete promotion?',
      description:
        'Are you sure you want to delete this promotion? This action cannot be undone.',
    },
    validation: {
      promotionNameMin: 'Promotion name must be at least 2 characters',
      promotionNameMax: 'Promotion name cannot exceed 120 characters',
      couponMin: 'Enter an integer greater than zero.',
      discountRange: 'Enter an integer from 0 to 100.',
      redirectPageInvalid: 'Enter a valid URL (e.g. https://example.com).',
    },
    toast: {
      createSuccess: 'Promotion created successfully.',
      createError: 'Failed to create the promotion. Please try again.',
      updateSuccess: 'Promotion updated successfully.',
      updateError: 'Failed to update the promotion. Please try again.',
      deleteSuccess: 'Promotion deleted successfully.',
      deleteError: 'Failed to delete the promotion. Please try again.',
      duplicateSuccess: 'Promotion duplicated successfully.',
      duplicateError: 'Failed to duplicate the promotion. Please try again.',
      stopSuccess: 'Promotion stopped successfully.',
      stopError: 'Failed to stop the promotion. Please try again.',
      restartSuccess: 'Promotion restarted successfully.',
      restartError: 'Failed to restart the promotion. Please try again.',
      logoUploadSuccess: 'Logo uploaded successfully.',
      logoUploadError: 'Failed to upload logo.',
      logoRemoveSuccess: 'Logo removed successfully.',
      logoRemoveError: 'Failed to remove logo.',
      posterUploadSuccess: 'Poster uploaded successfully.',
      posterUploadError: 'Failed to upload poster.',
      posterRemoveSuccess: 'Poster removed successfully.',
      posterRemoveError: 'Failed to remove poster.',
    },
    edit: {
      pageTitle: 'Edit promotion',
      subtitle: 'Configure form and promotion settings.',
      tabListLabel: 'Promotion edit sections',
      wizard: {
        stepListLabel: 'Promotion setup steps',
        form: 'Customer form',
        formDesc:
          'Customize the form the customer fills in to request the coupon.',
        rules: 'Rules',
        rulesDesc:
          'Set the period, availability, discount, and the details you ask from the customer.',
        message: 'Message',
        messageDesc:
          'Choose one of the three messages configured in Settings → Messages.',
        review: 'Review',
        reviewDesc:
          'Check the settings and save. Unsaved changes stay on this page.',
        editStep: 'Edit',
        period: 'Period',
        requestedFields: 'Requested details',
        noDate: 'No date',
        none: 'None',
      },
      uploadImage: 'Upload image',
      removeImage: 'Remove image',
      uploading: 'Uploading...',
      removing: 'Removing...',
      tabs: {
        formConfig: 'Form configuration',
        promoConfig: 'Promotion configuration',
      },
      formConfig: {
        media: 'Media',
        logo: 'Promo logo',
        details: 'Form details',
        promoText: 'Promo text',
        promoLink: 'Promo link',
      },
      promoConfig: {
        poster: 'Poster',
        posterLabel: 'Promo poster',
        options: 'Options',
        useOnlyOnce: 'Coupon usable only once',
        useOnlyOnceDesc: 'Limit coupon download to once per customer',
        showEmail: 'Request email',
        showEmailDesc: 'Show the email field in the promo form',
        showAddress: 'Request address',
        showAddressDesc: 'Show the address field in the promo form',
        showCap: 'Request zip code',
        showCapDesc: 'Show the zip code field in the promo form',
        showDob: 'Request date of birth',
        showDobDesc: 'Show the date of birth field in the promo form',
        showCountry: 'Request country',
        limits: 'Dates and limits',
        startDate: 'Start date',
        endDate: 'End date',
        tags: 'Tags',
        tagsOnDownload: 'Tags applied on download',
        tagsOnUse: 'Tags applied on use',
        tagsPlaceholder: 'Select tags',
        messaging: 'Messaging',
        messageLabel: 'Message for the customer',
        messagePlaceholder: 'Choose one of the three shared configurations.',
        messageStatus: '{{status}} · WhatsApp: {{whatsapp}} · Email: {{email}}',
        messageActive: 'Configuration active',
        messageInactive: 'Configuration disabled',
        channelOn: 'on',
        channelOff: 'off',
        messageWarning:
          'Enable at least one channel in Messages to send this communication.',
        configureMessages:
          'Configure texts, channels and templates in Messages',
        messageLoadError:
          'Unable to load the configurations. Try again from Messages.',
        sharedMessageNote:
          'Each action uses the dedicated message configured in Messages. PROMO_INFO is reserved for status updates.',
        whatsappMessage: 'WhatsApp coupon message',
        whatsappSubtitle: 'Message sent with the promo QR code',
        whatsappPlaceholder: 'Write your message here...',
        applyTemplate: 'Apply default template',
        sendMessage: 'Send message',
      },
      detailsTitle: 'Promotion details',
      detailsDesc: 'Update campaign information and limits.',
      branchHint: 'Branch: {{branch}}',
    },
  },
  supplierOrdersPage: {
    receivedModified: 'Received with changes',
    processedModified: 'Processed but modified',
    processed: 'Processed',
    title: 'Supplier orders',
    subtitle: 'Purchases, deliveries and product checks',
    history: 'Order history',
    create: 'Create order',
    search: 'Search order or product…',
    supplier: 'Supplier',
    allSuppliers: 'All suppliers',
    invalidDates: 'Select a valid date range.',
    onlyModified: 'Modified orders only',
    loadError: 'Unable to load orders.',
    ordersOn: 'Orders for {{date}} · {{count}}',
    empty: 'No orders found',
    order: 'Order',
    delivery: 'Delivery',
    status: 'Status',
    totalVat: 'Total incl. VAT',
    perPage: 'Per page',
    orderCount: '{{count}} orders',
    modified: 'Modified',
    recipient: 'Recipient',
    chooseRecipient: 'Choose a supplier or a venue',
    branchSuffix: 'Venue',
    deliveryDate: 'Delivery date',
    futureDate: 'Choose today or a future date.',
    pastDate: 'The delivery date must be today or a future date.',
    receiveFrom: 'Receiving from',
    receiveTo: 'Until',
    catalogSearch: 'Search the catalog…',
    catalogError: 'Unable to load the catalog.',
    noProducts: 'No products available',
    product: 'Product',
    vatLine: '{{unit}} · VAT {{vat}}%',
    net: 'Net',
    quantity: 'Quantity {{name}}',
    removeNamed: 'Remove {{name}}',
    remove: 'Remove',
    add: 'Add',
    productCount: '{{count}} products',
    totalLine: 'Total incl. VAT: {{amount}}',
    createHint:
      'Choose a recipient, a date and at least one product with a positive quantity. The end time must be after the start time.',
    createError: 'Unable to create the order.',
    calendarTitle: 'Order calendar',
    otherDate: 'Another date',
    loadPeriod: 'Load another period',
    today: 'Today',
    previousDays: 'Scroll to earlier days',
    nextDays: 'Scroll to later days',
    window: '(±20 days)',
    incoming: 'Incoming',
    toReview: 'To review',
    calendarTotals: 'Totals · all suppliers',
    calendarError: 'Calendar counts are unavailable.',
    calendarDays: 'Order calendar days',
    incomingSummary: '{{count}} incoming',
    reviewSummary: '{{count}} to review',
    processedSummary: '{{count}} processed',
    ordersSummary: '{{count}} orders, status detail unavailable',
    countsUnavailable: 'counts unavailable',
    calendarHint:
      'Each day counts scheduled, incoming and processed orders. Search and supplier filters apply to the list below.',
    exportJson: 'Export JSON',
    importJson: 'Import JSON',
    exported: 'File exported.',
    importedCount: '{{count}} items imported.',
    exportError: 'Export failed. Try again.',
    invalidJson: 'Invalid JSON.',
    importProducts:
      'Enter an array of products with a valid name, net price, VAT and unit.',
    importSuppliers: 'Enter an array of suppliers with a name on every row.',
    importError: 'Import failed. Check the data and try again.',
    importTitle: 'Import {{label}}',
    importAction: 'Import',
    jsonFile: 'JSON file',
    fileTooLarge: 'The file is larger than 5 MB.',
    fileReadError: 'Unable to read the file.',
    importOrdersHint:
      'Import creates new orders for the selected venue. Importing again creates more orders.',
    jsonArray: 'JSON array',
    ordersLabel: 'orders',
    productsLabel: 'products',
    suppliersLabel: 'suppliers',
    statuses: {
      BOZZA: 'Draft',
      PROGRAMMATO: 'Scheduled',
      IN_PREPARAZIONE: 'Preparing',
      IN_CONSEGNA: 'Out for delivery',
      DA_VERIFICARE: 'To review',
      RICEVUTO: 'Received',
      NON_CONSEGNATO: 'Not delivered',
      ANNULLATO: 'Cancelled',
      INVIATO: 'Sent',
      DA_CONFERMARE: 'To confirm',
      CONSEGNATO: 'Delivered',
    },
  },
  supplierOrderDetail: {
    matchProduct: 'Match to product',
    skipLine: 'Skip this line',
    duplicateMatch: 'Match each line to a different product.',
    continue: 'Continue',
    category: 'Category',
    close: 'Close',
    allCategories: 'All categories',
    personalWhatsApp: 'Personal WhatsApp',
    localWhatsApp: 'Restaurant WhatsApp',
    confirmSend: 'Send the order to the supplier using this channel?',
    confirmEdit: 'Save the updated order quantities?',
    checkQuantities: 'Check quantities',
    alreadyDelivered: 'Goods have arrived: proceed to verification',
    createReview: 'Order review',
    discardDraft: 'Discard the selected products?',
    savedShare:
      'Order saved. Would you like to choose how to send it to the supplier?',
    title: 'Supplier order',
    orderTitle: 'Order #{{id}}',
    back: 'Orders',
    unavailable: 'This order is not available.',
    requestedDelivery: 'Requested delivery',
    changeDate: 'Change date',
    orderTotal: 'Order total incl. VAT',
    netLine: 'Net {{net}} · VAT {{vat}}',
    products: 'Ordered products',
    addReceived: 'Add received product',
    verifyAll: 'Verify all',
    ordered: 'Ordered',
    sent: 'Sent',
    unitNet: 'Unit net',
    received: 'Received',
    receivedPrice: 'Received net price',
    verified: 'Verified',
    verifyNamed: 'Verify {{name}}',
    receivedNamed: 'Received quantity {{name}}',
    receivedPriceNamed: 'Received price {{name}}',
    editOrder: 'Update order',
    prepare: 'Prepare order',
    dispatch: 'Confirm dispatch',
    arrived: 'Delivery arrived',
    cancelOrder: 'Cancel order',
    notDelivered: 'Not delivered',
    receiptTitle: 'Check receipt',
    invoiceTotal: 'Invoice total incl. VAT',
    receivedProductsTotal: 'Calculated goods total (incl. VAT)',
    invoiceTotalHint: 'Amount recorded when receipt was confirmed.',
    invoiceDraftHint: 'Receipt amount entered; saved when you confirm.',
    insertedAmount: 'Entered total',
    expectedAmount: 'Expected order total',
    recordedAmount: 'Recorded total',
    receivedProductsTitle: 'Received products summary',
    lineTotal: 'Line total incl. VAT',
    receivedLineCalculation: '{{quantity}} × {{price}} + {{vat}}% VAT',
    invoiceGoodsDifference: 'Invoice minus goods total: {{amount}}',
    pendingVerification: 'Needs verification',
    verifiedProgress: '{{checked}} of {{total}} rows verified',
    receiptBlockedTitle: 'To enable “Confirm receipt”:',
    receiptNeedsProducts: 'Add at least one received product.',
    receiptNeedsChecks:
      'Tick the “Verified” checkbox for the remaining rows: {{count}}.',
    receiptInvalidLines:
      'Correct invalid received quantities or prices (zero or higher). Rows: {{count}}.',
    receiptInvalidInvoice: 'Enter a valid invoice total, zero or higher.',
    receiptSaving: 'Wait for the order to finish saving.',
    receiptUploading: 'Wait for the invoice photo to finish uploading.',
    receiptAnalyzing: 'Wait for invoice analysis to finish.',
    goToVerification: 'Go to the next row to verify',
    receiptReady: 'All rows are verified. You can confirm receipt.',
    receiptTotalDifference:
      'Invoice total {{invoice}} differs from the received products total {{products}}. Check the amounts; this difference does not disable confirmation.',
    movedToHistory: 'Message moved to order history',
    receiptHint:
      'Check the received quantity and net price, then tick “Verified” on each product row. Changing a quantity or price clears that row’s verification.',
    confirmReceipt: 'Confirm receipt',
    invoice: 'Invoice',
    invoiceAlt: 'Invoice photo',
    invoicePhoto: 'Invoice photo',
    choosePhoto: 'Choose file',
    ocrLabel: 'Optional notes',
    ocrPlaceholder:
      'Optional notes for AI analysis. Leave empty to analyze from the photo only.',
    ocrRunning: 'Analyzing invoice with AI…',
    ocrFailed: 'Invoice analysis failed. Try another photo or add notes.',
    clearPhotoAndText: 'Clear photo & text',
    readingPhotosTitle: 'Loading photo…',
    ocrProcessingTitle: 'Analyzing invoice with AI…',
    ocrProcessingHint:
      'Reading the invoice photo on the server. This can take a few seconds.',
    aiAnalyzingTitle: 'Analyzing invoice with AI…',
    aiAnalyzingHint:
      'Reading the invoice photo on the server. This can take a few seconds.',
    photoUploadingTitle: 'Saving invoice photo…',
    photoUploadingHint: 'Uploading your image before analysis.',
    analyze: 'Analyze invoice',
    reviewExtracted: 'Check the extracted values before applying them.',
    applyMatches: 'Apply to matching products',
    photoType: 'Upload a JPG or PNG image up to 5 MB.',
    imageInvalid: 'Invalid image.',
    imageReadError: 'Unable to read the image.',
    photoSaved: 'Invoice photo saved.',
    photoSaveError: 'Unable to save the invoice photo.',
    extractError: 'Invoice analysis failed.',
    communications: 'Supplier messages',
    configureMessages: 'Configure messages',
    communicationsError: 'Unable to load messages.',
    openSupplier: 'Open supplier page',
    recipientEmail: 'Recipient email',
    sendEmail: 'Send email',
    updated: 'Order updated.',
    operationError: 'The operation failed.',
    sendFailed: 'Sending failed.',
    messageSent: 'Message sent.',
    requestLogged: 'Request recorded. Check the result in messages.',
    communicationError: 'The message failed.',
    saveDate: 'Save date',
    invalidDate: 'Invalid date.',
    confirmTitle: 'Confirm action',
    confirmAction: 'Confirm',
    confirmBody: 'Update this order?',
    confirmReceive:
      'The receipt will be saved with the checked quantities and prices.',
    unexpectedProduct: 'Unexpected received product',
    catalogUnavailable: 'The catalog is not available.',
  },
  supplierOrderHistory: {
    title: 'Order history',
    subtitle: 'Search purchases by supplier, product and delivery date',
    back: 'Supplier orders',
    dateRange: 'Delivery dates',
    load: 'Load history',
    loading: 'Loading…',
    reset: 'Reset filters',
    orders: 'Orders',
    ordered: 'Ordered',
    received: 'Received',
    vatProducts: 'Incl. VAT · filtered products',
    allResults: 'All filtered results',
    search: 'Search by product name…',
    hint: 'All statuses, including archived · Amounts incl. VAT',
    summaryError: 'Unable to load the summary.',
    dirty:
      'Filters changed: press “Load history” to refresh the results and exports.',
    loadError: 'Unable to load the history.',
    results: 'Search results',
    resultsMeta: '{{count}} orders · Export includes every filtered result.',
    excel: 'Excel',
    pdf: 'PDF',
    exporting: 'Exporting…',
    empty: 'No orders found with these filters',
    deliveryDate: 'Delivery date',
    products: 'Products',
    filteredTotal: 'Filtered products · incl. VAT',
    exported: 'Exported {{orders}} orders and {{rows}} product rows.',
    exportError: 'Export failed.',
    allSuppliers: 'All suppliers',
    exportSheetProducts: 'Products',
    exportSheetSearch: 'Search',
    exportTitle: 'Supplier order history',
    exportOrder: 'Order',
    exportDelivery: 'Delivery date',
    exportSupplier: 'Supplier',
    exportSupplierCode: 'Supplier code',
    exportStatus: 'Status',
    exportProduct: 'Product',
    exportUnit: 'Unit',
    exportOrderedQty: 'Ordered quantity',
    exportSentQty: 'Sent quantity',
    exportReceivedQty: 'Received quantity',
    exportOrderedNet: 'Ordered net price',
    exportReceivedNet: 'Received net price',
    exportVat: 'VAT %',
    exportOrderedTotal: 'Ordered total incl. VAT',
    exportReceivedTotal: 'Received total incl. VAT',
    exportQtyChange: 'Quantity change',
    exportPriceChange: 'Price change',
    exportYes: 'Yes',
    exportNo: 'No',
    exportBranch: 'Venue',
    exportDeliveryFrom: 'Delivery from',
    exportDeliveryTo: 'Delivery to',
    exportProductName: 'Product name',
    exportAllProducts: 'All',
    exportStatuses: 'Statuses',
    exportAllStatuses: 'All, including archived',
    exportOrderCount: 'Order total',
    exportOrderedAmount: 'Ordered amount incl. VAT',
    exportReceivedAmount: 'Received amount incl. VAT',
    exportProductRows: 'Product rows',
    exportPage:
      'Page {{page}} of {{pages}} · {{orders}} orders · {{rows}} product rows',
  },
  suppliersPage: {
    title: 'Suppliers',
    subtitle: 'Contacts and catalogs for this venue’s orders',
    create: 'New supplier',
    active: 'Active',
    archived: 'Archived',
    search: 'Search supplier, email or VAT number…',
    noBranch: 'Select a venue.',
    loadError: 'Unable to load suppliers.',
    supplier: 'Supplier',
    contacts: 'Contacts',
    catalog: 'Catalog',
    openCatalog: 'Open catalog',
    productCount: '{{count}} products',
    vatLine: 'VAT {{vat}}',
    editNamed: 'Edit {{name}}',
    archiveNamed: 'Archive {{name}}',
    restoreNamed: 'Restore {{name}}',
    empty: 'No suppliers found.',
    archiveTitle: 'Archive supplier',
    restoreTitle: 'Restore supplier',
    archiveAction: 'Archive',
    restoreAction: 'Restore',
    archiveDesc:
      'Archive {{name}}? The catalog and order history are kept. The supplier cannot be selected for new orders until it is restored.',
    restoreDesc: 'Restore {{name}} to the active suppliers?',
    saveError: 'Unable to save the supplier. Try again.',
    archiveError: 'Unable to update the supplier archive. Try again.',
    editTitle: 'Edit supplier',
    groupContacts: 'Details and contacts',
    groupFiscal: 'Tax details',
    groupAddress: 'Address',
    legalName: 'Legal name',
    email: 'Email',
    phone: 'Phone',
    pec: 'Certified email',
    vat: 'VAT number',
    taxCode: 'Tax code',
    street: 'Street',
    city: 'City',
    postcode: 'Postcode',
    country: 'Country',
    fromInvoice: 'From invoice',
    invoiceTitle: 'Supplier from invoice',
    createSupplier: 'Create supplier',
    invoicePhoto: 'Invoice photo',
    photosSelected: '{{count}} photos selected',
    removePhotos: 'Remove photos',
    readingPhotos: 'Reading photos…',
    tooManyPhotos: 'Select up to 5 JPG or PNG images, 5 MB each.',
    invalidImage: 'Invalid image.',
    imageReadError: 'Unable to read the photo.',
    photosReadError: 'Unable to read the photos. Try again.',
    analyze: 'Analyze',
    existing: 'Suppliers already on file',
    analyzeError: 'Unable to analyze the invoice.',
    createError: 'Unable to create the supplier.',
  },
  menu: {
    ingredientsPage: {
      title: 'Ingredients',
      subtitle: 'Optional ingredients, categories and supplements',
      search: 'Search ingredient…',
      createIngredient: 'Create ingredient',
      createCategory: 'Create category',
      ingredients: 'Ingredients',
      categories: 'Categories',
      loadError: 'Unable to load the catalog.',
      operationError: 'The operation failed. Try again.',
      filterCategory: 'Filter category',
      allCategories: 'All categories',
      pageHint:
        'Categories group the ingredients on this page. A category can continue on the next page.',
      available: 'Available',
      unavailable: 'Unavailable',
      editNamed: 'Edit {{name}}',
      deleteNamed: 'Delete {{name}}',
      emptyCategory: 'No ingredients in this category.',
      emptyPage:
        'No ingredients on this page. Change the search or go back to the previous page.',
      perPage: 'Ingredients per page',
      createCategoryFirst: 'Create a category to get started.',
      categoryOrder:
        'Catalog order. Each recipe can customize the order of its own categories.',
      moveUp: 'Move {{name}} up',
      moveDown: 'Move {{name}} down',
      newIngredient: 'New ingredient',
      editIngredient: 'Edit ingredient',
      newCategory: 'New category',
      editCategory: 'Edit category',
      ingredientDesc: 'Name, category and supplement.',
      categoryDesc: 'Category name and translations.',
      name: 'Name *',
      category: 'Category *',
      select: 'Select',
      supplement: 'Supplement € *',
      description: 'Description',
      translations: 'Translations',
      english: 'English',
      spanish: 'Español',
      french: 'Français',
      deleteTitle: 'Delete from catalog',
      deleteDesc:
        'Delete “{{name}}”? Ingredients linked to dishes, and categories that are not empty, are protected.',
      delete: 'Delete',
    },
    recipeEditor: {
      createTitle: 'Create recipe',
      tabsLabel: 'Recipe and translations',
      configurationTab: 'Recipe',
      basePrice: 'Base price',
      dishName: 'Dish name',
      typeTitle: 'Dish type',
      simpleTitle: 'Simple recipe',
      simpleDesc: 'Name, description and allergens',
      customizableTitle: 'Customizable recipe',
      customizableDesc: 'Base + ingredients of your choice',
      compositeTitle: 'Set menu',
      compositeDesc: 'Simple dishes at a fixed price',
      wineTitle: 'Wine / Champagne',
      wineDesc: 'Bottle, glass and characteristics',
      includedDishes: 'Included dishes',
      searchDishes: 'Search dishes…',
      includedSections: 'Section of included dishes',
      allSections: 'All sections',
      selectionHint:
        '{{count}} dishes selected. Selections are kept when changing pages or searching.',
      cannotInclude: '{{reason}}: cannot be included',
      weightedDish: 'Priced by weight',
      noDishes: 'No dishes found.',
      dishesPerPage: 'Dishes per page',
      range: '{{from}}–{{to}} of {{total}}',
      previous: 'Previous',
      next: 'Next',
      minTwo: 'Select at least two simple dishes.',
      selectableIngredients: 'Selectable ingredients',
      minimumTotal: 'Total minimum',
      maximumTotal: 'Total maximum',
      searchIngredients: 'Search ingredients…',
      ingredientLoadError: 'Failed to load ingredients. Try again',
      disabled: 'Disabled',
      ingredientPage: '{{total}} ingredients · page {{page}}',
      categoryFilter: 'Category',
      allCategories: 'All categories',
      noIngredients: 'No ingredients found.',
      categoryOrderHelp:
        'Use the arrows to set the order in which customers see categories when customizing this recipe. Only categories with selected ingredients appear. Reordering does not change ingredients or prices.',
      categoryOrder: 'Category order in the recipe',
      moveEarlier: 'Move {{name}} earlier',
      moveLater: 'Move {{name}} later',
      availability: 'Availability and prices',
      restaurantMenu: 'Restaurant menu',
      takeaway: 'Takeaway',
      delivery: 'Delivery',
      enabled: 'Enabled',
      channelPrice: '{{channel}} price (€)',
      invalidChoices:
        'Select ingredients and check the minimum/maximum number of choices.',
      invalidPrices: 'Invalid prices: use at most two decimal places.',
      invalidComposition:
        'Customizable recipes and set menus require a fixed price and cannot be wines.',
      invalidForm: 'Check the name, price and required fields.',
      numericError: 'Enter a valid number.',
      nonNegativeError: 'The value must be zero or greater.',
      wineRed: 'Red',
      wineWhite: 'White',
      wineRose: 'Rosé',
      wineChampagne: 'Champagne',
      wineSparkling: 'Sparkling',
      wineOther: 'Other',
    },
    title: 'Menu',
    disabled: {
      badge: 'Section not enabled',
      title: 'This section is not enabled',
      description:
        'This feature is currently disabled for your account. Please contact support to know more.',
      infoTitle: 'Need access to this section?',
      infoDescription:
        "Reach out to our support team and we'll help you get started.",
      footerHelp: 'Still need help?',
      footerHelpDesc: 'Our support team is available 24/7 to assist you.',
    },
    subtitleDefault: 'View and manage your digital menus',
    statsCount: '{{count}} menu',
    searchPlaceholder: 'Search menus by name...',
    menuName: 'Menu Name',
    menuNamePlaceholder: 'Enter menu name',
    description: 'Description',
    descriptionPlaceholder: 'Enter menu description',
    newMenu: 'New Menu',
    createTitle: 'Create new menu',
    createAction: 'Create',
    url: 'URL',
    qrCode: 'QR Code',
    preview: 'Preview',
    copyUrl: 'Copy URL',
    editMenu: 'Edit Menu',
    deleteMenu: 'Delete Menu',
    duplicate: 'Duplicate',
    close: 'Close',
    saveChanges: 'Save',
    noBranch: 'Select a branch to view menus',
    empty: 'No menus found',
    emptyDesc: 'There are no menus for this branch yet.',
    validation: {
      nameRequired: 'Menu name is required',
      nameMax: 'Menu name cannot exceed 100 characters',
      descriptionMax: 'Description cannot exceed 500 characters',
    },
    deleteModal: {
      title: 'Delete Menu?',
      description:
        'Are you sure you want to delete this menu? This action cannot 		be undone.',
    },
    toast: {
      createSuccess: 'Menu created successfully.',
      createError: 'Failed to create the menu. Please try again.',
      updateSuccess: 'Menu updated successfully.',
      updateError: 'Failed to update the menu. Please try again.',
      deleteSuccess: 'Menu deleted successfully.',
      deleteError: 'Failed to delete the menu. Please try again.',
      duplicateSuccess: 'Menu duplicated successfully.',
      duplicateError: 'Failed to duplicate the menu. Please try again.',
      logoUploadSuccess: 'Logo uploaded successfully.',
      logoUploadError: 'Failed to upload logo.',
      logoRemoveSuccess: 'Logo removed successfully.',
      logoRemoveError: 'Failed to remove logo.',
      backgroundUploadSuccess: 'Background uploaded successfully.',
      backgroundUploadError: 'Failed to upload background.',
      backgroundRemoveSuccess: 'Background removed successfully.',
      backgroundRemoveError: 'Failed to remove background.',
      templateUpdateSuccess: 'Template updated successfully.',
      templateUpdateError: 'Failed to update template.',
    },
    edit: {
      pageTitle: 'Edit menu details',
      subtitle: 'Configure menu content, style, and sections.',
      tabListLabel: 'Menu edit tabs',
      withPhoto: 'with photo',
      withoutPhoto: 'without photo',
      uploading: 'Uploading...',
      removing: 'Removing...',
      tabs: {
        titleDescription: 'Title and Description',
        menuStyle: 'Menu Style',
        sectionsRecipes: 'Sections and Recipes',
      },
      informationTitle: 'Information Menu',
      informationDesc: 'Configure menu name and translations.',
      languages: {
        italian: 'Italian',
        italianMain: 'Main language',
        english: 'English',
        englishTranslation: 'English Translation',
        spanish: 'Spanish',
        spanishTranslation: 'Spanish Translation',
        french: 'French',
        frenchTranslation: 'French Translation',
      },
      descriptions: {
        italian: 'Menu Description',
        english: 'English Description',
        spanish: 'Spanish description',
        french: 'French Description',
      },
      style: {
        appearanceTitle: 'Menu Appearance',
        appearanceDesc: 'Upload logo and background image for your 	menu.',
        logo: 'Select menu logo',
        logoHint: 'We recommend a square logo (PNG), max 5MB.',
        background: 'Select menu background image',
        backgroundHint: 'We recommend a horizontal image, max 5MB.',
        removeImage: 'Remove image',
        templatesTitle: 'Select style to apply to menu',
        templatesDesc: 'Choose a template for your menu presentation.',
        previewStyleMenu: 'Preview style menu',
        select: 'Select',
        selected: 'Selected',
        active: 'Active',
      },
    },
    sections: {
      title: 'Sections and Recipes',
      description: 'Organize your menu into sections and add recipes.',
      addSection: 'Create new section',
      searchPlaceholder:
        'Search sections or recipes by name, description, or code...',
      noSearchResults: 'No matching sections or recipes',
      name: 'Section name',
      namePlaceholder: 'Enter section name',
      descriptionPlaceholder: 'Enter section description',
      createTitle: 'Create section on menu {{menu}}',
      createAction: 'Create',
      editTitle: 'Edit Menu Object',
      editDesc: 'Manage content, translations and ingredients',
      dishDrink: 'Dish/Drink',
      missingTranslations: 'Missing translations',
      missingTranslationsTooltip:
        'Name or description in this section have not been translated into all the languages provided by the menu, the Italian version will be shown in case of language change',
      addRecipe: 'Add recipe/product to {{section}}',
      sortAlphabetically: 'Sort recipes A-Z',
      moveLeft: 'Move section left',
      moveRight: 'Move section right',
      settings: 'Section settings',
      delete: 'Delete section',
      menuPrice: 'Menu price',
      sectionVisibility: 'Section visible',
      sectionVisibilityHint:
        'Show this section in the restaurant, delivery and takeaway menus.',
      sectionHidden: 'Section hidden',
      hideSectionPrices: 'Hide section prices',
      showSectionPrices: 'Show section prices',
      visibleSection: 'Visible section',
      visibility: 'Show / hide section',
      fixedMenuPrice: 'MENU FISSO ({{price}}€)',
      empty: 'No sections yet',
      emptyDesc: 'Use the plus button to create your first section.',
      emptyRecipes: 'No recipes added to this section yet.',
      recipeCount: '{{count}} recipes in this section',
      deleteModal: {
        title: 'Delete section?',
        description:
          'Are you sure you want to delete this section? This action cannot be undone.',
      },
      validation: {
        nameRequired: 'Section name is required',
        nameMax: 'Section name cannot exceed 100 characters',
        descriptionMax: 'Description cannot exceed 500 characters',
      },
      toast: {
        createSuccess: 'Section created successfully.',
        createError: 'Failed to create section. Please try again.',
        updateSuccess: 'Section updated successfully.',
        updateError: 'Failed to update section. Please try again.',
        deleteSuccess: 'Section deleted successfully.',
        deleteError: 'Failed to delete section. Please try again.',
        sortSuccess: 'Recipes sorted alphabetically.',
        sortError: 'Failed to sort recipes. Please try again.',
      },
    },
    recipes: {
      editTitle: 'Edit Menu Object',
      editDesc: 'Manage content, translations and ingredients',
      imageTitle: 'Image',
      productPhoto: 'Product photo',
      winePhoto: 'Wine photo',
      wineChampagne: 'Wine/Champagne',
      imageHintCreate:
        'Select an image to upload. You can add the recipe name before saving.',
      generalTitle: 'General Information',
      generalDesc: 'Name, description and configuration',
      priceSection: 'Price',
      priceHint: 'Enter the final price of the product',
      price: 'Price €',
      bottlePrice: 'Bottle Price',
      priceBottleLabel: 'Price bottle (€)',
      chalice: 'Chalice',
      pricePerGlass: 'Price per glass',
      alcoholPercentage: 'Alcohol (%) - Ex: 14',
      wineType: 'Type of Wine',
      wineTypePlaceholder: 'Type of Wine (e.g. Red, White, Rosé, Champagne...)',
      blend: 'Blend',
      blendPlaceholder:
        'Blend (e.g. 50% Negroamaro, 20% Nero di Troia, 30% Malvasia Nera)',
      yearOfProduction: 'Year of Production',
      cellar: 'Cellar',
      countryRegion: 'Country/Region',
      provenance: 'Provenance',
      pricePerWeight:
        'Is the price indicated per unit of weight (e.g. kg, litres) 	rather than per portion?',
      unitOfMeasurement: 'Unit of measurement',
      unitPlaceholder: 'Unit of measurement (ex: Kg, Lt, Pz)',
      assignAllergens: 'Assign allergens',
      productConfiguration: 'Product configuration',
      allergensTitle: 'Allergens',
      bestSeller: 'Best Seller',
      bestSellerDesc:
        'Highlight this product as one of the most ordered and make it visually more important on the menu.',
      available: 'Available',
      availableDesc:
        'Show this recipe to customers on your digital menu and make it orderable.',
      notAvailable: 'Not available',
      active: 'Active',
      visible: 'Visible',
      delete: 'Delete recipe/product',
      dragHandle: 'Drag to reorder recipe',
      deleteModal: {
        title: 'Delete recipe?',
        description:
          'Are you sure you want to delete this recipe? This action cannot be undone.',
      },
      validation: {
        nameRequired: 'Recipe name is required',
      },
      toast: {
        createSuccess: 'Recipe created successfully.',
        createError: 'Failed to create recipe. Please try again.',
        updateSuccess: 'Recipe updated successfully.',
        updateError: 'Failed to update recipe. Please try again.',
        deleteSuccess: 'Recipe deleted successfully.',
        deleteError: 'Failed to delete recipe. Please try again.',
        moveSuccess: 'Recipe order updated.',
        moveError: 'Failed to update recipe order. Please try again.',
        saveBeforeImage: 'Save the recipe before uploading an image.',
        imageUploadSuccess: 'Recipe image uploaded successfully.',
        imageUploadError: 'Failed to upload recipe image.',
        imageRemoveSuccess: 'Recipe image removed successfully.',
        imageRemoveError: 'Failed to remove recipe image.',
      },
    },
    allergens: {
      cereals: 'Cereals with gluten',
      gluten: 'Gluten',
      crustaceans: 'Crustaceans',
      molluscs: 'Mollusks',
      fish: 'Fish',
      eggs: 'Eggs',
      peanuts: 'Peanuts',
      soybean: 'Soy',
      mushrooms: 'Mushrooms',
      nuts: 'Tree nuts',
      celery: 'Celery',
      mustard: 'Mustard',
      sesame: 'Sesame',
      sulfur: 'Sulfur dioxide',
      lupins: 'Lupins',
      lactose: 'Lactose',
      corn: 'Corn',
      pork: 'Pork',
      beef: 'Beef',
      poultry: 'Poultry',
      nickel: 'Nickel',
      legumes: 'Legumes',
      citrus: 'Citrus fruits',
      cacao: 'Cocoa',
      honey: 'Honey',
    },
    translate: {
      action: 'Translate',
      progressTitle: 'Translation in progress...',
      progressDesc: 'Please wait for the translations to complete.',
      translationsTitle: 'Translations',
      success: 'Translations applied successfully.',
      error: 'Failed to translate. Please try again.',
    },
    previewPage: {
      pageTitle: 'Menu Preview',
      loading: 'Loading menu',
      menuLabel: 'Menu',
      sectionLabel: 'Section',
      priceLabel: 'Price',
      noPhoto: 'No photo available',
      bestSeller: 'Best',
      allergensTitle: 'Allergens',
      poweredBy: 'Powered by PRO20',
      scrollCue: 'Scroll to menu',
      storyContinue: 'Menu',
      close: 'Close',
      wine: {
        grape: 'Grape Variety',
        cellar: 'Winery',
        year: 'Year',
        region: 'Region',
        alcohol: 'Alcohol',
        glass: 'Glass',
        bottle: 'Bottle',
      },
    },
  },
  landingPages: {
    title: 'Landing Pages',
    subtitle: 'Landing Page List for {{branch}}',
    subtitleDefault: 'View and manage your Landing Page List',
    searchPlaceholder: 'Search for landing pages by name',
    searchPlaceholder2: 'Enter landing page name',
    landingPageName: 'Landing Page Name',
    itemsPerPage: 'Items per page',
    range: '{{start}} - {{end}} of {{total}}',
    noBranch: 'Select a branch to view landing pages',
    empty: 'No landing pages found',
    emptyDesc: 'There are no landing pages for this branch yet.',
    addLandingPage: 'Add Landing Page',
    newLanding: 'New Landing Page',
    newLandingDesc: 'Create a new landing page for your branch.',
    createLanding: 'Create landing page',
    statsCount: '{{count}} Landing Pages',
    present: 'present',
    url: 'URL',
    qrCode: 'QR Code',
    preview: 'Preview',
    showPreview: 'Show Preview',
    copyUrl: 'Copy URL',
    editLanding: 'Edit Landing Page',
    deleteLanding: 'Delete Landing Page',
    duplicate: 'Duplicate',
    validation: {
      landingPageNameMin: 'Landing page name must be at least 2 characters',
      landingPageNameMax: 'Landing page name cannot exceed 50 characters',
    },
    deleteModal: {
      title: 'Delete Landing Page?',
      description:
        'Are you sure you want to delete this landing page? This action cannot be undone.',
    },
    toast: {
      createSuccess: 'Landing page created successfully.',
      createError: 'Failed to create the landing page. Please try again.',
      deleteSuccess: 'Landing page deleted successfully.',
      deleteError: 'Failed to delete the landing page. Please try again.',
      duplicateSuccess: 'Landing page duplicated successfully.',
      duplicateError: 'Failed to duplicate the landing page. Please try again.',
      updateSuccess: 'Landing page updated successfully.',
      updateError: 'Failed to update the landing page. Please try again.',
      logoUploadSuccess: 'Logo uploaded successfully.',
      logoUploadError: 'Failed to upload logo.',
      logoRemoveSuccess: 'Logo removed successfully.',
      logoRemoveError: 'Failed to remove logo.',
      backgroundUploadSuccess: 'Background uploaded successfully.',
      backgroundUploadError: 'Failed to upload background.',
      backgroundRemoveSuccess: 'Background removed successfully.',
      backgroundRemoveError: 'Failed to remove background.',
      buttonSaveSuccess: 'Button saved successfully.',
      buttonSaveError: 'Failed to save button.',
      buttonDeleteSuccess: 'Button deleted successfully.',
      buttonDeleteError: 'Failed to delete button.',
    },
    edit: {
      pageTitle: 'Edit Landing Page',
      subtitle: 'Configure media and action buttons.',
      formConfiguration: 'Landing page configuration',
      formConfigurationDesc: 'Configure media and action buttons.',
      linkForm: 'Landing page link',
      close: 'Close',
      mediaTitle: 'Media',
      mediaDescription:
        'Upload a logo and background image for your landing page.',
      detailsTitle: 'Details',
      logo: 'Logo',
      background: 'Background image',
      imageUpload: 'Click or drag to upload an image',
      removeImage: 'Remove image',
      uploading: 'Uploading...',
      removing: 'Removing...',
      buttons: {
        title: 'Landing Buttons',
        description:
          'Add call-to-action buttons shown on your landing page. Use the check icon to save each row.',
        text: 'Button text',
        link: 'Link',
        color: 'Background',
        textColor: 'Text color',
        add: 'Add button',
        save: 'Save button',
        delete: 'Delete button',
        empty: 'No buttons yet. Click + to add one.',
        textPlaceholder: 'e.g. Book now',
        linkPlaceholder: 'https://',
        validationRequired: 'Button text and link are required.',
      },
    },
    public: {
      documentTitle: 'Landing page',
      loading: 'Loading landing page...',
      unavailableTitle: 'Landing page unavailable',
      unavailableDesc:
        'This landing page could not be found or is no longer available.',
      buttonFallback: 'Open',
    },
  },
  tags: {
    title: 'Tags',
    titleWithBranch: 'Tags {{branch}}',
    pageDescription: 'No tags present',
    subtitle: 'Tag List for {{branch}}',
    subtitleDefault: 'View and manage your Tag List',
    searchPlaceholder: 'Search for tags by name',
    searchPlaceholder2: 'Enter tag name',
    tagName: 'Tag Name',
    itemsPerPage: 'Items per page',
    range: '{{start}} - {{end}} of {{total}}',
    noBranch: 'Select a branch to view tags',
    empty: 'No Tags Present',
    emptyDesc:
      'Create the first tag to better organize customers, bookings and marketing segmentations.',
    addTag: 'Add Tag',
    createTitle: 'Add Tag',
    createDesc: 'Create a new tag for your branch.',
    editTitle: 'Edit Tag',
    editDesc: 'Update the tag name.',
    editTag: 'Edit tag',
    duplicate: 'Duplicate',
    typeLabel: 'TAG',
    statsCount: '{{count}} tags present',
    updatedAgo: 'Updated 2 mins ago',
    creating: 'Creating...',
    saving: 'Saving...',
    saveChanges: 'Save changes',
    toast: {
      createSuccess: 'Tag created successfully.',
      createError: 'Failed to create the tag. Please try again.',
      updateSuccess: 'Tag updated successfully.',
      updateError: 'Failed to update the tag. Please try again.',
      deleteSuccess: 'Tag deleted successfully.',
      deleteError: 'Failed to delete the tag. Please try again.',
      duplicateSuccess: 'Tag duplicated successfully.',
      duplicateError: 'Failed to duplicate the tag. Please try again.',
    },
    validation: {
      tagNameMin: 'Tag name must be at least 2 characters',
      tagNameMax: 'Tag name cannot exceed 50 characters',
    },
    deleteTag: 'Delete Tag',
    deleteModel: {
      deleteTitle: 'Delete Tag?',
      deleteDescription:
        'Are you sure you want to delete this Tag? This action cannot be undone.',
    },
  },
  publicRelations: {
    title: 'Public Relations',
    titleWithBranch: 'PR {{branch}}',
    pageDescription: 'No PR present',
    subtitle: 'pr List for {{branch}}',
    subtitleDefault: 'View and manage your pr List',
    searchPlaceholder: 'Search for pr by name',
    searchPlaceholder2: 'Enter pr name',
    prName: 'PR Name',
    itemsPerPage: 'Items per page',
    range: '{{start}} - {{end}} of {{total}}',
    noBranch: 'Select a branch to view pr',
    empty: 'No PR Present',
    emptyDesc:
      'Create the first PR to organize referrals, collaborations and customer management.',
    addPr: 'Add PR',
    createTitle: 'Add PR',
    createDesc: 'Create a new public relations contact for your branch.',
    editTitle: 'Edit PR',
    editDesc: 'Update the public relations contact name.',
    editPr: 'Edit PR',
    duplicate: 'Duplicate',
    typeLabel: 'PR MANAGER',
    statsCount: '{{count}} PRs present',
    updatedAgo: 'Updated 2 mins ago',
    creating: 'Creating...',
    saving: 'Saving...',
    saveChanges: 'Save changes',
    toast: {
      createSuccess: 'PR created successfully.',
      createError: 'Failed to create the PR. Please try again.',
      updateSuccess: 'PR updated successfully.',
      updateError: 'Failed to update the PR. Please try again.',
      deleteSuccess: 'PR deleted successfully.',
      deleteError: 'Failed to delete the PR. Please try again.',
      duplicateSuccess: 'PR duplicated successfully.',
      duplicateError: 'Failed to duplicate the PR. Please try again.',
    },
    validation: {
      prNameMin: 'PR name must be at least 2 characters',
      prNameMax: 'PR name cannot exceed 50 characters',
    },
    deletePR: 'Delete PR',
    deleteModel: {
      deleteTitle: 'Delete PR?',
      deleteDescription:
        'Are you sure you want to delete this PR? This action cannot be undone.',
    },
  },
  staff: {
    collaborators: {
      title: 'Collaborators',
      subtitle: 'Collaborators for {{branch}}',
      subtitleDefault: 'View and manage your collaborator list',
      searchPlaceholder:
        'Search for collaborators by first name, last name, email, phone number or role',
      firstName: 'First Name',
      lastName: 'Last Name',
      firstNamePlaceholder: 'Enter first name',
      lastNamePlaceholder: 'Enter last name',
      email: 'Email',
      phone: 'Phone Number',
      role: 'Role',
      itemsPerPage: 'Items per page',
      viewDetails: 'View Details',
      sharedCredentials: 'Shared Credentials',
      addCollaborator: 'Add Collaborator',
      editCollaborator: 'Edit Collaborator',
      deleteCollaborator: 'Delete Collaborator',
      changePassword: {
        action: 'Change password',
        title: 'Change password',
        subtitle: 'Set a new password for {{name}}',
        newPassword: 'New password',
        newPasswordPlaceholder: 'Enter 6-digit password',
        confirmPassword: 'Confirm password',
        confirmPasswordPlaceholder: 'Confirm 6-digit password',
        submit: 'Save password',
        validation: {
          passwordDigits: 'Password must be exactly 6 digits',
          passwordMismatch: 'Passwords do not match',
        },
      },
      range: '{{start}} - {{end}} of {{total}}',
      noBranch: 'Select a branch to view collaborators',
      empty: 'No collaborators found',
      emptyDesc: 'There are no collaborators for this branch yet.',
      emailPlaceholder: 'Enter email',
      phonePlaceholder: 'Enter phone number',
      username: 'Username',
      usernamePlaceholder: 'Enter username',
      password: 'Password',
      passwordPlaceholder: 'Enter password',
      gender: 'Gender',
      selectGender: 'Select gender',
      male: 'Male',
      female: 'Female',
      selectRole: 'Select role',
      remunerationType: 'Remuneration type',
      selectRemunerationType: 'Select remuneration type',
      hourly: 'Hourly',
      daily: 'Daily',
      monthly: 'Monthly',
      sendCredentials: 'Send credentials',
      roles: {
        receptionist: 'Receptionist',
        assistantCook: 'Assistant cook',
        administrator: 'Administrator',
        bartender: 'Bartender',
        chef: 'Chef',
        dishwasher: 'Dishwasher',
        pizzaChef: 'Pizza chef',
        director: 'Director',
        waiter: 'Waiter',
        publicRelations: 'Public relations',
        deputyDirector: 'Deputy director',
      },
      validation: {
        firstNameMin: 'First name must be at least 2 characters',
        lastNameMin: 'Last name must be at least 2 characters',
        firstNameMax: 'First name cannot exceed 40 characters',
        lastNameMax: 'Last name cannot exceed 40 characters',
        emailInvalid: 'Please enter a valid email address',
        phoneInvalid: 'Phone number must contain 6 to 15 digits',
        countryCodeInvalid: 'Please select a valid country code',
        usernameMin: 'Username must be at least 4 characters',
        usernameMax: 'Username cannot exceed 30 characters',
        passwordMin: 'Password must be at least 8 characters',
        passwordDigits: 'Password must be exactly 6 digits',
        genderRequired: 'Please select a gender',
        roleRequired: 'Please select a role',
        remunerationRequired: 'Please select a remuneration type',
      },
      deleteModel: {
        deleteTitle: 'Delete Collaborator?',
        deleteDescription:
          'Are you sure you want to delete this collaborator? This action cannot be undone.',
      },
      collaboratorDetails: {
        title: 'Collaborator Details',
        subtitle: 'View collaborator information and employment details.',
        personalDetails: 'Personal Details',
        employmentDetails: 'Employment Details',
        jobDescription: 'Job Description',
        dateOfBirth: 'Date of Birth',
        branchCode: 'Branch Code',
        contractType: 'Contract Type',
        startDateInduction: 'Start Date Induction',
        endDateInduction: 'End Date Induction',
        mobileDeviceAccessDetail: 'Mobile Device Access Detail',
        uniqueId: 'Unique ID',
        platform: 'Platform',
        model: 'Model',
        machine: 'Machine',
        release: 'Release',
        fcmToken: 'FCM Token',
        registrationDate: 'Registration Date',
        noMobileDevices: 'No mobile devices registered.',
        close: 'Close',
        contractTypes: {
          determined: 'Determined',
          indeterminate: 'Indeterminate',
        },
        visible: 'Visible',
        fired: 'Fired',
        retribution: 'Retribution',
        yes: 'Yes',
        no: 'No',
      },
      toast: {
        createSuccess: 'Collaborator created successfully.',
        createError: 'Failed to create collaborator. Please try again.',
        updateSuccess: 'Collaborator updated successfully.',
        updateError: 'Failed to update collaborator. Please try again.',
        deleteSuccess: 'Collaborator deleted successfully.',
        deleteError: 'Failed to delete collaborator. Please try again.',
        sendCredentialsSoon: 'Sending credentials will be available soon.',
        sendCredentialsSuccess: 'Credentials sent successfully.',
        sendCredentialsError: 'Failed to send credentials. Please try again.',
        sendCredentialsMissingPhone:
          'This collaborator does not have a valid phone number.',
        sendCredentialsMissingCredentials:
          'Username or password is missing for this collaborator.',
        changePasswordSuccess: 'Password updated successfully.',
        changePasswordError: 'Failed to update password. Please try again.',
      },
      credentialsMessage:
        'Your Proventi credentials:\nUsername: {{username}}\nPassword: {{password}}',
    },
    collaboratorReports: {
      title: 'Collaborator Reports',
      titleWithMonth: 'Report {{branch}} of {{monthYear}}',
      subtitle: 'Collaborator reports for {{branch}}',
      subtitleDefault: 'View and manage your collaborator reports list',
      noReportsPresent: 'No report present',
      searchPlaceholder: 'Search by name',
      name: 'Name',
      lunch: 'Lunch',
      cena: 'Cena',
      illness: 'Illness',
      holiday: 'Holiday',
      free: 'Free',
      hoursWorked: 'Hours Worked',
      itemsPerPage: 'Items per page',
      range: '{{start}} - {{end}} of {{total}}',
      noBranch: 'Select a branch to view collaborator Reports',
      empty: 'No collaborators reports found',
      emptyDesc: 'There are no collaborators reports for this branch yet.',
      previousMonth: 'Previous month',
      nextMonth: 'Next month',
      downloadPdf: 'Download report of {{monthYear}}',
      downloadPdfSuccess: 'Collaborator report PDF downloaded.',
      downloadPdfError: 'Failed to download collaborator report PDF.',
      pdfTitle: 'Collaborator Reports',
      pdfMeta:
        '{{branch}} · {{monthYear}} · {{count}} collaborators · Generated {{date}}',
    },
  },
  extraServices: {
    title: 'Extra Services & Products',
    subtitle:
      'Manage services, products and amenities available for {{branchName}}',
    subtitleDefault: 'View and manage your extra services list',
    statsCount: '{{count}} Extra Services',
    searchPlaceholder: 'Search for a service or product...',
    noBranch: 'Select a branch to view extra services',
    empty: 'No Extra Service',
    emptyDesc:
      "You have not configured extra services or products yet. Add amenities to improve your customers' experience.",
    itemsPerPage: 'Items per page',
    range: '{{start}} - {{end}} of {{total}}',
    columns: {
      service: 'Service',
      price: 'Price',
      guests: 'Guests',
      state: 'State',
    },
    active: 'Active',
    inactive: 'Inactive',
    createTitle: 'Create new service',
    createDesc: 'Add a new service or product for your branch.',
    editTitle: 'Edit service',
    editDesc: 'Update service information',
    createService: 'Create service',
    addService: 'Add service',
    creating: 'Creating...',
    saveChanges: 'Save changes',
    close: 'Close',
    name: 'Name',
    namePlaceholder: 'Enter service name',
    description: 'Description',
    descriptionPlaceholder: 'Enter description',
    price: 'Price (€)',
    pricePlaceholder: '0',
    guests: 'Number of guests',
    guestsPlaceholder: '1',
    activeService: 'Active service',
    serviceImage: 'Service image',
    uploadImage: 'Upload image',
    removeImage: 'Remove image',
    uploading: 'Uploading...',
    removing: 'Removing...',
    editService: 'Edit service',
    duplicate: 'Duplicate',
    deleteService: 'Delete service',
    deleteModal: {
      title: 'Delete service?',
      description:
        'Are you sure you want to delete this service? This action cannot be undone.',
    },
    validation: {
      nameRequired: 'Name is required',
      nameMax: 'Name cannot exceed 120 characters',
      descriptionMax: 'Description cannot exceed 300 characters',
      numberInvalid: 'Enter a valid number only',
      priceMin: 'Price cannot be negative',
      guestsMin: 'Number of guests cannot be negative',
      guestsInteger: 'Number of guests must be a whole number',
    },
    toast: {
      createSuccess: 'Service created successfully.',
      createError: 'Failed to create the service. Please try again.',
      updateSuccess: 'Service updated successfully.',
      updateError: 'Failed to update the service. Please try again.',
      deleteSuccess: 'Service deleted successfully.',
      deleteError: 'Failed to delete the service. Please try again.',
      duplicateSuccess: 'Service duplicated successfully.',
      duplicateError: 'Failed to duplicate the service. Please try again.',
      uploadSuccess: 'Image uploaded successfully.',
      uploadError: 'Failed to upload image.',
      removeImageSuccess: 'Image removed successfully.',
      removeImageError: 'Failed to remove image.',
    },
  },
  formMenu: {
    title: 'Form Menu',
    subtitleDefault: 'View and manage Form Menus for your branch',
    statsCount: '{{count}} Form Menus',
    searchPlaceholder: 'Search for a Form Menu...',
    noBranch: 'Select a branch to view Form Menus',
    empty: 'No Form Menu',
    emptyDesc:
      'You have not configured any Form Menus yet. Add one to get started.',
    columns: {
      item: 'Menu Name',
      status: 'Status',
      price: 'Price',
      totalBooking: 'Total Booking',
      forms: 'Forms',
      state: 'State',
    },
    formsCount: '{{count}} forms',
    active: 'Active',
    inactive: 'Inactive',
    createTitle: 'Create Form Menu',
    createDesc: 'Add a new Form Menu for your branch.',
    editTitle: 'Edit Form Menu',
    editDesc: 'Update Form Menu information',
    createItem: 'Create',
    addItem: 'Add Form Menu',
    saveChanges: 'Save changes',
    close: 'Close',
    name: 'Menu Name',
    namePlaceholder: 'Enter Form Menu name',
    description: 'Description',
    descriptionPlaceholder: 'Enter description',
    price: 'Price (€)',
    pricePlaceholder: '0',
    forms: 'Forms',
    formsPlaceholder: 'Select forms',
    activeItem: 'Active Form Menu',
    itemImage: 'Menu Image',
    uploadImage: 'Upload image',
    removeImage: 'Remove image',
    uploading: 'Uploading...',
    weekdaysLabel: 'Weekdays',
    selectAllWeekdays: 'Select all',
    clearWeekdays: 'Clear',
    availabilityDates: {
      title: 'Menu availability dates',
      dateRange: 'Date range',
      placeholder: 'Select date range',
      empty: 'No availability dates yet. Add a date range to get started.',
      addRange: 'Add date range',
      createTitle: 'Add date range',
      editTitle: 'Edit date range',
      create: 'Add',
      update: 'Update',
      editRange: 'Edit date range',
      deleteRange: 'Delete date range',
      deleteTitle: 'Delete date range?',
      deleteDescription:
        'Are you sure you want to remove this availability date range?',
      dateRequired: 'Select a date range.',
      dateOrder: 'End date must be on or after the start date.',
      dateOverlap:
        'This range overlaps existing availability dates. Choose dates that are free.',
    },
    weekdays: {
      monday: 'Monday',
      tuesday: 'Tuesday',
      wednesday: 'Wednesday',
      thursday: 'Thursday',
      friday: 'Friday',
      saturday: 'Saturday',
      sunday: 'Sunday',
    },
    editItem: 'Edit Form Menu',
    duplicate: 'Duplicate',
    editNotFound: 'This Form Menu was not found or is no longer available.',
    deleteItem: 'Delete Form Menu',
    deleteModal: {
      title: 'Delete Form Menu?',
      description:
        'Are you sure you want to delete this Form Menu? This action cannot be undone.',
    },
    validation: {
      nameRequired: 'Name is required',
      nameMax: 'Name cannot exceed 120 characters',
      descriptionMax: 'Description cannot exceed 300 characters',
      numberInvalid: 'Enter a valid number only',
      priceMin: 'Price cannot be negative',
    },
    toast: {
      createSuccess: 'Form Menu created successfully.',
      createError: 'Failed to create the Form Menu. Please try again.',
      updateSuccess: 'Form Menu updated successfully.',
      updateError: 'Failed to update the Form Menu. Please try again.',
      deleteSuccess: 'Form Menu deleted successfully.',
      deleteError: 'Failed to delete the Form Menu. Please try again.',
      duplicateSuccess: 'Form Menu duplicated successfully.',
      duplicateError: 'Failed to duplicate the Form Menu. Please try again.',
      showEnabled: 'Form Menu is now visible.',
      showDisabled: 'Form Menu is now hidden.',
      statusError: 'Failed to update Form Menu status. Please try again.',
      uploadSuccess: 'Image uploaded successfully.',
      uploadError: 'Failed to upload image.',
      removeImageSuccess: 'Image removed successfully.',
      removeImageError: 'Failed to remove image.',
      dateRangeCreateSuccess: 'Availability date range added.',
      dateRangeCreateError: 'Failed to add availability date range.',
      dateRangeUpdateSuccess: 'Availability date range updated.',
      dateRangeUpdateError: 'Failed to update availability date range.',
      dateRangeDeleteSuccess: 'Availability date range deleted.',
      dateRangeDeleteError: 'Failed to delete availability date range.',
      dateRangeLoadError: 'Failed to load availability date range.',
    },
  },
  events: {
    title: 'Events',
    subtitleDefault: 'View and manage branch events',
    statsCount: '{{count}} Events',
    searchPlaceholder: 'Search events...',
    filterDatePlaceholder: 'Filter by date',
    reset: 'Reset',
    noBranch: 'Select a branch to view events',
    empty: 'No events',
    emptyDesc:
      'You have not created any events yet. Add an event to get started.',
    columns: {
      event: 'Event',
      start: 'Start',
      end: 'End',
      capacity: 'Capacity',
      totalBooking: 'Total Booking',
      forms: 'Forms',
      status: 'Status',
    },
    formsCount: '{{count}} forms',
    createTitle: 'Create event',
    createDesc: 'Add a new event for your branch.',
    editTitle: 'Edit event',
    editDesc: 'Update event information',
    createEvent: 'Create event',
    createItem: 'Create',
    addEvent: 'Add event',
    saveChanges: 'Save changes',
    close: 'Close',
    name: 'Name',
    namePlaceholder: 'Enter event name',
    description: 'Description',
    descriptionPlaceholder: 'Enter description',
    startDate: 'Start date',
    startTime: 'Start time',
    endDate: 'End date',
    endTime: 'End time',
    capacity: 'Max capacity',
    capacityPlaceholder: '100',
    visible: 'Show max capacity',
    forms: 'Forms',
    formsPlaceholder: 'Select forms',
    mandatory: 'Event mandatory',
    weekdaysLabel: 'Weekdays',
    selectAllWeekdays: 'Select all',
    clearWeekdays: 'Clear',
    availabilityDates: {
      title: 'Event availability dates',
      dateRange: 'Date range',
      placeholder: 'Select date range',
      empty: 'No availability dates yet. Add a date range to get started.',
      addRange: 'Add date range',
      createTitle: 'Add date range',
      editTitle: 'Edit date range',
      create: 'Add',
      update: 'Update',
      editRange: 'Edit date range',
      deleteRange: 'Delete date range',
      deleteTitle: 'Delete date range?',
      deleteDescription:
        'Are you sure you want to remove this availability date range?',
      dateRequired: 'Select a date range.',
      dateOrder: 'End date must be on or after the start date.',
      dateOverlap:
        'This range overlaps existing availability dates. Choose dates that are free.',
    },
    weekdays: {
      monday: 'Monday',
      tuesday: 'Tuesday',
      wednesday: 'Wednesday',
      thursday: 'Thursday',
      friday: 'Friday',
      saturday: 'Saturday',
      sunday: 'Sunday',
    },
    eventImage: 'Event image',
    uploadImage: 'Upload image',
    removeImage: 'Remove image',
    uploading: 'Uploading...',
    removing: 'Removing...',
    editEvent: 'Edit event',
    duplicate: 'Duplicate',
    deleteEvent: 'Delete event',
    deleteModal: {
      title: 'Delete event?',
      description:
        'Are you sure you want to delete this event? This action cannot be undone.',
    },
    validation: {
      nameRequired: 'Name is required',
      nameMax: 'Name cannot exceed 120 characters',
      descriptionMax: 'Description cannot exceed 500 characters',
      capacityInvalid: 'Enter a valid capacity',
      capacityInteger: 'Capacity must be a whole number',
      capacityMin: 'Capacity must be at least 1',
      capacityMax: 'Capacity cannot exceed 999',
      startDateRequired: 'Start date is required',
      startTimeRequired: 'Start time is required',
      endDateRequired: 'End date is required',
      endTimeRequired: 'End time is required',
      endBeforeStart: 'End must be after start',
    },
    toast: {
      createSuccess: 'Event created successfully.',
      createError: 'Failed to create the event. Please try again.',
      updateSuccess: 'Event updated successfully.',
      updateError: 'Failed to update the event. Please try again.',
      deleteSuccess: 'Event deleted successfully.',
      deleteError: 'Failed to delete the event. Please try again.',
      duplicateSuccess: 'Event duplicated successfully.',
      duplicateError: 'Failed to duplicate the event. Please try again.',
      showEnabled: 'Event is now visible.',
      showDisabled: 'Event is now hidden.',
      statusError: 'Failed to update event status. Please try again.',
      uploadSuccess: 'Image uploaded successfully.',
      uploadError: 'Failed to upload image.',
      saveBeforeImage:
        'Fill in the required event details before uploading an image.',
      removeImageSuccess: 'Image removed successfully.',
      removeImageError: 'Failed to remove image.',
      dateRangeCreateSuccess: 'Availability date range added.',
      dateRangeCreateError: 'Failed to add availability date range.',
      dateRangeUpdateSuccess: 'Availability date range updated.',
      dateRangeUpdateError: 'Failed to update availability date range.',
      dateRangeDeleteSuccess: 'Availability date range deleted.',
      dateRangeDeleteError: 'Failed to delete availability date range.',
      dateRangeLoadError: 'Failed to load availability date range.',
    },
  },
  reports: {
    title: 'Reports',
    subtitle: 'Analytics and reporting dashboard',
    comingSoon: 'Reports coming soon',
    comingSoonDesc:
      'Advanced analytics and custom report generation will be available in a future release.',
  },
  errors: {
    somethingWrong: 'Something went wrong',
    unexpected: 'An unexpected error occurred. Please try again.',
    pageNotFound: 'Page not found',
    pageNotFoundDesc:
      "The page you're looking for doesn't exist or has been moved.",
    accessDenied: 'Access Denied',
    accessDeniedDesc:
      "You don't have permission to access this resource. Contact your administrator if you believe this is an error.",
    backToDashboard: 'Back to Dashboard',
    backToHome: 'Back to home',
    publicHint:
      'Check the link you were sent, or contact the venue if you need help.',
    applicationError: 'Application Error',
    reload: 'Reload page',
  },
  publicPromo: {
    documentTitle: 'Promotion',
    loading: 'Loading promotion',
    unavailableTitle: 'Promotion unavailable',
    unavailableDesc:
      'This promotion link is missing, invalid, or no longer active.',
    expiredDesc: 'This promotion has expired and is no longer available.',
    comingSoonDesc: 'This promotion is not ready to collect responses yet.',
    formUnavailableTitle: 'Form not available',
    formUnavailableMessage:
      'This promotion form is currently suspended or unavailable.',
    badge: 'Promotion',
    requestTitle: 'Request your coupon',
    requestSubtitle: 'Fill in your details to request the promotion.',
    optional: 'Optional',
    birthDate: 'Date of birth',
    cap: 'Postal code',
    capPlaceholder: 'e.g. 09124',
    address: 'Address',
    addressPlaceholder: 'Street and number',
    nationality: 'Nationality',
    nationalityPlaceholder: 'Your nationality',
    requiredNote: '* Required fields',
    submitting: 'Saving…',
    personalInfo: 'Personal information',
    phoneLabel: 'Mobile',
    phonePlaceholder: 'Mobile phone',
    firstName: 'Name',
    firstNamePlaceholder: 'Name',
    lastName: 'Surname',
    lastNamePlaceholder: 'Surname',
    email: 'Email',
    emailPlaceholder: 'Email',
    consentsTitle: 'I consent to the processing of personal data',
    privacyConsent: 'Privacy and data processing',
    marketingConsent: 'Marketing and communication consensus',
    profilingConsent: 'Profiling consent',
    detailsLink: 'Details',
    selectAll: 'Select all',
    submit: 'Request the coupon',
    toast: {
      success: 'Coupon generated successfully.',
      error: 'Failed to generate coupon. Please try again.',
    },
    thankYou: {
      title: 'Thank you!',
      message: 'Your request has been submitted successfully.',
      continue: 'Continue',
    },
    lookupFound: 'We found your details and filled them in.',
    welcomeBack: 'Welcome back, {{firstName}}. We already have your details.',
    welcomeBackNoName: 'Welcome back. We already have your details.',
    alreadyHasCoupon: 'You already have a coupon for this promotion.',
    alreadyUsed: 'This coupon has already been used.',
    maxCoupons:
      'No more coupons can be issued for this promotion. The limit has been reached.',
    lookupError:
      'We could not check this number. You can still complete the form.',
    lookupNotFound: 'This promotion could not be found.',
    lookupInvalid: 'Check the mobile number and try again.',
    resend: {
      title: 'Send your coupon on WhatsApp?',
      message:
        'You already requested a coupon. We can send it again on WhatsApp, including the QR code.',
      confirm: 'Send on WhatsApp',
      decline: 'Not now',
      close: 'Close',
      success: 'We sent the coupon to your WhatsApp.',
      error: 'The coupon could not be sent. Please try again.',
    },
    validation: {
      prefixRequired: 'Select a country code.',
      phoneMin: 'Enter a valid mobile number.',
      phoneRange: 'Enter 6 to 15 digits.',
      firstNameRequired: 'Name is required.',
      lastNameRequired: 'Surname is required.',
      emailRequired: 'Email is required.',
      emailInvalid: 'Enter a valid email address.',
      birthDateInvalid: 'Enter a valid date that is not in the future.',
      privacyRequired: 'Privacy consent is required.',
    },
  },
  publicFidelity: {
    documentTitle: 'Fidelity card',
    loading: 'Loading fidelity program',
    unavailableTitle: 'Program unavailable',
    unavailableDesc: 'Check the link you received, or contact the restaurant.',
    badge: 'Your loyalty program',
    cardLabel: 'Fidelity card',
    previewPoints: 'Every visit counts.',
    previewDiscount: 'Benefits made for you.',
    requestTitle: 'Request your card',
    requestSubtitle: 'Create your personal card and keep it with you.',
    optional: 'Optional',
    requiredNote: '* Required fields',
    submitting: 'Saving…',
    phoneLabel: 'Mobile',
    phonePlaceholder: 'Your number',
    firstName: 'Name',
    firstNamePlaceholder: 'Your name',
    lastName: 'Surname',
    lastNamePlaceholder: 'Your surname',
    email: 'Email',
    emailPlaceholder: 'name@example.com',
    consentsTitle: 'Your preferences',
    privacyConsent: 'Privacy and data processing',
    marketingConsent: 'Marketing and communication consent',
    profilingConsent: 'Profiling consent',
    detailsLink: 'Details',
    selectAll: 'Select all',
    submit: 'Create my card',
    successTitle: 'Your card is ready.',
    successSubtitle: 'Save the QR and show it at the restaurant.',
    qrAlt: 'QR code of your fidelity card',
    openQr: 'Open and save the QR',
    welcomeBack: 'Welcome back, {{firstName}}. We already have your details.',
    welcomeBackNoName: 'Welcome back. We already have your details.',
    lookupError:
      'We could not check this number. You can still complete the form.',
    lookupUnavailable: 'This program is no longer available.',
    lookupInvalid: 'Check the mobile number and try again.',
    requestLink: 'Request the link again',
    requestLinkSending: 'Sending request…',
    requestLinkSent:
      'If this contact is linked to an active card, the link will be sent again.',
    requestLinkError: 'The link could not be requested. Please try again.',
    errors: {
      alreadyRegistered:
        'You already have a card for this program. Use the QR you received, or contact the restaurant to recover it.',
      unavailable: 'This program is no longer available.',
      generic:
        'The card could not be created. Try again shortly, or contact the restaurant.',
    },
    validation: {
      prefixRequired: 'Select a country code.',
      phoneRange: 'Enter 6 to 15 digits.',
      firstNameRequired: 'Enter your name.',
      lastNameRequired: 'Enter your surname.',
      emailInvalid: 'Check the email address.',
      privacyRequired: 'Privacy consent is required.',
    },
  },
  publicBookingForm: {
    imagePreview: {
      open: 'Enlarge image: {{name}}',
      close: 'Close image preview',
      error: 'Unable to load the image.',
    },
    title: 'Reservation',
    welcome: 'Welcome to',
    loading: 'Loading reservation form',
    missingFormCode: 'Form code is missing from the URL.',
    loadError: 'Unable to load this reservation form.',
    inactiveTitle: 'Form unavailable',
    inactiveMessage: 'This reservation form is not currently active.',
    invalidFormType: 'This link is not a reservation form.',
    steps: {
      date: 'Date',
      hours: 'Hours',
      menu: 'Menu',
      extra: 'Extra',
      details: 'Details',
      calendar: 'Calendar',
    },
    eventBanner: {
      countOne: '{{count}} event this month',
      count: '{{count}} events this month',
      tapHint: 'Tap to see which days',
    },
    menuBanner: {
      countOne: '{{count}} menu this month',
      count: '{{count}} menus this month',
      tapHint: 'Tap to see which days',
    },
    calendar: {
      legendEvent: 'Special event',
      legendMenu: 'Menus available',
    },
    restaurantNoMenu: {
      title: 'No menu',
      description: 'Number of guests without a menu',
    },
    eventSheet: {
      fromTime: 'From {{time}}',
      maxCovers: 'Max {{count}} covers',
      gotIt: 'Continue',
      continueWithEvent: 'Continue for event',
      showMore: 'Show more',
      showLess: 'Show less',
      close: 'Close',
    },
    hours: {
      selectedDate: 'Selected date',
      guestsLabel: 'Guests (children excluded)',
      childrenLabel: 'Children',
      timeSlotLabel: 'Available hour slot',
      guestOption: '{{count}} Guests',
      childrenOption: '{{count}} Children',
      overlimit: 'Overlimit by {{count}}',
      overbooking: 'Overbooking by {{count}}',
      availabilityFinished: 'Availability finished for this time slot',
      attentionTitle: 'Attention',
      attentionMessage:
        'The selected number of guests exceeds the remaining availability for this time slot.',
      attentionOk: 'OK',
      callOverMaxOption: 'Call for tables larger than {{count}} Guests',
      callOverMaxTitle: 'Info',
      callOverMaxMessageBefore: 'Call',
      callOverMaxMessageAfter: 'to book a table?',
      callOverMaxHint:
        'We’ll open your phone dialer so you can complete the call.',
      callOverMaxCall: 'Call',
      callOverMaxCancel: 'Cancel',
    },
    extra: {
      badge: 'Extra',
      catalogTitle: 'Service catalog',
      mandatoryNotice:
        'The choice of at least one product/service is mandatory.',
      serviceForGuests: 'Service/table for {{count}} guests',
      decrease: 'Decrease quantity',
      increase: 'Increase quantity',
    },
    menu: {
      badge: 'Menu',
      catalogTitle: 'Menu catalog',
      mandatoryQuantityHint:
        'Choose a menu (or “No menu”) for each person. Total selections must equal {{count}} (guests + children).',
      decrease: 'Decrease quantity',
      increase: 'Increase quantity',
    },
    recap: {
      title: 'Reservation summary',
      time: 'Hours',
      guests: 'Guests',
      children: 'Children',
      selectedServices: 'Selected services',
      selectedMenus: 'Selected menus',
      total: 'Total',
      paymentOnSite: 'Payment at the venue',
    },
    details: {
      phoneTitle: 'Enter your mobile number to continue',
      welcomeBackTitle: 'Welcome back, {{name}}!',
      welcomeBackTitleGeneric: 'Welcome back!',
      welcomeBackMessage:
        'We already have your data. You can continue or update your details if needed.',
      personalInfo: 'Personal information',
      consentsTitle: 'I consent to the processing of personal data',
      privacyConsent: 'Processing of privacy and data',
      marketingConsent: 'Consent marketing and communication',
      commercialConsent:
        'Consent transfer of data to third parties for commercial purposes',
      profilingConsent: 'Consent to profiling',
      detailsLink: 'Details',
      selectAll: 'Select all',
      consentModal: {
        title: 'INFORMATION ON THE PROCESSING OF PERSONAL DATA',
        subtitle: '(pursuant to Art. 13 of EU Reg. No. 679/2016 - GDPR)',
        addressLabel: 'Address',
        emailLabel: 'Email',
        lastUpdated: 'Last updated',
        section1Title: '1. Introduction',
        section1P1:
          'This information is provided by {{branchName}} (Tax Code and VAT Number{{vatDisplay}}), with registered office in {{branchAddress}} (hereinafter "Data Controller"). (hereinafter "Data Controller"), in compliance with European Regulation EU 2016/679 (GDPR) and Legislative Decree 196/2003 (Privacy Code), as amended by <b>Legislative Decree 101/2018</b>.',
        section1P2:
          'This policy applies to the processing of personal data carried out by the Data Controller through the <b>"Pro20"</b> platform for managing the business, bookings, services offered, and customer communications.',
        section1P3: 'The information is provided to Users who:',
        section1Li1:
          "• They use the Owner's website to request information or book a table;",
        section1Li2:
          '• They voluntarily provide their data through personal mobile devices or in-store devices (e.g. tablets) to access services such as the loyalty program or free Wi-Fi connection;',
        section1Li3:
          "• They physically show up at the premises and provide their data to receive newsletters, commercial offers and promotional material on the Data Controller's products and services.",
        section2Title: '2. Data controller',
        section2Sensitive:
          'Sensitive data will be processed only when strictly necessary and with the User\'s consent, expressed through explicit actions such as selecting an online option (e.g., "I accept" or "I consent") or signing a paper form.',
        section3Title: '3. Data processed and methods of acquisition',
        section3Intro: 'Data is collected in the following ways:',
        section3Li1:
          '• Through the "PRO20" platform for managing reservations and services offered',
        section3Li2:
          "• Through the Owner's website, when the User requests information or books a table",
        section3Li3:
          '• Directly on the premises, when the User provides data to access services (Wi-Fi, loyalty, etc.).',
        section4Title: '4. Browsing data',
        section4Body:
          "If the User interacts with the website or other digital platforms, browsing data is automatically collected (e.g., IP address, device type, operating system, pages visited). The use of cookies and similar tools is described in the Cookie Policy, available on the Data Controller's website.",
        section5Title: '5. Purpose of the processing',
        section51Title: '5.1 Primary purposes',
        section51Intro: 'The data is processed for:',
        section51Li1:
          '✔ Manage reservations and requested services (fila-fast, Wi-Fi, loyalty, satisfaction questionnaires, etc.)',
        section51Li2: '✔ Comply with legal, tax and administrative obligations',
        section51Li3:
          '✔ Protection of staff and service from illicit or fraudulent behavior',
        section51Li4:
          '✔ Anonymous statistical analysis to improve the quality of services',
        section51Li5:
          '✔ Communicate with the User regarding the booking or other requested services',
        section51Note:
          '📌 Providing your data is necessary to access the services: failure to provide it may prevent you from booking or providing the service.',
        section52Title:
          '5.2 Secondary purposes (Marketing and Promotions): only with explicit consent.',
        section52Intro: 'With your explicit consent, your data may be used to:',
        section52Li1:
          '📢 Sending promotional communications and personalized offers via email, SMS, or other digital channels',
        section52Li2: '📢 Market research and customer experience surveys',
        section52Li3:
          '📢 Analyze consumer habits to offer personalized experiences.',
        section52Note:
          '⚠ Consent to marketing is optional and can be revoked at any time by writing to {{email}}.',
        section6Title: '6. Data recipients',
        section6Intro: 'The data collected may be communicated to:',
        section6Li1:
          '• Authorized internal personnel of the Owner, for the management of the service',
        section6Li2:
          '• IT service providers and management platforms (e.g. online booking services, email marketing, web hosting), in compliance with confidentiality agreements',
        section6Li3:
          '• Public authorities, in case of legal requests or regulatory obligations',
        section6Note:
          '📌 The data will NOT be disclosed or sold to third parties',
        section7Title: '7. Data retention',
        section7Intro: 'The data will be stored for:',
        section7Li1:
          '📌 Administrative and accounting purposes: for the time necessary to fulfill legal obligations (up to 10 years).',
        section7Li2:
          '📌 Marketing purposes: until consent is revoked or, in the absence of interaction, for a maximum of 24 months.',
        section7Li3: '📌 Browsing data: as established in the Cookie Policy.',
        section8Title: '8. User Rights',
        section8Intro: 'Users have the right to:',
        section8Li1:
          '✅ Access your data and obtain information on its processing;',
        section8Li2: '✅ Request rectification or deletion of data;',
        section8Li3: '✅ Limit or object to processing for legitimate reasons;',
        section8Li4:
          '✅ Revoke consent at any time, without affecting the lawfulness of previous processing;',
        section8Li5:
          '✅ File a complaint with the Italian Data Protection Authority (www.garanteprivacy.it).',
        section9Title:
          '9. Consent to the transfer of data to third parties for commercial purposes',
        section9Li1:
          '✅ I consent to the processing of my personal data for direct marketing purposes by third parties, including through their transfer to commercial partners operating in the following sectors: [specify sectors, e.g., publishing, technology, travel, finance, etc.]. These parties may contact me by email, telephone, or other means for their own promotional initiatives. Consent is optional and may be revoked at any time by writing to {{email}}',
        section9Contact: '📧 To exercise your rights, write to: {{email}}',
        section10Title: '10. Changes to the information',
        section10Body:
          'The Data Controller reserves the right to update this policy. Changes will be communicated on the website or via email, if necessary.',
      },
    },
    actions: {
      continue: 'Continue',
      back: 'Back',
      proceedToDetails: 'Proceed to personal details',
    },
    validation: {
      date: 'Please select a date.',
      hours: 'Please complete guests, children, and time slot.',
      menu: 'Please review your menu selection.',
      menuMandatory:
        'Please choose menus so the total equals guests + children.',
      extra: 'Please select at least one service.',
    },
    fieldErrors: {
      firstNameMin: 'First name is required.',
      firstNameInvalid: 'First name can only contain letters.',
      lastNameMin: 'Last name must be at least 2 characters.',
      lastNameInvalid: 'Last name can only contain letters.',
      emailRequired: 'Email is required.',
      emailInvalid: 'Please enter a valid email.',
      postalCodeRequired: 'Postal code is required.',
      dateOfBirthRequired: 'Date of birth is required.',
      specialRequestsRequired: 'Special requests are required.',
      singleChoiceRequired: 'Please select an option.',
      multiChoiceRequired: 'Please select at least one option.',
      dateOfBirthInvalid: 'Please enter a valid date of birth.',
      dateOfBirthFuture: 'Date of birth cannot be in the future.',
      countryCodeInvalid: 'Please select a valid country code.',
      phoneRequired: 'Phone number is required.',
      phoneInvalid: 'Phone number must contain only digits.',
      phoneMin: 'Phone number must be at least 6 digits.',
      phoneMax: 'Phone number must be at most 15 digits.',
      privacyRequired: 'Privacy consent is required.',
      cardRequestConsentRequired:
        'Please accept the card verification / deposit terms.',
      bookingTimeInvalid: 'Please select a valid time.',
      coverOverlimit:
        'Guests and children exceed the maximum covers for this form.',
    },
    cardRequest: {
      title: 'Card required for this booking',
      notice:
        'To confirm this reservation a payment card is required for the selected time slot.',
      alreadyOnFile:
        'A card is already saved for this number. Confirm the booking to use it.',
      acceptValidation:
        'I accept card verification. No amount will be charged or blocked during this operation.',
      acceptDeposit:
        'I accept the pre-authorization of {{amount}} on the card and the conditions governing any collection, cancellation and no-show.',
      acceptPayNow:
        'I confirm payment of {{amount}} and accept the cancellation, refund and no-show conditions.',
      policyToggle: 'Read the full payment policy',
      policyTitle: 'Payment, card and cancellation policy',
      policy: {
        s1Title: '1. Venue identity',
        s1Body:
          'Booking and payment are managed by {{branchName}}, {{branchAddress}}. Stripe acts exclusively as the payment service provider. Contact: {{contact}}.',
        s2Title: '2. Card verification',
        s2Body:
          'When only verification is requested, no amount is charged or blocked. The card is verified via Stripe to confirm it is valid.',
        s3Title: '3. Pre-authorization / deposit',
        s3Body:
          'A pre-authorization of {{amount}} ({{perGuest}} per guest) may be temporarily held on the card. Release timing depends on the issuing bank.',
        s4Title: '4. Advance payment',
        s4Body:
          'Where configured, an advance payment or deposit may be charged according to the venue rules shown at booking time.',
        s5Title: '5. Cancellation and changes',
        s5Body:
          'Free cancellation may apply until the venue deadline. After that, the venue may retain or charge amounts according to its policy.',
        s6Title: '6. No-show and late arrival',
        s6Body:
          'In case of no-show or significant delay, the venue may retain or charge amounts under its no-show rules.',
        s7Title: '7. Refunds',
        s7Body:
          'Eligible refunds are processed to the same payment method used for the booking, within the timeframes set by the venue and the payment provider.',
        s8Title: '8. Card storage',
        s8Body:
          'Card data may be stored in tokenized form by Stripe and reused only with specific consent. PRO20 and the venue do not store the full card number.',
        s9Title: '9. Disputes and support',
        s9Body:
          'For cancellations, disputes or assistance contact the venue ({{contact}}) quoting your booking code.',
        version: 'Version {{version}}',
      },
    },
    legacyCard: {
      eyebrow: 'Direct verification',
      title: 'Credit card details',
      subtitle: 'This venue does not use Stripe for this reservation.',
      holder: 'Name on card',
      number: 'Card number',
      month: 'Month',
      year: 'Year',
      cvc: 'CVV',
      circuit: '{{circuit}}',
      verified: 'Card verified · ending in {{last4}}',
      cancel: 'Cancel',
      verify: 'Verify card',
      verifying: 'Verifying…',
      confirm: 'Confirm',
      verifySuccess: 'Card verified successfully',
      verifyError: 'The card details are not valid',
      requiredTitle: 'Card required',
      required: 'The card must be verified to complete the reservation.',
      storeWarning:
        'The reservation was created, but the card could not be saved. Try again from the card request.',
      saved: 'Card saved · ending in {{last4}}',
      alreadyOnFile:
        'A card is already on file for this guest. No further details are needed.',
      pageLoadError: 'This card request could not be opened.',
      saveError: 'The card could not be saved. Please try again.',
      fieldRequired: 'This field is required',
      fieldInvalid: 'Invalid value',
      circuits: {
        VISA: 'Visa',
        MASTERCARD: 'Mastercard',
        AMERICAN_EXPRESS: 'American Express',
        DISCOVER: 'Discover',
        JCB: 'JCB',
        MAESTRO: 'Maestro',
      },
    },
    stripe: {
      verifyTitle: 'Verify your card',
      verifyDescription:
        'Enter your card details to confirm this reservation. No amount will be charged now.',
      verifyConfirm: 'Verify card',
      payTitle: 'Complete payment',
      payDescription: 'Authorize {{amount}} to confirm this reservation.',
      payConfirm: 'Pay {{amount}}',
      initializing: 'Preparing secure payment…',
      processing: 'Processing…',
      initError: 'Unable to start Stripe checkout. Please try again.',
      confirmError: 'Payment could not be completed. Please try again.',
      verifyPending: 'Stripe has not confirmed the card yet. Please try again.',
      cardRequired: 'The card must be verified to complete the reservation.',
      resumePayment: 'Complete payment',
      resumeNotice:
        'Your reservation is on hold awaiting payment. Complete the payment to confirm it — no new reservation will be created.',
    },
    phonePlaceholder: 'Enter phone number',
    unknownCustomer: 'Unknown customer',
    blacklist: 'Blacklist',
    customerDetails: 'Your details',
    bookingDetails: 'Reservation details',
    firstName: 'First name',
    lastName: 'Last name',
    email: 'Email',
    phone: 'Phone',
    postalCode: 'Postal code',
    dateOfBirth: 'Date of birth',
    guests: 'Guests',
    children: 'Children',
    time: 'Time',
    selectTime: 'Select time',
    slotNotAvailable: 'No availability for this time slot',
    noAvailableTimes: 'No available times for the selected date.',
    specialRequests: 'Personal notes and requests',
    comingWithDogs: 'I am coming with dogs',
    extraServices: 'Extra services',
    amenityGuests: '{{count}} guests',
    submit: 'Submit reservation',
    submitError: 'Unable to submit your reservation. Please try again.',
    existingBooking: {
      title: 'Be careful, a booking already exists',
      date: 'Date',
      time: 'Time',
      guests: 'Guests',
      children: 'Children',
      event: 'Event',
      menus: 'Menus',
      noMenu: 'No menu',
      services: 'Services',
      continueQuestion:
        'Would you like to continue and create this reservation anyway?',
      continue: 'Continue',
    },
    thankYou: {
      title: 'Thank you!',
      message:
        'Your reservation request has been received. We will contact you shortly.',
      summaryTitle: 'Reservation summary',
      date: 'Date',
      time: 'Time',
      guests: 'Guests',
      guestsValue: '{{guests}} Guest ({{children}} Children)',
      location: 'Location',
      name: 'Name',
      phone: 'Phone',
      eventCode: 'Event code',
      eventName: 'Event',
      menus: 'Menus',
      makeAnother: 'Make another reservation',
      backHome: 'Back to home',
      continue: 'Continue',
    },
  },
  publicBookingUpdate: {
    title: 'Update reservation',
    loading: 'Loading reservation',
    missingBookingCode: 'Booking code is missing from the URL.',
    loadError: 'Unable to load this reservation.',
    formLoadError: 'Unable to load availability for this reservation.',
    noFormTitle: 'Online changes unavailable',
    noFormMessage:
      'This reservation cannot be changed online. Please contact the venue directly.',
    notEditable: 'The booking is no longer editable',
    hello: 'Hello {{name}}',
    subtitle: 'You are modifying your reservation at {{branch}}',
    currentBadge: 'Current: {{time}} · {{date}}',
    currentlyBooked: 'Currently booked',
    current: {
      title: 'Your reservation',
      client: 'Client',
      date: 'Current date',
      time: 'Current time',
      guests: 'Guests',
      children: 'Children',
      event: 'Event',
      menu: 'Menu',
      menuPeople: 'People',
      showDetails: 'Details',
      hideDetails: 'Hide',
    },
    pendingModification: {
      title: 'There is already a modification request pending confirmation',
      summary: '{{date}} · {{time}} · {{guests}} guests, {{children}} children',
      date: 'Date',
      time: 'Time',
      guests: 'Guests',
      children: 'Children',
    },
    form: {
      title: 'Change reservation',
      subtitle: 'Pick a new date, party size, and time slot.',
      selectedDate: 'Selected date',
      guestsLabel: 'Guests (children excluded)',
      childrenLabel: 'Children',
      timeSlotLabel: 'Available hour slot',
      guestOption: '{{count}} Guests',
      childrenOption: '{{count}} Children',
      selectTime: 'Select time',
      noAvailableTimes: 'No available times for the selected date.',
      notesLabel: 'Personal notes and requests',
      notesPlaceholder: 'Add a note for the venue…',
      readyToUpdate:
        'Review your changes, then tap Update to send the modification request.',
      calendarUnavailable:
        'Calendar is unavailable for this booking (missing form or branch).',
    },
    actions: {
      update: 'Update reservation',
      updating: 'Updating…',
      delete: 'Delete',
      cancelReservation: 'Cancel reservation',
      cancelling: 'Cancelling…',
    },
    deleteConfirm:
      'Are you sure you want to cancel this reservation? This action cannot be undone.',
    deleteModal: {
      title: 'Cancel reservation?',
      description:
        'Are you sure you want to cancel this reservation? This action cannot be undone.',
    },
    toast: {
      updateSuccess: 'Reservation update request sent.',
      updateError: 'Unable to update the reservation. Please try again.',
      validationError: 'Please fix the highlighted fields before updating.',
      deleteSuccess: 'Reservation deleted successfully.',
      deleteError: 'Unable to delete the reservation. Please try again.',
    },
    thankYou: {
      badge: 'Request sent',
      title: 'Thanks',
      headerMessage:
        'We have received your booking modification request and will respond as soon as possible.',
      statusLabel: 'Request sent',
      awaitingTitle: 'Your modification is awaiting confirmation',
      awaitingMessage:
        'We have received your booking modification request and will respond as soon as possible.',
      step1Title: 'Venue review',
      step1Message:
        'Staff will check availability and the details of your request.',
      step2Title: 'Automatic response',
      step2Message:
        'You will receive an update as soon as the modification is accepted or refused.',
      closeHint:
        'You can close this page: your request has been registered correctly.',
    },
    result: {
      updatedTitle: 'Reservation updated',
      updatedMessage:
        'Your changes have been saved. The venue has been notified of your update.',
      deletedTitle: 'Reservation deleted',
      deletedMessage:
        'Your reservation has been cancelled. We hope to see you another time.',
    },
  },
  backoffice2020: {
    title: 'Backoffice PRO20',
    logout: 'Logout',
    login: {
      documentTitle: 'Backoffice login',
      title: 'Sign in to Backoffice',
      subtitle: 'Use your admin email and password to continue.',
      emailPlaceholder: 'Email',
      passwordPlaceholder: 'Password',
      rememberCredentials: 'Remember credentials',
      signIn: 'Sign In',
      invalidCredentials: 'Invalid email or password.',
      adminBadge: 'Admin access',
      authorizedOnly: 'Authorized personnel only',
      heroTitle: 'Operate the Pro20 platform with confidence',
      heroSubtitle:
        'Secure backoffice access for managing restaurants, messaging, and platform-wide controls.',
      heroCardTitle: 'Secure control center',
      heroCardSubtitle: 'Platform operations',
      heroPointOne: 'Manage restaurants, staff, and onboarding flows',
      heroPointTwo: 'Monitor messaging health and operational alerts',
      heroPointThree: 'Apply platform-wide settings with full accountability',
      trustedBy: 'Trusted for secure operations',
      trustAccessTitle: 'Controlled access',
      trustAccessDesc: 'Admin-only credentials with protected sessions',
      trustAuditTitle: 'Audit ready',
      trustAuditDesc: 'Critical actions stay traceable',
      trustPlatformTitle: 'Platform scale',
      trustPlatformDesc: 'Operate across venues from one console',
      validation: {
        emailRequired: 'Email is required',
        emailInvalid: 'Enter a valid email address',
        emailMax: 'Email must be at most 255 characters',
        passwordMin: 'Password must be at least 8 characters',
        passwordMax: 'Password must be at most 128 characters',
      },
    },
    whatsapp: 'WhatsApp',
    footer: 'BackOffice Pro20 © {{year}}',
    headingTitle: 'Welcome to the Backoffice PRO20',
    headingWelcomeBack: 'Welcome Back, {{name}}',
    headingWelcomeBackGeneric: 'Welcome Back',
    actions: {
      announcements: 'Announcements',
      notifications: 'Notifications',
      globalAlertTooltip: 'Global Alert on App',
      expiredActivities: 'Expired activities',
      expiredActivitiesTooltip: 'Toggle expired deadline alerts',
      removeBanner: 'Remove banner',
      removeBannerTooltip: 'Remove global banner from the app',
    },
    releaseMobile: {
      title: 'Release Mobile',
      currentVersion: 'Current version {{version}}',
      manageVersion: 'Manage version',
      toast: {
        versionUpdated: 'App version updated',
        versionUpdateFailed: 'Could not update app version',
        bannerRemoved: 'Global banner removed',
        bannerRemoveFailed: 'Could not remove global banner',
      },
    },
    manageVersionModal: {
      title: 'Update app version',
      loading: 'Loading current version…',
      loadError: 'Could not load the current version.',
      major: 'Major',
      majorHint: 'Major changes',
      minor: 'Minor',
      minorHint: 'New features',
      bug: 'Bug',
      bugHint: 'Application fixes',
      patch: 'Patch',
      patchHint: 'Release Review',
      formatHint: 'The published format will be Major.Minor.Bug.Patch.',
      update: 'Update version',
    },
    removeBannerModal: {
      title: 'Remove global banner?',
      description: 'The message will no longer be displayed in the app.',
      confirm: 'Remove banner',
      cancel: 'Cancel',
    },
    globalAlertModal: {
      title: 'Enter the new global alert message',
      confirmation: 'Confirmation',
      cancel: 'Cancel',
      toast: {
        success: 'Global alert updated',
        failed: 'Could not update global alert',
      },
    },
    createActivityModal: {
      title: 'Create Branch',
      cancel: 'Cancel',
      create: 'Create',
      selectUser: 'Select User',
      selectUserPlaceholder: 'Select a user',
      selectUserSearchPlaceholder: 'Search for user by name or email',
      restaurantName: 'Restaurant Name',
      email: 'Email',
      phone: 'Phone',
      vatNumber: 'VAT Number',
      companyName: 'Company Name',
      sections: {
        marketing: 'Marketing',
        address: 'Address',
        geolocation: 'Geolocation',
        integration: 'Integration',
      },
      gtmCode: 'GTM Code',
      gtmCodeHint: 'Google Tag Manager code',
      facebookPixel: 'Facebook Pixel',
      facebookPixelHint: 'The Pixel code consists of numbers only',
      address: 'Address',
      postalCode: 'Postal Code',
      city: 'City',
      country: 'Country',
      latitude: 'Latitude',
      latitudeHint: 'E.g. 45.464211',
      longitude: 'Longitude',
      longitudeHint: 'E.g. 9.191383',
      ipraticoApiKey: 'iPratico API Key',
      ipraticoApiKeyHint: 'API Key for integration with iPracto',
      noUsers: 'No users available',
      toast: {
        created: 'Branch created',
        createFailed: 'Could not create branch',
      },
      validation: {
        userRequired: 'User selection is required',
        restaurantRequired: 'Restaurant name is required',
        emailInvalid: 'Enter a valid email address',
        facebookPixelNumeric: 'Facebook Pixel must contain numbers only',
      },
    },
    editActivity: {
      pageTitle: 'Edit Activity',
      breadcrumbRoot: 'backoffice',
      close: 'Close',
      back: 'Back to activities',
      notFound: 'Activity not found.',
      update: 'Update',
      enable: 'Enable',
      disabled: 'Disabled',
      smsRemaining: 'Residual SMS',
      addSms: 'Add SMS',
      editSms: 'Edit residual SMS',
      configureSmsModal: {
        title: 'Update remaining SMS count',
        branchCodeLabel: 'BranchCode {{branchCode}}',
        confirm: 'Configure SMS',
      },
      aiCreditsRemaining: 'AI credits',
      editAiCredits: 'Edit AI credits',
      configureAiCreditsModal: {
        title: 'Update AI credits balance',
        branchCodeLabel: 'BranchCode {{branchCode}}',
        confirm: 'Configure AI credits',
      },
      emailSetup: 'Email Setup',
      editEmailConfig: 'Edit Email Configuration',
      restoreDefaultEmailConfig: 'Restore default configuration',
      emailFields: {
        mailResidue: 'Mail residue',
        customerEmail: 'Customer email',
        fromAddress: 'From address',
        password: 'Password',
        smtpServer: 'SMTP Server',
        smtpPort: 'SMTP Port',
      },
      configureEmailModal: {
        title: 'Update remaining email count',
        mailResidue: 'Mail Residue',
        customerEmail: 'Customer email for personalized messaging',
        fromAddress: 'From address',
        password: 'Password generated',
        smtpServer: 'SMTP Server',
        smtpPort: 'SMTP Port',
        required: 'This field is required',
        save: 'Save Configuration',
      },
      sections: {
        general: 'General Data',
        subscription: 'Subscription',
        address: 'Address',
        coordinates: 'Coordinates',
        marketing: 'Marketing',
        integrations: 'Integrations',
        communications: 'Communications',
      },
      restaurantName: 'Branch Name',
      branchNameOnGoogle: 'Brand name (Google)',
      email: 'Email',
      capacity: 'Local Capacity',
      vatNumber: 'VAT Number',
      companyName: 'Company Name',
      phone: 'Mobile',
      url: 'URL',
      deadline: 'Expiration Date',
      address: 'Address',
      postalCode: 'Postal Code',
      city: 'City',
      province: 'Province / Locality',
      region: 'Region',
      country: 'Country',
      longitude: 'Longitude',
      latitude: 'Latitude',
      gtmCode: 'GTM Code',
      facebookPixel: 'FB Pixel Code',
      ipraticoApiKey: 'iPratico XApiKey',
      ipraticoApiKeyPlaceholder: 'Insert the key',
      menuRestaurant: 'Restaurant Menu',
      menuRestaurantDesc: 'Enable the digital restaurant menu for this branch.',
      officialApi: 'Official API',
      officialApiDesc: 'Enable official WhatsApp API for this branch.',
      googleReservation: 'Google Reservation',
      googleReservationDesc: 'Enable Google Reservation for this branch.',
      supplierOrders: 'Supplier Orders',
      supplierOrdersDesc: 'Enable supplier orders for this branch.',
      deliveryTakeaway: 'Delivery / Takeaway',
      deliveryTakeawayDesc:
        'Enable delivery and takeaway ordering for this branch. Requires Restaurant Menu.',
      automation: 'Automation',
      automationDesc: 'Enable automation for this branch.',
      statusEnabled: 'Active',
      statusDisabled: 'Off',
      sectionHints: {
        general:
          'Name, contact details, and how many guests this branch can host.',
        subscription: 'When access to this branch expires.',
        address: 'Where guests and partners find this branch.',
        coordinates: 'Map position used by public listings.',
        marketing: 'Tracking codes for this branch website.',
        integrations: 'Products and partner services connected to this branch.',
        communications:
          'SMS balance, AI credits, and the mailbox used for guest messages.',
      },
      publicApi: {
        title: 'Public REST API',
        description:
          'Expose booking history for this branch. Callers authenticate with the X-API-KEY header.',
        configure: 'Configure',
        statusConfigured: 'Configured',
        statusMissing: 'Not configured',
        notConfigured:
          'No X-API-KEY yet. Configure this branch to create one on the server.',
        loadFailed: 'Could not load the X-API-KEY.',
        retry: 'Retry',
        configureFailed: 'Could not create the X-API-KEY',
        configured: 'X-API-KEY created',
        deleteConfiguration: 'Delete configuration',
        deleteConfirmTitle: 'Delete REST API configuration?',
        deleteConfirmDescription:
          'This removes the X-API-KEY for this branch. Existing callers will no longer be able to use it.',
        deleted: 'REST API configuration deleted',
        deleteFailed: 'Could not delete the REST API configuration',
        keyLabel: 'X-API-KEY',
        keyHint:
          'Send this value in the X-API-KEY header on every booking history request.',
        showKey: 'Show key',
        hideKey: 'Hide key',
        copyKey: 'Copy key',
        copiedKey: 'X-API-KEY copied',
        copyFailed: 'Could not copy to the clipboard',
        bookingHistory: 'Booking history',
        sampleRequest: 'Sample request',
        sampleResponse: 'Sample response',
        copySample: 'Copy sample',
        copiedSample: 'Sample copied',
        filtersHint:
          'Each parameter is listed on its own. The endpoint changes with the filters you include.',
        copyFilters: 'Copy all filters',
        showFilters: 'Show filters',
        hideFilters: 'Hide filters',
        copiedFilters: 'Filters copied',
        endpointLabel: 'Endpoint',
        params: {
          page: 'page',
          size: 'size',
          status: 'status',
          bookingDate: 'bookingDate',
          bookingDateFrom: 'bookingDateFrom',
          bookingDateTo: 'bookingDateTo',
          search: 'search',
          timeRangeFilter: 'timeRangeFilter',
          assigned: 'assigned',
          walkInOnly: 'walkInOnly',
          sortBy: 'sortBy',
          sortDirection: 'sortDirection',
        },
        languages: {
          curl: 'cURL',
          javascript: 'JavaScript',
          python: 'Python',
          php: 'PHP',
        },
      },
      toast: {
        updated: 'Branch updated',
        updateFailed: 'Could not update branch',
        featureUpdated: 'Feature updated',
        featureUpdateFailed: 'Could not update feature',
        deliveryRequiresMenu:
          'Enable Restaurant Menu before turning on Delivery / Takeaway.',
        smsUpdated: 'SMS count updated',
        smsUpdateFailed: 'Could not update SMS count',
        aiCreditsUpdated: 'AI credits updated',
        aiCreditsUpdateFailed: 'Could not update AI credits',
        emailUpdated: 'Email configuration saved',
        emailRestored: 'Email configuration restored',
      },
      validation: {
        restaurantRequired: 'Branch name is required',
        emailInvalid: 'Enter a valid email address',
        urlInvalid: 'Enter a valid link',
        facebookPixelNumeric: 'Facebook Pixel must contain numbers only',
        capacityInvalid: 'Enter a valid capacity',
        capacityInteger: 'Capacity must be a whole number',
        capacityMin: 'Capacity must be at least 0',
      },
    },
    activities: {
      title: 'Branches',
      count: '{{count}} branches',
      create: 'Create Branch',
      loadError: 'Could not load branches. Please try again.',
      expiringCaption: 'Expiring',
      expiringAria: '{{count}} branches expiring in {{month}}',
    },
    tabs: {
      branch: 'Branch Pro20',
      users: 'Pro20 Users',
      reports: 'iPratico - Report',
      security: 'Security',
    },
    security: {
      title: 'Change password',
      subtitle: 'Update the password for your backoffice account.',
      password: 'Password',
      confirmPassword: 'Confirm password',
      submit: 'Update password',
      validation: {
        required: 'Password is required',
        minLength: 'Password must be at least 6 characters',
        confirmRequired: 'Please confirm your password',
        mismatch: 'Passwords do not match',
      },
      toast: {
        success: 'Password updated successfully',
        failed: 'Could not update password. Please try again.',
      },
    },
    users: {
      title: 'Users',
      count: '{{count}} users',
      create: 'Create User',
      searchPlaceholder: 'Search user..',
      loadError: 'Could not load users. Please try again.',
      table: {
        userCode: 'UserCode',
        name: 'Name',
        email: 'Email',
        phone: 'Phone',
        activity: 'Activity',
        visibleOnApp: 'Visible On App',
      },
      tooltips: {
        linkActivity: 'Link activity to user',
        editUser: 'Edit user',
        goToBackoffice: 'Go to the user back office',
        deleteUser: 'Delete user',
      },
      fields: {
        name: 'Name',
        email: 'Email',
        phone: 'Phone',
        password: 'Password',
        confirmPassword: 'Confirm Password',
      },
      createModal: {
        title: 'Create user',
        cancel: 'Cancel',
        create: 'Create',
      },
      editModal: {
        title: 'Edit user',
        cancel: 'Cancel',
        update: 'Update',
      },
      linkActivityModal: {
        title: 'Select Activity',
        searchLabel: 'Search Activity',
        searchPlaceholder: 'Search by name or code',
        availableTitle: 'Available Activities',
        cancel: 'Cancel',
        confirm: 'Confirm',
      },
      relatedActivitiesModal: {
        title: 'Related Activities',
        name: 'Name',
        code: 'Code',
        expiration: 'Expiration',
        unlink: 'Unlink activity',
        close: 'Close',
      },
      unlinkModal: {
        title: 'Unlink activity?',
        description:
          'Are you sure you want to unlink "{{name}}" from this user? This action cannot be undone.',
      },
      deleteModal: {
        title: 'Delete user?',
        description:
          'Are you sure you want to delete "{{name}}"? This action cannot be undone.',
      },
      validation: {
        nameRequired: 'Name is required',
        emailRequired: 'Email is required',
        emailInvalid: 'Enter a valid email address',
        passwordMin: 'Password must be at least 6 characters',
        passwordMismatch: 'Passwords do not match',
      },
      toast: {
        created: 'User created',
        createFailed: 'Could not create user',
        updated: 'User updated',
        updateFailed: 'Could not update user',
        deleted: 'User deleted',
        deleteFailed: 'Could not delete user',
        loadFailed: 'Could not load user details',
        activitiesLinked: 'Activities linked to user',
        activitiesLinkFailed: 'Could not link activities',
        activityUnlinked: 'Activity unlinked from user',
        activityUnlinkFailed: 'Could not unlink activity',
        noAssociatedActivity: 'No associated activity',
        visibilityUpdated: 'User visibility updated',
        visibilityUpdateFailed: 'Could not update user visibility',
      },
    },
    reports: {
      badge: 'Integration monitor',
      title: 'iPratico Errors',
      subtitle:
        'Check requests, responses, and anomalies recorded during operations towards iPratico.',
      errorsFound: 'errors found',
      actions: {
        update: 'Update',
        reset: 'Reset',
        filter: 'Filter',
      },
      filters: {
        branchCode: 'Branch code',
        operation: 'Operation',
        httpStatus: 'HTTP status',
        fromDay: 'From the day',
        toDay: 'Per day',
        allStatuses: 'All statuses',
        status400: '400 — Invalid request',
        status401: '401 — Unauthorized',
        status403: '403 — Access Denied',
        status404: '404 — Resource not found',
        status409: '409 — Conflict',
        status429: '429 — Too many requests',
        status500: '500 — iPratico Error',
        status502: '502 — Gateway',
        status503: '503 — Service unavailable',
      },
      table: {
        date: 'Data',
        branch: 'Branch',
        operation: 'Operation',
        status: 'Status',
        endpoint: 'Endpoint',
        mistake: 'Mistake',
      },
      detail: {
        title: 'Error detail',
        branch: 'Branch',
        branchName: 'Branch name',
        branchCode: 'Branch code',
        date: 'Data',
        method: 'Method',
        status: 'Status',
        apiKey: 'X-API-KEY used',
        endpoint: 'Endpoint',
        message: 'Message',
        requestPayload: 'Payload request',
        responsePayload: 'iPratico answer',
      },
      tooltips: {
        openDetail: 'Open detail',
        closeDetail: 'Close detail',
        copyPayload: 'Copy payload',
      },
      toast: {
        updated: 'Reports updated',
        copied: 'Copied to clipboard',
        loadFailed: 'Could not load iPratico errors',
      },
      loadError: 'Could not load iPratico errors. Please try again.',
    },
    searchPlaceholder: 'Search by name, code, company name or VAT',
    filters: {
      features: {
        label: 'Features',
        google: 'Google Reservation',
        menu: 'Menu Restaurant',
        ipraticoKey: 'iPratico Key',
        whatsappBusiness: 'WhatsApp Business',
        stripe: 'Stripe',
      },
    },
    table: {
      nameActivities: 'Branch Name',
      branchCode: 'BranchCode',
      extra: 'Extra',
      deadline: 'Expiration',
      expiredOn: 'Expired on {{date}}',
      alertOnApp: 'Alert On App',
      companyName: 'Company Name',
      noCompanyName: 'No company name',
      delete: 'Eliminate Branch',
      settings: 'Update Branch',
      chat: 'Monitor messages',
      send: 'Go to the backoffice',
      tooltips: {
        whatsappBusiness: 'WhatsApp Business enabled',
        menu: 'Menu enabled',
        google: 'Google Reservation enabled',
        ipraticoKey: 'iPratico Key',
        sms: 'Remaining SMS: {{count}}',
        aiCredits: 'AI credits: {{count}}',
        loadError: 'Could not load users for this branch',
      },
    },
    stripeConnect: {
      tooltip: {
        ready: 'Stripe Connect active — view or remove configuration',
        pending: 'Complete, resume, or remove Stripe onboarding',
        unlinked: 'Connect the branch to Stripe Connect',
      },
      link: {
        title: 'Stripe onboarding link',
        body: 'The link for {{branch}} is ready.',
        warning:
          'This is a temporary, single-use Stripe link: share or use it as soon as possible.',
        openLink: 'Open link',
        copyLink: 'Copy link',
        linkCopied: 'Link copied',
        copyFailed: 'Copy failed — select and copy the link manually.',
      },
      connected: {
        title: 'Stripe Connect active',
        body: '{{branch}} is already connected and can receive payments and payouts.',
      },
      pending: {
        title: 'Stripe checks in progress',
        body: 'Stripe still requires some checks for {{branch}}.',
        resume: 'Resume onboarding',
      },
      error: {
        title: 'Stripe configuration failed',
        fallback: 'Check the Stripe configuration and try again.',
      },
      remove: {
        action: 'Remove Stripe configuration',
        confirmTitle: 'Remove Stripe configuration?',
        confirmBody:
          'Disconnect Stripe for {{branch}}? The branch will no longer accept Stripe payments until it is connected again.',
        success: 'Stripe configuration removed',
        failed: 'Could not remove Stripe configuration',
      },
      close: 'Close',
    },
    accessUserModal: {
      title: 'Select a user to access the backoffice with',
      name: 'Name',
      email: 'Email',
      close: 'Close',
      loadError: 'Could not load users for this branch',
    },
    signInAsUser: {
      success: 'Signed in as selected user',
      failed: 'Could not sign in as this user',
      missingIdentity: 'User code and email are required',
    },
    expiringBranchesModal: {
      title: 'Branches expiring in {{month}}',
      name: 'Name',
      userCode: 'Usercode',
      email: 'Mail',
      expires: 'Expires',
      close: 'Close',
      empty: 'No branches expire in this month.',
      openDetails: 'Open branch details',
    },
    deleteActivityModal: {
      title: 'Confirms elimination of branch?',
      description:
        'Write here the branch code ({{branchCode}}) to continue with the removal',
      branchCodePlaceholder: 'BRANCH CODE',
      eliminate: 'Eliminate',
      cancel: 'Cancel',
      deleted: 'Branch deleted',
      deleteFailed: 'Could not delete branch',
    },
    access: {
      title: 'Backoffice access',
      description:
        'Enter the numeric code to access the Pro20 operating panel.',
      codePlaceholder: '',
      enter: 'Enter',
    },
    validation: {
      codeRequired: 'Access code is required',
      codeNumeric: 'Access code must contain only numbers',
      invalidCode: 'Invalid access code',
    },
    messageMonitor: {
      title: 'Message Monitor',
      subtitle: 'Check normal and scheduled submissions for API tests.',
      close: 'Close',
      reset: 'Reset',
      update: 'Update',
      stats: {
        posted: 'Posted on this page',
        errors: 'Errors on this page',
        inProgress: 'In progress on this page',
      },
      filters: {
        origin: 'Origin',
        state: 'State',
        channel: 'Channel',
        reference: 'Reference',
        referencePlaceholder: 'Reference',
        originEveryone: 'Everyone',
        originNormal: 'Normal',
        originScheduled: 'Scheduled',
        originBulk: 'Bulk',
        stateEveryone: 'Everyone',
        stateSent: 'Sent',
        stateErrors: 'Errors',
        statePending: 'Pending',
        stateInQueue: 'In coda',
        stateInProgress: 'In progress',
        channelEveryone: 'Everyone',
        channelWhatsapp: 'WhatsApp',
        channelThem: 'THEM',
        channelEmail: 'Email',
        channelSms: 'SMS',
      },
      eventsFound: '{{count}} events found',
      empty: 'No messages found with the selected filters.',
      loadError: 'Could not load message events. Please try again.',
      actions: {
        resubmit: 'Resubmit message',
      },
      toast: {
        resubmitted: 'Message resubmitted',
        resubmitFailed: 'Could not resubmit message',
      },
      table: {
        date: 'Data',
        type: 'Type',
        recipient: 'Recipient',
        reference: 'Reference',
        message: 'Message',
        channel: 'Channel',
        state: 'State',
        attempts: 'Attempts',
        endpoint: 'Endpoint',
        payload: 'Payload',
        details: 'Details',
      },
    },
  },
  homeLanding: {
    documentTitle: 'PRO20 — Restaurant Reservation & Table Booking Software',
    login: 'Log in',
    bookDemo: 'Book a Demo',
    startFreeTrial: 'Request a Free Trial',
    learnMore: 'Learn more',
    nav: {
      home: 'Home',
      product: 'Product',
      solutions: 'Solutions',
      industries: 'Industries',
      integrations: 'Integrations',
      pricing: 'Pricing',
      resources: 'Resources',
      why: 'Why PRO20',
      apps: 'Apps',
      howItWorks: 'How it works',
      reviews: 'Customer Reviews',
      faq: 'FAQ',
      about: 'About',
    },
    hero: {
      partnerBadge: 'Google Reservation C2C Partner',
      metaPartnerBadge: 'Official Meta Tech Provider',
      titleBefore: 'All-in-One Platform for',
      titleHighlight: 'Restaurant',
      titleAfter: 'Table Booking.',
      subtitle:
        'Manage reservations, floor plans, staff, marketing, payments and delight your guests — all in one powerful platform.',
      checkGoogle: 'Google Reservations',
      checkWhatsapp: 'WhatsApp Official API',
      checkFloor: 'Floor & Table Mapping',
      checkMarketing: 'Marketing & Loyalty',
      checkPayments: 'Payments & Deposits',
      checkApps: 'iOS & Android Apps',
      trustedBy: 'Trusted by 100+ Restaurants across 10+ cities in Italy.',
      previewAlt: 'PRO20 restaurant reservation dashboard preview',
      chatAria: 'Open chat',
    },
    trust: {
      heading: 'Trusted by 100+ Restaurants & Venue Brands',
      subtitle:
        'Independent restaurants and multi-site groups use PRO20 to fill tables every day.',
    },
    stats: {
      title: 'Trusted by restaurant teams worldwide',
      subtitle: 'Real scale across restaurants, cafés, beach clubs, and bars.',
      restaurantsValue: '100+',
      restaurantsLabel: 'Restaurants',
      countriesValue: '10+',
      countriesLabel: 'Cities in Italy',
      bookingsValue: '2M+',
      bookingsLabel: 'Bookings managed',
      uptimeValue: '99.9%',
      uptimeLabel: 'Uptime',
    },
    features: {
      title: 'Everything you need to run reservations',
      subtitle:
        'From bookings and floor plans to marketing, loyalty, and integrations — one platform.',
      reservationsTitle: 'Smart Reservations',
      reservationsDesc:
        'Accept, manage, and optimize bookings across every channel in real time.',
      floorTitle: 'Floor Plan & Tables',
      floorDesc:
        'Visual floor mapping with live table status for smoother service.',
      bookingLinkTitle: 'Public booking link',
      bookingLinkDesc:
        'Create a booking form and share the public URL on your website, QR, WhatsApp, or social so guests reserve tables online.',
      marketingTitle: 'Marketing & Campaigns',
      marketingDesc:
        'Reach guests with targeted offers, reminders, and campaigns.',
      loyaltyTitle: 'Loyalty & Promo',
      loyaltyDesc:
        'Reward regulars and drive repeat visits with flexible promotions.',
      reportsTitle: 'Reports & Insights',
      reportsDesc:
        'Track occupancy, revenue, and guest trends with clear dashboards.',
    },
    why: {
      title: 'Why PRO20?',
      subtitle:
        'Built specifically for restaurants and bars — not a generic booking widget.',
      allInOneTitle: 'All-in-one platform',
      allInOneDesc:
        'Reservations, floor plan, marketing, loyalty, and payments in one place.',
      channelsTitle: 'Google & WhatsApp native',
      channelsDesc: 'Meet guests where they already book and message.',
      hospitalityTitle: 'Made for floor operations',
      hospitalityDesc:
        'Floor mapping, staff workflows, and live service tools that fit the floor.',
      appsTitle: 'Apps for every device',
      appsDesc:
        'Native phone and iPad apps on Android and iOS, synced with the web backoffice.',
      fidelityTitle: 'Fidelity & loyalty',
      fidelityDesc:
        'Reward regulars with points, cards, and promos — drive repeat visits from one program.',
      supportTitle: '24×7 Support',
      supportDesc:
        'Round-the-clock help whenever your team needs it — day or night, every day of the week.',
    },
    industries: {
      title: 'Built for every type of venue',
      subtitle:
        'From fine dining to beach clubs — PRO20 adapts to how your venue serves guests.',
      restaurants: 'Restaurants',
      cafes: 'Cafés',
      beachClubs: 'Beach Clubs',
      bars: 'Bars & Pubs',
      rooftops: 'Rooftops',
      resorts: 'Resorts',
    },
    integrations: {
      title: 'Powerful Integrations',
      subtitle:
        'Connect the tools you already use — bookings, messaging, POS, and payments.',
      google: 'Google Reservations',
      whatsapp: 'WhatsApp Official API',
      ipratico: 'iPratico POS',
      centralino: 'Centralino VOIP',
      stripe: 'Stripe Payments',
      meta: 'Meta Tech Partner',
    },
    engage: {
      title: 'Communicate. Engage. Delight.',
      subtitle:
        'Connect with your guests instantly through WhatsApp official API and automated messages.',
      confirmations: 'Booking Confirmations',
      reminders: 'Reminders & Alerts',
      offers: 'Offers & Promotions',
      feedback: 'Feedback & Reviews',
      chatGuest: 'Hi! Your table for 2 is confirmed for 8:00 PM.',
      chatModify: 'Modify Booking',
      chatCancel: 'Cancel Booking',
      chatDirections: 'Get Directions',
    },
    apps: {
      title: 'Run your venue from phone or iPad',
      subtitle:
        'Native PRO20 apps for phones and iPads — available on Android and iOS, so your team stays in sync on the floor and on the go.',
      phoneTablet: 'Phones & iPads',
      androidIos: 'Android & iOS',
      floorPlan: 'Full floor plan and table control on iPad',
      onTheGo: 'Manage bookings from your phone anywhere',
      sync: 'Same live data as the web backoffice',
      appStore: 'Download on the App Store',
      googlePlay: 'Get it on Google Play',
    },
    about: {
      title: 'About Us',
      subtitle:
        'We help restaurant brands turn every table into a better guest experience.',
      body: 'PRO20 is an all-in-one restaurant table booking platform trusted by venues worldwide. From independent restaurants to multi-site groups, we combine reservations, floor plans, guest messaging, and marketing so operators can focus on hospitality — not juggling tools.',
      missionTitle: 'Our mission',
      missionBody:
        'Make world-class reservation operations simple, connected, and delightful for every venue.',
      pillarGuests: 'Guests first',
      pillarOperators: 'Operators empowered',
      pillarConnected: 'Always connected',

      imageAlt: 'Restaurant team at work',
    },
    howItWorks: {
      title: 'Get started in 6 simple steps',
      subtitle:
        'Everything you need to manage your restaurant operations, from setup to happy guests.',
      steps: {
        s1: {
          title: 'Create Your Restaurant',
          desc: 'Set up your restaurant profile with cuisine, location, and branding in minutes.',
        },
        s2: {
          title: 'Design Floors & Tables',
          desc: 'Build your floor plan with a drag-and-drop planner and organize tables your way.',
        },
        s3: {
          title: 'Configure Booking Rules',
          desc: 'Define time slots, party sizes, deposits, and policies that fit your service.',
        },
        s4: {
          title: 'Share & Receive Bookings',
          desc: 'Publish your booking link on your website, WhatsApp, socials, or QR codes.',
        },
        s5: {
          title: 'Manage & Grow',
          desc: 'Run the floor, staff, and marketing from one place as reservations come in.',
        },
        s6: {
          title: 'Analyze & Optimize',
          desc: 'Use clear reports on occupancy, revenue, and guest trends to improve every service.',
        },
      },
      mocks: {
        createTitle: 'Create your restaurant',
        name: 'Restaurant name',
        cuisine: 'Cuisine type',
        location: 'Location',
        continue: 'Continue',
        floorTitle: 'Floor planner',
        rulesTitle: 'Booking Settings',
        timeSlots: 'Time Slots',
        bookingRules: 'Booking Rules',
        advancePayment: 'Advance Payment',
        shareTitle: 'Share booking link',
        manageTitle: "Today's Overview",
        reservations: 'Reservations',
        occupied: 'Occupied Tables',
        walkIns: 'Walk-ins',
        revenue: 'Revenue',
        reportsTitle: 'Reports Overview',
        totalReservations: 'Total Reservations',
        occupancy: 'Occupancy Rate',
      },
      ctaText:
        'Ready to get started? Join thousands of restaurants already growing with PRO20.',
      bookDemo: 'Book a Demo',
      contactSales: 'Contact Sales',
    },
    faq: {
      title: 'Frequently asked questions',
      subtitle:
        'Quick answers about PRO20 restaurant reservation software for multi-site venues.',
      viewAll: 'View all FAQs',
      q1: 'Can I share a public booking link?',
      a1: 'Yes. Create a booking form and share the public URL on your website, QR code, WhatsApp, or social channels so guests can reserve tables online.',
      q2: 'Does it support multiple restaurants?',
      a2: 'Yes. Multi-location groups can manage venues centrally through PRO20 Backoffice.',
      q3: 'Can I connect WhatsApp?',
      a3: 'Yes. PRO20 supports WhatsApp Business and the Official Meta API for confirmations, reminders, and campaigns.',
      q4: 'Which integrations are available?',
      a4: 'Google Reservations, WhatsApp Official API, POS systems, Stripe, Razorpay, and many more.',
      q5: 'Is there a mobile app?',
      a5: 'Yes. Native apps for phones and iPads on Android and iOS stay synced with the web platform.',
      q6: 'How do I get started?',
      a6: 'Book a demo or start a free trial — we will help you set up floors, tables, and your public booking link.',
    },
    reviews: {
      title: "Don't just take our word for it",
      subtitle:
        'Restaurants across 10+ cities in Italy already use PRO20 to manage reservations, fill tables, and delight guests. Ready to see what the fuss is about?',
      reassurance:
        '(Yes, really. No fine print. No sales pressure. Just solid software and a team that gets restaurants.)',
      trustLabel: 'From 100s of reviews',
      r1: {
        quote:
          'PRO20 replaced three tools we used before. Reservations and WhatsApp finally live in one place.',
        author: 'Marco Rossi • Owner, Osteria Bella',
      },
      r2: {
        quote:
          'Setup was fast and the table planner is exactly what our floor staff needed on busy nights.',
        author: 'Sophie Laurent • Manager, Café Lumière',
      },
      r3: {
        quote:
          'Seasonal capacity and guest messaging used to be chaos. Now it is predictable and professional.',
        author: 'Elena Bianchi • Director, Azure Beach Club',
      },
      r4: {
        quote:
          'Our public booking link filled evening slots we used to leave empty. Guests book themselves — we just confirm.',
        author: 'Alex Kim • Owner, Harbor Bistro',
      },
    },
    nextLevel: {
      title: 'Are you ready to take your restaurant to the next level?',
      body: 'PRO20 is designed to simplify daily operations, increase bookings, and reduce the stress of managing a busy venue.',
      closing: 'Get started with PRO20 today and experience the difference.',
    },

    cta: {
      heading: 'Ready to Transform Your Restaurant?',
      subtitle:
        'Join thousands of restaurants growing their business with PRO20.',
    },
    footer: {
      blurb:
        'The all-in-one restaurant reservation software for table booking, guest engagement, and venue operations.',
      product: 'Product',
      productReservations: 'Reservations',
      productFloor: 'Floor Plan',
      productMarketing: 'Marketing',
      productLoyalty: 'Loyalty',
      productReports: 'Reports',
      productIntegrations: 'Integrations',
      solutions: 'Solutions',
      solutionsRestaurants: 'For Restaurants',
      solutionsCafes: 'For Cafés',
      solutionsBeach: 'For Beach Clubs',
      solutionsBars: 'For Bars',
      solutionsChains: 'For Chains',
      resources: 'Resources',
      resourcesBlog: 'Blog',
      resourcesGuides: 'Guides',
      resourcesHelp: 'Help Center',
      resourcesCases: 'Case Studies',
      resourcesApi: 'API Documentation',
      resourcesApps: 'Mobile Apps',
      company: 'Company',
      companyAbout: 'About Us',
      companyCareers: 'Careers',
      companyContact: 'Contact Us',
      companyPartners: 'Partners',
      legal: 'Legal',
      legalPrivacy: 'Privacy Policy',
      legalTerms: 'Terms of Service',
      legalCookies: 'Cookie Policy',
      copyright: '© {{year}} PRO20. All rights reserved.',
      socialFacebook: 'Facebook',
      socialInstagram: 'Instagram',
      socialLinkedin: 'LinkedIn',
      socialYoutube: 'YouTube',
    },
  },
  faqPage: {
    documentTitle: 'FAQ — PRO20 Restaurant Reservation Software',
    title: 'Frequently asked questions',
    subtitle:
      'Get quick answers to the most common questions about PRO20 restaurant reservation software.',
    searchPlaceholder: 'Search your question...',
    emptyResults:
      'No questions match your search. Try another keyword or category.',
    categories: {
      all: 'All Questions',
      reservations: 'Reservations',
      whatsapp: 'WhatsApp',
      payments: 'Payments',
      reports: 'Reports',
      staff: 'Staff & Users',
      integrations: 'Integrations',
      general: 'General',
    },
    promo: {
      badge: 'All-in-one platform',
      titleBefore: 'Everything you need to',
      titleAccent: 'manage & grow',
      titleAfter: 'your restaurant',
      body: 'From reservation to marketing, WhatsApp automation to reports — PRO20 helps you run your business smarter.',
      restaurantsValue: '10,000+',
      restaurantsLabel: 'Restaurants',
      bookingsValue: '2M+',
      bookingsLabel: 'Reservations',
      countriesValue: '10+',
      countriesLabel: 'Cities in Italy',
      uptimeValue: '99.9%',
      uptimeLabel: 'Uptime',
    },
    items: {
      q1: 'Can I share booking links?',
      a1: 'Yes. Create a booking form and share the public URL on your website, QR code, WhatsApp, or social channels so guests can reserve tables online.',
      q1Tag1: 'Public URL',
      q1Tag2: 'QR Code',
      q1Tag3: 'WhatsApp',
      q1Tag4: 'Social Media',
      q1Tag5: 'Website',
      q2: 'Does PRO20 support multiple restaurants or locations?',
      a2: 'Yes. Multi-location groups can manage venues centrally through PRO20 Backoffice with shared controls and reporting.',
      q3: 'Can I connect WhatsApp Official and send messages?',
      a3: 'Yes. PRO20 supports WhatsApp Business and the Official Meta API for confirmations, reminders, campaigns, and guest messaging.',
      q4: 'Which payment gateways are supported?',
      a4: 'PRO20 integrates with Stripe and other payment providers so you can collect deposits and reduce no-shows.',
      q5: 'Is there a mobile app available?',
      a5: 'Yes. Native apps for phones and iPads on Android and iOS stay synced with the web platform for floor and on-the-go management.',
      q6: 'Which languages are supported?',
      a6: 'PRO20 supports multiple languages for operators and guests, including English, Italian, Spanish, French, Dutch, and Chinese.',
      q7: 'Can I manage my staff and their roles?',
      a7: 'Yes. Assign roles and permissions so your team only sees what they need — from floor staff to multi-site managers.',
      q8: 'Can I manage marketing campaigns and promo codes?',
      a8: 'Yes. Run campaigns, offers, and promo codes from PRO20 to fill quieter shifts and reward regular guests.',
      q9: 'What kind of reports can I get?',
      a9: 'Track occupancy, bookings, revenue trends, and guest activity with clear dashboards built for restaurant operations.',
      q10: 'How secure is my data?',
      a10: 'We use industry-standard security practices to protect your restaurant and guest data, with reliable uptime for daily operations.',
    },
    cta: {
      eyebrow: 'Still have questions?',
      title: "We're here to help you!",
      body: 'Talk with our restaurant experts and see how PRO20 can transform your business.',
      bookDemo: 'Book a Demo',
      contactSales: 'Contact Sales',
      startTrial: 'Or request a free trial',
    },
  },
  legalPages: {
    privacy: {
      documentTitle: 'Privacy Policy — PRO20',
      loading: 'Loading privacy policy…',
      loadError:
        'Unable to load controller details. The policy is shown with limited information.',
    },
    privacyPolicy: {
      documentTitle: 'Privacy notice | Proventi',
      title: 'Information on the processing of personal data',
      subtitle:
        'Pursuant to Articles 13 and 14 of Regulation (EU) 2016/679 – GDPR',
      contents: 'On this page',
      body: privacyPolicyBodyEn,
    },
    terms: {
      documentTitle: 'Terms of Service — PRO20',
      title: 'Terms of Service',
      subtitle: 'These terms will be updated soon.',
      body: 'The PRO20 Terms of Service are being prepared. Please check back later, or contact us if you need a copy in the meantime.',
    },
  },
} as const;
